"use strict";

/* =========================================================
   [CGJS-00] SHARED CORE
   One MutationObserver, one rAF scheduler, shared helpers.
   Every feature registers a cheap, idempotent callback instead
   of spinning up its own body-wide observer + timeout bursts.
   ========================================================= */
(function () {
  if (window.__cgCore) return;

  var D = document;
  var callbacks = [];
  var scheduled = false;
  var observer = null;

  function ready(fn) {
    if (D.readyState === 'loading') {
      D.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  var holdUntil = 0;
  function runAll() {
    scheduled = false;
    // Briefly pause running feature callbacks while the theme does its big
    // variant re-render (~67 DOM mutations / ~1s). Re-running every module on
    // each of those mutations piles churn onto the theme's dropdown-widget
    // re-bind and can make it drop clicks (worse on Safari). A single catch-up
    // run is scheduled when the hold ends.
    if (holdUntil && Date.now() < holdUntil) return;
    for (var i = 0; i < callbacks.length; i++) {
      try { callbacks[i](); } catch (e) {}
    }
  }
  function hold(ms) {
    holdUntil = Date.now() + (ms || 0);
    setTimeout(function () { holdUntil = 0; schedule(); }, (ms || 0) + 30);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(runAll);
  }

  function ensureObserver() {
    if (observer || !D.body) return;
    observer = new MutationObserver(schedule);
    // childList only — far cheaper than watching attributes/characterData.
    observer.observe(D.body, { childList: true, subtree: true });
  }

  // Register a callback to run (debounced) whenever the DOM changes,
  // plus on the lifecycle events that matter.
  function register(fn) {
    callbacks.push(fn);
    ensureObserver();
  }

  ready(function () {
    ensureObserver();
    runAll();
  });

  window.addEventListener('load', schedule);
  window.addEventListener('pageshow', schedule);
  D.addEventListener('shopify:section:load', schedule);

  // A couple of late ticks for apps that inject after first paint
  // (Okendo, Searchanise, FrontChat, payment buttons).
  setTimeout(schedule, 500);
  setTimeout(schedule, 1800);

  window.__cgCore = {
    ready: ready,
    toInt: toInt,
    clamp: clamp,
    register: register,
    schedule: schedule,
    hold: hold
  };
})();


/* =========================================================
   [CGJS-01] PRODUCT PAGE BASICS + STANDALONE QTY PILL
   ========================================================= */
(function () {
  if (window.__cgProductBasicsFinal) return;
  window.__cgProductBasicsFinal = true;

  var core = window.__cgCore;
  var toInt = core.toInt;
  var clamp = core.clamp;

  function upgradeAccountLinks() {
    var links = document.querySelectorAll(
      '#nav-user a[href^="http://account.cctvguru.com.au"]'
    );
    for (var i = 0; i < links.length; i++) {
      links[i].href = links[i].href.replace('http://', 'https://');
    }
  }

  function getQtyInput(wrapper) {
    return wrapper.querySelector('input[name="quantity"]');
  }

  function syncPill(pill, input) {
    if (!pill || !input) return;

    var dec = pill.querySelector('.cg-product-qty-btn--dec');
    var inc = pill.querySelector('.cg-product-qty-btn--inc');
    var valueEl = pill.querySelector('.cg-product-qty-value');

    var min = toInt(input.getAttribute('min'), 1);
    var max = input.getAttribute('max') ? toInt(input.getAttribute('max'), 999) : 999;
    var value = clamp(toInt(input.value, min), min, max);

    input.value = String(value);
    valueEl.textContent = String(value);
    dec.disabled = value <= min;
    inc.disabled = value >= max;
  }

  function bindInputSync(pill, input) {
    if (input.dataset.cgQtyPillBound === 'true') return;
    input.dataset.cgQtyPillBound = 'true';

    input.addEventListener('input', function () { syncPill(pill, input); });
    input.addEventListener('change', function () { syncPill(pill, input); });
  }

  function createPill(wrapper, input) {
    var pill = document.createElement('div');
    pill.className = 'cg-product-qty-pill';

    var dec = document.createElement('button');
    dec.type = 'button';
    dec.className = 'cg-product-qty-btn cg-product-qty-btn--dec';
    dec.setAttribute('aria-label', 'Decrease quantity');
    dec.textContent = '-';

    var value = document.createElement('span');
    value.className = 'cg-product-qty-value';
    value.textContent = input.value || '1';

    var inc = document.createElement('button');
    inc.type = 'button';
    inc.className = 'cg-product-qty-btn cg-product-qty-btn--inc';
    inc.setAttribute('aria-label', 'Increase quantity');
    inc.textContent = '+';

    function update(direction) {
      var min = toInt(input.getAttribute('min'), 1);
      var max = input.getAttribute('max') ? toInt(input.getAttribute('max'), 999) : 999;
      var step = toInt(input.getAttribute('step'), 1);
      var current = clamp(toInt(input.value, min), min, max);
      var next = clamp(current + step * direction, min, max);

      if (next === current) {
        syncPill(pill, input);
        return;
      }

      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      syncPill(pill, input);
    }

    dec.addEventListener('click', function () { update(-1); });
    inc.addEventListener('click', function () { update(1); });

    pill.appendChild(dec);
    pill.appendChild(value);
    pill.appendChild(inc);

    pill.__cgSourceInput = input;
    pill.__cgSync = function () { syncPill(pill, input); };

    wrapper.parentNode.insertBefore(pill, wrapper);
    bindInputSync(pill, input);
    syncPill(pill, input);

    return pill;
  }

  function enhanceQtyWrapper(wrapper) {
    if (!wrapper || !wrapper.parentNode) return;

    var input = getQtyInput(wrapper);
    if (!input) return;

    wrapper.classList.add('cg-product-qty-source');
    wrapper.setAttribute('aria-hidden', 'true');
    input.tabIndex = -1;

    var existingPill = wrapper.previousElementSibling;
    var hasMatchingPill =
      existingPill &&
      existingPill.classList &&
      existingPill.classList.contains('cg-product-qty-pill') &&
      existingPill.__cgSourceInput === input;

    if (!hasMatchingPill) {
      if (existingPill && existingPill.classList && existingPill.classList.contains('cg-product-qty-pill')) {
        existingPill.remove();
      }
      existingPill = createPill(wrapper, input);
    }

    if (existingPill && existingPill.__cgSync) existingPill.__cgSync();
  }

  function cleanupOrphanPills() {
    var pills = document.querySelectorAll('#main-product .cg-product-qty-pill');
    for (var i = 0; i < pills.length; i++) {
      var next = pills[i].nextElementSibling;
      if (!next || !next.classList || !next.classList.contains('cg-product-qty-source')) {
        pills[i].remove();
      }
    }
  }

  function run() {
    upgradeAccountLinks();

    if (!document.body.classList.contains('template-product')) return;

    var wrappers = document.querySelectorAll('#main-product .input-amount');
    for (var i = 0; i < wrappers.length; i++) enhanceQtyWrapper(wrappers[i]);
    cleanupOrphanPills();
  }

  core.register(run);
})();


/* =========================================================
   [CGJS-02] CART PAGE
   ========================================================= */
(function () {
  if (window.__cgCartFinal) return;

  function isCartPage() {
    return document.body.classList.contains('template-cart') || !!document.getElementById('page-cart');
  }
  // Also run when the cart DRAWER (aside#cart) exists — it's a global element
  // that uses the same markup, so it gets the same pill/SKU styling. (Cart-page
  // -only logic like the summary totals self-guards on template-cart below.)
  if (!isCartPage() && !document.getElementById('cart')) return;
  window.__cgCartFinal = true;

  var core = window.__cgCore;
  var toInt = core.toInt;
  var clamp = core.clamp;

  /* ---- Apple-style quantity pill ---- */
  function enhanceQtyInput(originalInput) {
    var oldWrapper = originalInput.closest('.input-amount');
    if (!oldWrapper) return;

    // Already enhanced and our pill is still in the DOM (even after mobilePillFix
    // moves it into a .cg-pill-wrap) → nothing to do. Tracking by reference avoids
    // re-building duplicate pills on mobile.
    if (oldWrapper.classList.contains('cg-rebuilt') &&
        oldWrapper.__cgPill && oldWrapper.__cgPill.isConnected) {
      return;
    }

    // Theme re-rendered and dropped our pill → remove any leftover, then rebuild.
    if (oldWrapper.__cgPill) {
      var stale = oldWrapper.__cgPill.closest('.cg-pill-wrap') || oldWrapper.__cgPill;
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
    }

    oldWrapper.classList.add('cg-rebuilt');
    oldWrapper.style.cssText =
      'all:unset;display:block;width:0;height:0;overflow:hidden;position:absolute;opacity:0;pointer-events:none;visibility:hidden;';

    var pill = document.createElement('div');
    pill.className = 'cg-qty-pill';
    pill.style.cssText =
      'display:inline-flex;flex-direction:row;align-items:center;justify-content:center;width:124px;min-width:124px;height:52px;padding:6px;border:1px solid rgba(29,29,31,0.12);border-radius:999px;background:#fff;box-shadow:0 1px 2px rgba(15,23,42,0.06),0 8px 24px rgba(15,23,42,0.08);box-sizing:border-box;overflow:visible;';

    var displayInput = originalInput.cloneNode(true);
    displayInput.removeAttribute('id');
    displayInput.removeAttribute('name');
    displayInput.readOnly = true;
    displayInput.tabIndex = -1;
    displayInput.style.cssText =
      'flex:0 0 30px;width:30px;min-width:30px;max-width:30px;height:auto;border:0;border-radius:0;background:transparent;box-shadow:none;text-align:center;font-size:20px;font-weight:600;color:#1d1d1f;padding:0;margin:0;-webkit-appearance:none;-moz-appearance:textfield;appearance:none;outline:none;';

    var dec = document.createElement('button');
    dec.type = 'button';
    dec.className = 'cg-qty-btn cg-qty-dec';
    dec.setAttribute('aria-label', 'Decrease quantity');
    dec.textContent = '-';

    var inc = document.createElement('button');
    inc.type = 'button';
    inc.className = 'cg-qty-btn cg-qty-inc';
    inc.setAttribute('aria-label', 'Increase quantity');
    inc.textContent = '+';

    function sync() {
      var min = toInt(originalInput.getAttribute('min'), 1);
      var max = originalInput.getAttribute('max') ? toInt(originalInput.getAttribute('max'), 999) : 999;
      var value = clamp(toInt(originalInput.value, min), min, max);

      originalInput.value = String(value);
      displayInput.value = String(value);
      dec.disabled = value <= min;
      inc.disabled = value >= max;
    }

    function update(direction) {
      var min = toInt(originalInput.getAttribute('min'), 1);
      var max = originalInput.getAttribute('max') ? toInt(originalInput.getAttribute('max'), 999) : 999;
      var step = toInt(originalInput.getAttribute('step'), 1);
      var current = clamp(toInt(originalInput.value, min), min, max);
      var next = clamp(current + step * direction, min, max);

      if (next === current) { sync(); return; }

      originalInput.value = String(next);
      displayInput.value = String(next);
      originalInput.dispatchEvent(new Event('input', { bubbles: true }));
      // Cart page: use the fast AJAX path ([CGJS-31]) instead of the theme's
      // change handler (which 409s on /cart/change and falls back to a ~2s full
      // reload). Elsewhere (drawer, etc.) keep the native change event.
      if (window.__cgFastCart && document.body.classList.contains('template-cart')) {
        window.__cgFastCart(originalInput, next);
      } else {
        originalInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      sync();
    }

    dec.addEventListener('click', function () { update(-1); });
    inc.addEventListener('click', function () { update(1); });
    originalInput.addEventListener('input', sync);
    originalInput.addEventListener('change', sync);

    pill.appendChild(dec);
    pill.appendChild(displayInput);
    pill.appendChild(inc);

    oldWrapper.parentNode.insertBefore(pill, oldWrapper);
    oldWrapper.__cgPill = pill; // track so we don't rebuild duplicates
    sync();
  }

  function initCartQty() {
    var inputs = document.querySelectorAll('.template-cart .input-amount input, #page-cart .input-amount input, #cart .input-amount input');
    for (var i = 0; i < inputs.length; i++) enhanceQtyInput(inputs[i]);
  }

  /* ---- Desktop: equal spacing + vertical alignment ---- */
  function fixCartRows() {
    if (window.innerWidth <= 760) return;

    var pills = document.querySelectorAll('.cg-qty-pill');
    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      // Skip the cart DRAWER — these row-alignment hacks are for the cart-page
      // table/card layout; the drawer pill uses its clean base form + CSS.
      if (pill.closest('#cart')) continue;
      var row = pill.closest('tr') || pill.parentElement;
      if (!row || row.dataset.cgAligned) continue;
      row.dataset.cgAligned = '1';

      var tds = row.querySelectorAll('td');
      for (var t = 0; t < tds.length; t++) {
        tds[t].style.cssText += ';vertical-align:middle!important;';
      }

      if (row.tagName !== 'TR') {
        row.style.cssText += ';display:flex!important;align-items:center!important;flex-wrap:nowrap!important;justify-content:flex-start!important;';
      }

      var prev = pill.previousElementSibling;
      while (prev) {
        if (prev.textContent.trim().indexOf('$') !== -1) {
          prev.style.cssText += ';flex-shrink:0!important;font-size:22px!important;font-weight:700!important;letter-spacing:-0.03em!important;white-space:nowrap!important;vertical-align:middle!important;';
          break;
        }
        prev = prev.previousElementSibling;
      }

      pill.style.cssText += ';flex-shrink:0!important;margin-left:0!important;margin-right:16px!important;vertical-align:middle!important;display:inline-flex!important;align-self:center!important;';

      var next = pill.nextElementSibling;
      while (next) {
        if (
          next.tagName === 'A' ||
          next.tagName === 'BUTTON' ||
          next.innerHTML.indexOf('svg') !== -1 ||
          String(next.className).indexOf('remove') !== -1 ||
          String(next.className).indexOf('delete') !== -1 ||
          String(next.className).indexOf('del') !== -1
        ) {
          next.style.cssText += ';flex-shrink:0!important;margin-left:0!important;vertical-align:middle!important;align-self:center!important;';
          break;
        }
        next = next.nextElementSibling;
      }
    }
  }

  /* ---- Mobile: pill + bin wrapper ---- */
  function mobilePillFix() {
    if (window.innerWidth > 760) return;

    var pills = document.querySelectorAll('.cg-qty-pill');
    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      if (pill.closest('.cg-pill-wrap')) continue;
      // Skip the cart DRAWER — it keeps the clean base pill at any width.
      if (pill.closest('#cart')) continue;

      var tr = pill.closest('tr');
      var bin = null;

      if (tr) {
        var els = tr.querySelectorAll('a, button');
        for (var e = 0; e < els.length; e++) {
          var el = els[e];
          if (bin || el === pill || pill.contains(el)) continue;

          var href = el.getAttribute('href') || '';
          var name = el.getAttribute('name') || '';
          var cls = String(el.className || '');

          if (
            el.querySelector('svg') ||
            el.innerHTML.toLowerCase().indexOf('svg') !== -1 ||
            cls.indexOf('remove') !== -1 ||
            cls.indexOf('del') !== -1 ||
            href.indexOf('remove') !== -1 ||
            name === 'remove'
          ) {
            bin = el;
          }
        }
      }

      var wrap = document.createElement('div');
      wrap.className = 'cg-pill-wrap';
      wrap.style.cssText =
        'display:flex;flex-direction:row;align-items:center;justify-content:flex-start;gap:12px;width:100%;height:52px;margin-top:8px;box-sizing:border-box;';

      pill.parentNode.insertBefore(wrap, pill);
      wrap.appendChild(pill);

      pill.style.setProperty('margin-right', '0px', 'important');
      pill.style.setProperty('margin-left', '0px', 'important');
      pill.style.setProperty('position', 'static', 'important');
      pill.style.setProperty('top', '0px', 'important');

      if (bin) {
        var binTd = bin.closest('td');
        if (binTd && !binTd.contains(pill)) {
          binTd.style.cssText = 'display:none!important;padding:0!important;width:0!important;overflow:hidden!important;';
        }

        var binWrap = document.createElement('span');
        binWrap.style.cssText =
          'display:inline-flex;align-items:center;justify-content:center;height:52px;width:40px;flex-shrink:0;overflow:visible;';
        binWrap.appendChild(bin);
        wrap.appendChild(binWrap);

        bin.style.cssText = '';
        bin.style.setProperty('display', 'block', 'important');
        bin.style.setProperty('position', 'static', 'important');
        bin.style.setProperty('margin', '0', 'important');
        bin.style.setProperty('padding', '0', 'important');
        bin.style.setProperty('height', 'auto', 'important');
        bin.style.setProperty('width', 'auto', 'important');
        bin.style.setProperty('transform', 'none', 'important');
      }
    }
  }

  /* ---- Summary: total count highlight + checkout note ---- */
  function normalize(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function summaryScope() {
    // Scope scans to the cart summary column instead of the whole page.
    return document.querySelector('.template-cart .cols.aside') ||
           document.querySelector('#page-cart .cols.aside') ||
           document.querySelector('.template-cart aside') ||
           document.body;
  }

  function findLeaf(scope, regex) {
    var nodes = scope.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      if (regex.test(normalize(el.textContent))) return el;
    }
    return null;
  }

  function findRowContainer(labelEl) {
    var node = labelEl;
    while (node && node !== document.body) {
      if (/\$[\d,]+(?:\.\d{2})?/.test(normalize(node.textContent))) return node;
      node = node.parentElement;
    }
    return labelEl ? labelEl.parentElement : null;
  }

  function applyTotalProductsHighlight(scope) {
    var nodes = scope.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.children.length) continue;
      var match = normalize(el.textContent).match(/^Total products \((\d+)\)$/i);
      if (!match) continue;
      el.innerHTML = 'Total products <span class="cg-total-products-count">' + match[1] + '</span>';
    }
  }

  function applyCheckoutDiscountNote(scope) {
    var totalLabel = findLeaf(scope, /^Total including GST$/i) || findLeaf(scope, /Total including GST/i);
    if (!totalLabel) return;

    var totalRow = findRowContainer(totalLabel);
    if (!totalRow || !totalRow.parentElement) return;
    if (totalRow.parentElement.querySelector(':scope > .cg-checkout-discount-note')) return;

    var note = document.createElement('p');
    note.className = 'cg-checkout-discount-note';
    note.textContent = 'Discount codes can be applied at checkout.';
    totalRow.parentElement.insertBefore(note, totalRow);
  }

  function emphasizeTotal(scope) {
    // Find the "Total including GST" line (works whether the label is an
    // element or a bare text node) and enlarge its $ amount.
    var rows = scope.querySelectorAll('li, p, tr');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var txt = (row.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/total including gst/i.test(txt)) continue;
      if (txt.length > 60) continue; // skip big containers; keep the total line
      if (row.querySelector('.cg-cart-total-value')) return;
      var leaves = row.querySelectorAll('*');
      for (var j = 0; j < leaves.length; j++) {
        var el = leaves[j];
        if (el.children.length) continue;
        if (/\$[\d,]+(?:\.\d{2})?/.test((el.textContent || '').trim())) {
          el.classList.add('cg-cart-total-value');
          return;
        }
      }
      // Fallback: the amount is a BARE TEXT NODE (e.g. <span>Total …</span> "$537.00")
      // — wrap it in a span we can size.
      for (var c = 0; c < row.childNodes.length; c++) {
        var n = row.childNodes[c];
        if (n.nodeType === 3 && /\$[\d,]+(?:\.\d{2})?/.test(n.nodeValue || '')) {
          var span = document.createElement('span');
          span.className = 'cg-cart-total-value';
          span.textContent = (n.nodeValue || '').trim();
          n.parentNode.replaceChild(span, n);
          return;
        }
      }
      return;
    }
  }

  function tightenDispatch(scope) {
    var leaf = findLeaf(scope, /Dispatch today/i);
    if (!leaf) return;
    var node = leaf;
    for (var k = 0; k < 4 && node; k++) {
      if (/\d{1,2}:\d{2}:\d{2}/.test(node.textContent || '')) break;
      node = node.parentElement;
    }
    if (node && node.nodeType === 1) node.classList.add('cg-cart-dispatch');
  }

  // Remove the redundant "$X" price next to "Total products (N)" — it duplicates
  // the "Total including GST" amount below.
  function emphasizeSubtotal(scope) {
    var lis = scope.querySelectorAll('ul.l4tt li, .l4tt li');
    for (var i = 0; i < lis.length; i++) {
      var li = lis[i];
      if (!/total products/i.test(li.textContent || '')) continue;
      // drop a wrapped price if a previous run made one
      var wrapped = li.querySelector('.cg-cart-subtotal-value');
      if (wrapped) wrapped.remove();
      // drop the bare "$X" text node
      for (var c = li.childNodes.length - 1; c >= 0; c--) {
        var n = li.childNodes[c];
        if (n.nodeType === 3 && /\$[\d,]+(?:\.\d{2})?/.test(n.nodeValue || '')) li.removeChild(n);
      }
      return;
    }
  }

  // Show the GST component under the "Total including GST" line. AU prices are
  // GST-inclusive, so GST = total / 11. The desktop total is a <li> and the
  // mobile total a <span class="mobile-only"> (the other is display:none per
  // breakpoint), so we find whichever is VISIBLE and drop the note under it.
  // Updates live as the cart total changes.
  function showGst(scope) {
    var matches = [];
    var els = scope.querySelectorAll('span, li, p, div, td');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.offsetParent === null) continue;                  // visible only
      var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (/total including gst/i.test(t) && t.length < 55 && /\$[\d,]/.test(t)) matches.push(el);
    }
    if (!matches.length) return;
    // smallest (deepest) match = the actual total line, not a big container
    matches.sort(function (a, b) { return a.getElementsByTagName('*').length - b.getElementsByTagName('*').length; });
    var totalEl = matches[0];
    var m = (totalEl.textContent || '').match(/\$[\d,]+(?:\.\d{2})?/);
    if (!m) return;
    var total = parseFloat(m[0].replace(/[^\d.]/g, ''));
    if (!total || isNaN(total)) return;
    var label = 'Includes $' + (total / 11).toFixed(2) + ' GST';
    // insert right after the total line itself (its row li, or on mobile the
    // inline total span) so it sits directly under the total — above Checkout.
    var anchor = totalEl.closest('li, tr') || totalEl;
    if (!anchor.parentElement) return;
    var note = document.querySelector('.cg-gst-note');
    if (!note) { note = document.createElement('p'); note.className = 'cg-gst-note'; }
    if (anchor.nextSibling !== note) anchor.parentElement.insertBefore(note, anchor.nextSibling);
    if (note.textContent !== label) note.textContent = label;
  }

  function enhanceSummary() {
    if (!document.body.classList.contains('template-cart')) return;
    var scope = summaryScope();
    applyTotalProductsHighlight(scope);
    applyCheckoutDiscountNote(scope);
    emphasizeTotal(scope);
    emphasizeSubtotal(scope);
    showGst(scope);
    tightenDispatch(scope);
  }

  // Force Checkout buttons blue + slim/wide via INLINE styles — the theme's red
  // wins the CSS cascade (even with !important), but inline styles beat it.
  function styleCheckout() {
    var btns = document.querySelectorAll('button[name="checkout"]');
    for (var i = 0; i < btns.length; i++) {
      var s = btns[i].style;
      s.setProperty('background', '#0e5fd8', 'important');
      s.setProperty('background-color', '#0e5fd8', 'important');
      s.setProperty('background-image', 'none', 'important');
      s.setProperty('border', '0', 'important');
      s.setProperty('border-radius', '999px', 'important');
      s.setProperty('box-shadow', 'none', 'important');
      s.setProperty('color', '#fff', 'important');
      s.setProperty('min-height', '42px', 'important');
      s.setProperty('height', 'auto', 'important');
      s.setProperty('min-width', '160px', 'important');
      s.setProperty('padding', '0 30px', 'important');
      s.setProperty('position', 'relative', 'important');
      s.setProperty('overflow', 'hidden', 'important');
      // The red fill comes from a ::before using the theme's secondary colour
      // variable — override it so the pseudo inherits blue too.
      s.setProperty('--secondary_bg', '#0e5fd8', 'important');
      s.setProperty('--secondary-bg', '#0e5fd8', 'important');
    }
  }

  function run() {
    initCartQty();
    fixCartRows();
    mobilePillFix();
    enhanceSummary();
    styleCheckout();
  }

  // Sweep loader on the Checkout buttons while the page navigates to /checkout
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"]') : null;
    if (!btn || btn.querySelector('.cg-btn-load')) return;
    btn.classList.add('cg-is-busy');
    var load = document.createElement('span');
    load.className = 'cg-btn-load';
    load.setAttribute('aria-hidden', 'true');
    btn.appendChild(load);
  }, true);

  // Clear that sweep whenever the page (re)appears. When the user taps Checkout
  // then swipes / presses Back from Shopify checkout, the browser restores this
  // page from its back-forward cache with the button still mid-sweep, and init
  // never re-runs — so the loader stays stuck until a manual reload. pageshow
  // fires on both fresh load and bfcache restore (e.persisted === true), and it
  // never fires mid-navigation, so it's always safe to reset the button here.
  function cgClearCheckoutSweep() {
    var loaders = document.querySelectorAll('button[name="checkout"] .cg-btn-load');
    for (var i = 0; i < loaders.length; i++) {
      if (loaders[i].parentNode) loaders[i].parentNode.removeChild(loaders[i]);
    }
    var busy = document.querySelectorAll('button[name="checkout"].cg-is-busy');
    for (var j = 0; j < busy.length; j++) busy[j].classList.remove('cg-is-busy');
  }
  window.addEventListener('pageshow', cgClearCheckoutSweep);

  core.register(run);
  window.addEventListener('resize', core.schedule);
})();


/* =========================================================
   [CGJS-03] PRODUCT UPSELL TOTALS  (event-driven — unchanged)
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;

  var activeUpsellItem = null;
  var productCache = Object.create(null);
  var lastRenderedSignature = '';
  var totalTweenRaf = 0;

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  }
  function parseHandle(url) {
    var match = (url || '').match(/\/products\/([^?#]+)/);
    return match ? match[1] : '';
  }
  function arraysMatch(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) if (String(a[i]) !== String(b[i])) return false;
    return true;
  }
  function parseMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }
  function formatMoney(amount) {
    return '$' + Number(amount || 0).toLocaleString('en-AU', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }
  function getUpsellHandle(item) {
    var link = item.querySelector('h3 > a[href*="/products/"]') ||
               item.querySelector('figure a[href*="/products/"]');
    return link ? parseHandle(link.getAttribute('href')) : '';
  }
  function getHiddenVariants(item) {
    return Array.prototype.map.call(
      item.querySelectorAll('select[name="upsell-id"] option'),
      function (option) {
        try {
          var data = JSON.parse(option.dataset.variantinfo || '{}');
          data.id = data.id || option.value;
          return data;
        } catch (e) { return null; }
      }
    ).filter(Boolean);
  }
  function getPopupSelectedOptions() {
    var popup = document.querySelector('article[data-title="upsell-popup"]');
    if (!popup) return [];
    return Array.prototype.map.call(popup.querySelectorAll('select'), function (s) { return s.value; });
  }
  function findVariantByOptions(variants, options) {
    for (var i = 0; i < variants.length; i++) {
      if (arraysMatch(variants[i].options || [], options)) return variants[i];
    }
    return null;
  }
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(shopRoot() + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('fail'); return r.json(); })
      .catch(function () { return null; });
    return productCache[handle];
  }
  function getQty() {
    var input = document.querySelector('#main-product input[name="quantity"]');
    var qty = input ? parseInt(input.value, 10) : 1;
    return qty > 0 ? qty : 1;
  }
  // Return the element holding the CURRENT price. Theme 6.4 used a
  // <span>; Xtra Live 8 renders it as a bare text node ("$939.00 Incl. GST"),
  // so we wrap the number in .cg-price-amount (a stable element to update +
  // size) and the "Incl. GST" suffix in <small class="cg-price-suffix">.
  function ensureAmountSpan(totalEl) {
    var span = totalEl.querySelector('.cg-price-amount');
    if (span) return span;
    span = totalEl.querySelector('span:not(.old-price)');
    if (span) return span;
    var nodes = totalEl.childNodes;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType !== 3) continue; // text nodes only
      var m = (n.nodeValue || '').match(/^(\s*)(\$?[\d,]+(?:\.\d{2})?)(\s*)(incl[\s\S]*?)?\s*$/i);
      if (!m || !m[2]) continue;
      var frag = document.createDocumentFragment();
      if (m[1]) frag.appendChild(document.createTextNode(m[1]));
      var amt = document.createElement('span');
      amt.className = 'cg-price-amount';
      amt.textContent = m[2];
      frag.appendChild(amt);
      if (m[4]) {
        frag.appendChild(document.createTextNode(m[3] || ' '));
        var sfx = document.createElement('small');
        sfx.className = 'cg-price-suffix';
        sfx.textContent = m[4].trim();
        frag.appendChild(sfx);
      } else if (m[3]) {
        frag.appendChild(document.createTextNode(m[3]));
      }
      n.parentNode.replaceChild(frag, n);
      return amt;
    }
    return null;
  }

  function getTotalRefs() {
    var totalEl = document.querySelector('#main-product .f8pr-price.s1pr.price');
    if (!totalEl) return null;
    var oldEl = totalEl.querySelector('.old-price');
    var currentEl = ensureAmountSpan(totalEl);
    if (!currentEl) return null;
    return { totalEl: totalEl, oldEl: oldEl, currentEl: currentEl };
  }
  function getBaseUnitPrices() {
    var refs = getTotalRefs();
    if (!refs) return null;
    var qty = getQty();
    if (!refs.totalEl.dataset.cgBaseCurrent) {
      refs.totalEl.dataset.cgBaseCurrent = String(parseMoney(refs.currentEl.textContent) / qty);
    }
    if (!refs.totalEl.dataset.cgBaseOld) {
      refs.totalEl.dataset.cgBaseOld = String(
        refs.oldEl && !refs.oldEl.classList.contains('hidden')
          ? parseMoney(refs.oldEl.textContent) / qty : 0
      );
    }
    return {
      current: parseFloat(refs.totalEl.dataset.cgBaseCurrent || '0') || 0,
      old: parseFloat(refs.totalEl.dataset.cgBaseOld || '0') || 0,
      refs: refs
    };
  }
  function resetBaseUnitPrices() {
    var refs = getTotalRefs();
    if (!refs) return;
    delete refs.totalEl.dataset.cgBaseCurrent;
    delete refs.totalEl.dataset.cgBaseOld;
    lastRenderedSignature = '';
    if (totalTweenRaf) { cancelAnimationFrame(totalTweenRaf); totalTweenRaf = 0; }
    refs.totalEl.classList.remove('cg-price-soft');
  }
  function getUpsellPrice(item) {
    var priceEl = item.querySelector('.price');
    if (!priceEl) return { current: 0, old: 0 };
    var oldEl = priceEl.querySelector('.old-price');
    var oldText = oldEl && !oldEl.classList.contains('hidden') ? oldEl.textContent : '';
    var old = parseMoney(oldText);
    var currentText = priceEl.textContent;
    if (oldText) currentText = currentText.replace(oldText, '');
    return { current: parseMoney(currentText), old: old };
  }
  function getSelectedUpsellTotals() {
    var totals = { current: 0, old: 0 };
    var items = document.querySelectorAll('#main-product .f8pr-upsell .upsell-items > li');
    for (var i = 0; i < items.length; i++) {
      var checkbox = items[i].querySelector('.check input[type="checkbox"]');
      if (!checkbox || !checkbox.checked) continue;
      var price = getUpsellPrice(items[i]);
      totals.current += price.current;
      // A non-discounted add-on's "compare" price is its own price — add that
      // to the old total so the bundle compare-at doesn't drop below current
      // (which hid the strikethrough whenever add-ons were ticked).
      totals.old += price.old || price.current;
    }
    // [CGJS-33] Also fold in the chip selectors' selections (HDD/Cat6 multi-qty)
    // so the main price reflects them live. No-op on pages without them.
    if (typeof cgMySelectionTotal === 'function') {
      var mine = cgMySelectionTotal();
      totals.current += mine.current;
      totals.old += mine.old;
    }
    return totals;
  }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function animateTotal(totalEl, valueEl, from, to) {
    if (!totalEl || !valueEl) return;
    if (totalTweenRaf) cancelAnimationFrame(totalTweenRaf);
    totalEl.classList.remove('cg-price-soft');
    void totalEl.offsetWidth;
    totalEl.classList.add('cg-price-soft');
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      valueEl.textContent = formatMoney(to);
      return;
    }
    var start = performance.now();
    var duration = 360;
    function frame(now) {
      var t = Math.min(1, (now - start) / duration);
      valueEl.textContent = formatMoney(from + (to - from) * easeOutCubic(t));
      if (t < 1) { totalTweenRaf = requestAnimationFrame(frame); }
      else { totalTweenRaf = 0; valueEl.textContent = formatMoney(to); }
    }
    totalTweenRaf = requestAnimationFrame(frame);
  }
  function renderVisibleTotal() {
    var base = getBaseUnitPrices();
    if (!base) return;
    var qty = getQty();
    var upsell = getSelectedUpsellTotals();
    var nextCurrent = (base.current + upsell.current) * qty;
    var nextOld = (base.old + upsell.old) * qty;
    var refs = base.refs;
    var nextSignature = [qty, base.current, base.old, upsell.current, upsell.old].join('|');
    var currentChanged = Math.abs(parseMoney(refs.currentEl.textContent) - nextCurrent) > 0.009;

    if (currentChanged && nextSignature !== lastRenderedSignature) {
      animateTotal(refs.totalEl, refs.currentEl, parseMoney(refs.currentEl.textContent), nextCurrent);
    } else {
      refs.currentEl.textContent = formatMoney(nextCurrent);
    }
    if (refs.oldEl) {
      if (nextOld > nextCurrent) {
        refs.oldEl.textContent = formatMoney(nextOld);
        refs.oldEl.classList.remove('hidden');
      } else {
        refs.oldEl.textContent = '';
        refs.oldEl.classList.add('hidden');
      }
    }
    lastRenderedSignature = nextSignature;
  }
  function scheduleTotalRender(delay) { setTimeout(renderVisibleTotal, delay || 0); }
  function setVariantText(item, text) {
    var row = Array.prototype.find.call(item.querySelectorAll('p'), function (p) {
      return !!p.querySelector('a[data-upsell]');
    });
    var span = row && row.querySelector('span');
    if (span && text) span.textContent = text;
  }
  function setPrice(item, price, oldPrice) {
    var priceEl = item.querySelector('.price');
    if (!priceEl || !price) return;
    priceEl.innerHTML =
      '<span class="old-price' + (oldPrice ? '' : ' hidden') + '">' + (oldPrice || '') + '</span>&nbsp;' + price;
  }
  function setImage(item, src) {
    var img = item.querySelector('figure img');
    if (!img || !src) return;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    img.src = src;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
  }
  function setVariantId(item, variantId) {
    var hiddenSelect = item.querySelector('select[name="upsell-id"]');
    var trigger = item.querySelector('a[data-upsell]');
    var checkbox = item.querySelector('span.check > input');
    if (hiddenSelect) hiddenSelect.value = String(variantId);
    if (trigger) trigger.setAttribute('data-variant', String(variantId));
    if (checkbox) { checkbox.checked = true; checkbox.dataset.id = String(variantId); }
  }
  function setSku(item, sku) {
    return; // SKU is hidden in the add-on list (see hideUpsellSku) — don't populate it
    var skuLabel = Array.prototype.find.call(item.querySelectorAll('span.strong'), function (el) {
      return /^SKU:\s*$/i.test((el.textContent || '').trim());
    });
    if (!skuLabel) return;
    var node = skuLabel.nextSibling;
    while (node && node.nodeType === 3 && !node.textContent.trim()) node = node.nextSibling;
    if (node && node.nodeType === 3) { node.textContent = ' ' + sku; return; }
    if (node && node.nodeType === 1 && node.classList.contains('cg-upsell-sku-value')) { node.textContent = sku; return; }
    var span = document.createElement('span');
    span.className = 'cg-upsell-sku-value';
    span.textContent = sku;
    skuLabel.parentNode.insertBefore(document.createTextNode(' '), node || null);
    skuLabel.parentNode.insertBefore(span, node || null);
  }
  function syncActiveUpsell() {
    if (!activeUpsellItem) return;
    var localVariants = getHiddenVariants(activeUpsellItem);
    var selectedVariant = findVariantByOptions(localVariants, getPopupSelectedOptions());
    if (!selectedVariant) return;
    setVariantId(activeUpsellItem, selectedVariant.id);
    setVariantText(activeUpsellItem, selectedVariant.variant || '');
    setPrice(activeUpsellItem, selectedVariant.price || '', selectedVariant.price_old || '');
    setImage(activeUpsellItem, selectedVariant.image || '');
    scheduleTotalRender(0);
    scheduleTotalRender(100);
    var handle = getUpsellHandle(activeUpsellItem);
    if (!handle) return;
    loadProduct(handle).then(function (product) {
      if (!product || !product.variants) return;
      var variant = product.variants.find(function (v) { return String(v.id) === String(selectedVariant.id); });
      if (variant) setSku(activeUpsellItem, variant.sku || '');
    });
  }
  function preloadUpsells() {
    var items = document.querySelectorAll('#main-product .f8pr-upsell .upsell-items > li');
    for (var i = 0; i < items.length; i++) {
      var handle = getUpsellHandle(items[i]);
      if (handle) loadProduct(handle);
    }
  }
  /* ---- Inline variant chips (replaces the Choose-variant popup) ---- */
  function chipLabelRaw(v) {
    if (v.options && v.options.length) return v.options.join(' / ');
    return v.variant || '';
  }

  function commonTokenCount(arrs, fromEnd) {
    var minLen = Math.min.apply(null, arrs.map(function (a) { return a.length; }));
    var count = 0;
    for (var i = 0; i < minLen; i++) {
      var ref = fromEnd ? arrs[0][arrs[0].length - 1 - i] : arrs[0][i];
      var allSame = arrs.every(function (a) {
        var tok = fromEnd ? a[a.length - 1 - i] : a[i];
        return tok === ref;
      });
      if (allSame) count++; else break;
    }
    return count;
  }

  // Trim shared leading/trailing WORDS across all variant labels so
  // "2TB Surveillance HDD – 24/7 Recording" collapses to "2TB",
  // while distinct labels like "White" / "Black" stay untouched.
  function shortChipLabels(variants) {
    var raws = variants.map(chipLabelRaw);
    var toks = raws.map(function (s) { return String(s).split(/\s+/).filter(Boolean); });
    var minLen = Math.min.apply(null, toks.map(function (a) { return a.length; }));
    var pre = commonTokenCount(toks, false);
    var suf = commonTokenCount(toks, true);
    if (pre + suf > minLen - 1) suf = Math.max(0, minLen - pre - 1);
    return toks.map(function (t, i) {
      var mid = t.slice(pre, t.length - suf).join(' ').trim();
      return mid || raws[i];
    });
  }

  function setActiveChip(container, activeChip) {
    Array.prototype.forEach.call(container.children, function (c) {
      c.classList.toggle('is-active', c === activeChip);
    });
  }

  function applyVariant(item, v, doCheck) {
    var hiddenSelect = item.querySelector('select[name="upsell-id"]');
    var trigger = item.querySelector('a[data-upsell]');
    var checkbox = item.querySelector('.check input[type="checkbox"]');

    if (hiddenSelect) hiddenSelect.value = String(v.id);
    if (trigger) trigger.setAttribute('data-variant', String(v.id));
    if (checkbox) {
      checkbox.dataset.id = String(v.id);
      if (doCheck && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setVariantText(item, v.variant || '');
    setPrice(item, v.price || '', v.price_old || '');
    setImage(item, v.image || '');
    scheduleTotalRender(0);
    scheduleTotalRender(80);

    var handle = getUpsellHandle(item);
    if (handle) loadProduct(handle).then(function (product) {
      if (!product || !product.variants) return;
      var mv = product.variants.find(function (x) { return String(x.id) === String(v.id); });
      if (mv) setSku(item, mv.sku || '');
    });
  }

  function buildVariantChips(item) {
    if (item.dataset.cgChipsBuilt === '1') return;

    var triggerP = Array.prototype.find.call(item.querySelectorAll('p'), function (p) {
      return !!p.querySelector('a[data-upsell]');
    });
    if (!triggerP) return;

    var variants = getHiddenVariants(item);
    if (!variants.length) return; // can't parse variant data — keep native popup intact

    item.dataset.cgChipsBuilt = '1';

    // Single variant: nothing to choose — just hide the redundant button.
    if (variants.length === 1) {
      triggerP.classList.add('cg-upsell-row-hidden');
      return;
    }

    var handle = getUpsellHandle(item);
    // The HDD / Cat6 accessories are surfaced by the TOP selectors and their
    // rows are hidden — don't also wire them here (would double-register).
    if (handle === CG_OPT_HDD || handle === CG_OPT_CABLE) return;

    var chips = document.createElement('div');
    chips.className = 'cg-upsell-chips';

    if (handle) {
      // Multi-qty chips (click = +1, badge = -1) — same engine as the top
      // selectors. Our chips decide inclusion now, so hide the theme checkbox.
      // Lead with a "None" chip so it's easy to clear a selection (the qty badge
      // is a fiddly tap target on mobile). CSS shows it on mobile only.
      cgWireQtyChips(handle, chips, variants, true, 'None');
      triggerP.parentNode.insertBefore(chips, triggerP);
      triggerP.classList.add('cg-upsell-row-hidden');
      item.classList.add('cg-opt-mqty');
      var cb = item.querySelector('.check input[type="checkbox"]');
      if (cb && cb.checked) cb.checked = false;
      var cw = item.querySelector('.check');
      if (cw) cw.classList.add('cg-opt-hide-check');
      cgPaint(handle);
      return;
    }

    // Fallback (no product handle): original single-select chips.
    var labels = shortChipLabels(variants);
    variants.forEach(function (v, idx) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cg-upsell-chip';
      chip.textContent = labels[idx];
      chip.addEventListener('click', function () {
        applyVariant(item, v, true);
        setActiveChip(chips, chip);
      });
      chips.appendChild(chip);
    });
    triggerP.parentNode.insertBefore(chips, triggerP);
    triggerP.classList.add('cg-upsell-row-hidden');
  }

  // Hide the "SKU: …" line in add-on items (the product title already carries it).
  function hideUpsellSku(item) {
    var skuLabel = Array.prototype.find.call(item.querySelectorAll('span.strong'), function (el) {
      return /^SKU:\s*$/i.test((el.textContent || '').trim());
    });
    if (!skuLabel) return;
    skuLabel.style.display = 'none';
    // Clear the value (a bare text node) + hide any value span, up to the next element.
    var node = skuLabel.nextSibling;
    while (node && (node.nodeType === 3 ||
      (node.nodeType === 1 && node.classList && node.classList.contains('cg-upsell-sku-value')))) {
      var next = node.nextSibling;
      if (node.nodeType === 3) node.textContent = '';
      else node.style.display = 'none';
      node = next;
    }
  }

  function buildAllChips() {
    var items = document.querySelectorAll('#main-product .f8pr-upsell .upsell-items > li');
    for (var i = 0; i < items.length; i++) {
      buildVariantChips(items[i]);
      hideUpsellSku(items[i]);
    }
  }

  // [CGJS-33] PILOT (single product): HDD + Cat6 accessories as chip selectors
  // above the price / Add to cart. MULTI-SELECT with per-size quantity — click a
  // size to add one, click again to bump it, and a circle badge shows the count
  // (click the badge to remove one). The MAIN price folds these in live (via
  // getSelectedUpsellTotals → renderVisibleTotal). On Add to cart we take over
  // and add the base unit + every selected size (× qty) + any other ticked add-on
  // as separate cart lines. Scoped to ONE product handle while we trial it.
  var CG_OPT_PILOT = 'dahua-dhi-nvr4104hs-p-ai-anz-4ch-wizsense-ai-nvr-4poe';
  var CG_OPT_HDD = 'surveillance-hdd-cctv-nvr-hard-drive-2tb-14tb';
  var CG_OPT_CABLE = '8ware-blue-cat6-cat6a-cctv-patch-leads-slim-shielded';
  var CG_SEL = {};          // handle -> { chips: el, qty: {vid:n}, vmap: {vid:variant} }

  function cgFindUpsell(handle) {
    var items = document.querySelectorAll('#main-product .f8pr-upsell .upsell-items > li');
    for (var i = 0; i < items.length; i++) {
      if (getUpsellHandle(items[i]) === handle) return items[i];
    }
    return null;
  }
  // Sum of the chip selectors' picks — folded into the theme total so the main
  // price reflects them (current = price×qty; old = compare-at (or price)×qty).
  function cgMySelectionTotal() {
    var cur = 0, old = 0;
    for (var h in CG_SEL) {
      var st = CG_SEL[h];
      for (var vid in st.qty) {
        var q = st.qty[vid]; if (!q) continue;
        var v = st.vmap[vid]; if (!v) continue;
        var c = parseMoney(v.price);
        cur += c * q;
        old += (v.price_old ? parseMoney(v.price_old) : c) * q;
      }
    }
    return { current: cur, old: old };
  }

  function cgMakeBadge(handle, vid) {
    var b = document.createElement('span');
    b.className = 'cg-opt__qty';
    b.title = 'Remove one';
    b.addEventListener('click', function (ev) {
      ev.stopPropagation(); ev.preventDefault();
      var st = CG_SEL[handle];
      if (!st) return;
      var n = (st.qty[vid] || 0) - 1;
      if (n > 0) st.qty[vid] = n; else delete st.qty[vid];
      cgPaint(handle);
    });
    return b;
  }
  function cgPaint(handle) {
    var st = CG_SEL[handle];
    if (!st || !st.chips) return;
    var any = false;
    Array.prototype.forEach.call(st.chips.querySelectorAll('.cg-upsell-chip[data-vid]'), function (c) {
      var vid = c.getAttribute('data-vid');
      var q = st.qty[vid] || 0;
      if (q > 0) any = true;
      c.classList.toggle('is-active', q > 0);
      var b = c.querySelector('.cg-opt__qty');
      if (q >= 1) {
        if (!b) { b = cgMakeBadge(handle, vid); c.appendChild(b); }
        b.textContent = String(q);
      } else if (b) { b.parentNode.removeChild(b); }
    });
    var none = st.chips.querySelector('.cg-opt-none');
    if (none) none.classList.toggle('is-active', !any);
    cgUpdateItemDisplay(handle);        // keep the item's price + thumbnail in sync
    scheduleTotalRender(0); scheduleTotalRender(80);
  }

  // Strip a trailing "(SKU)" so chips read "1500VA / 900W" not the whole title.
  function cgCleanLabel(s) {
    return String(s).replace(/\s*\([^)]*\)\s*$/, '').trim();
  }
  // Wire multi-qty chips into `container` for an accessory. Each variant is a
  // chip (click = +1); a badge shows the count (click badge = -1). Selections
  // live in CG_SEL[handle]. Optional leading "none" chip clears the accessory.
  function cgWireQtyChips(handle, container, variants, withNone, noneLabel) {
    var st = CG_SEL[handle] || (CG_SEL[handle] = { chips: null, qty: {}, vmap: {} });
    variants.forEach(function (v) { st.vmap[String(v.id)] = v; });
    st.list = variants;                 // ordered list — [0] is the default display variant
    st.chips = container;
    var labels = shortChipLabels(variants);
    if (withNone) {
      var noneChip = document.createElement('button');
      noneChip.type = 'button';
      noneChip.className = 'cg-upsell-chip cg-opt-none is-active';
      noneChip.textContent = noneLabel;
      noneChip.addEventListener('click', function () { st.qty = {}; st.lastVid = null; cgPaint(handle); });
      container.appendChild(noneChip);
    }
    variants.forEach(function (v, idx) {
      var vid = String(v.id);
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cg-upsell-chip';
      chip.setAttribute('data-vid', vid);
      chip.style.setProperty('position', 'relative', 'important');   // anchor the qty badge
      chip.style.setProperty('overflow', 'visible', 'important');
      chip.appendChild(document.createTextNode(cgCleanLabel(labels[idx])));
      chip.addEventListener('click', function () {
        st.qty[vid] = (st.qty[vid] || 0) + 1;
        st.lastVid = vid;               // show this variant's price + thumbnail
        cgPaint(handle);
      });
      container.appendChild(chip);
    });
  }
  // Reflect the selected add-on variant in the item's own price + thumbnail (the
  // theme renders only the default variant; the chips just changed the qty before).
  // Shows the last-clicked variant if still selected, else any selected, else the
  // default ([0]). Only for VISIBLE add-on rows — the top HDD/PoE source items are
  // hidden, so their price/image aren't shown.
  function cgUpdateItemDisplay(handle) {
    var st = CG_SEL[handle];
    if (!st || !st.list) return;
    var item = cgFindUpsell(handle);
    if (!item || !item.classList.contains('cg-opt-mqty')) return;
    var vid = (st.lastVid && st.qty[st.lastVid]) ? st.lastVid : null;
    if (!vid) { for (var i = 0; i < st.list.length; i++) { var id = String(st.list[i].id); if (st.qty[id]) { vid = id; break; } } }
    var v = vid ? st.vmap[vid] : null;
    if (v) {
      setPrice(item, v.price || '', v.price_old || '');
      setImage(item, v.image || '');
    } else {
      // None selected (incl. initial load) → $0.00, since nothing is added to the
      // bundle. Keep the default product image so the row still shows its photo.
      setPrice(item, '$0.00', '');
      setImage(item, (st.list[0] && st.list[0].image) || '');
    }
  }

  function cgBuildOptRow(id, labelText, handle, noneLabel, hint) {
    var item = cgFindUpsell(handle);
    if (!item) return null;
    var variants = getHiddenVariants(item);
    if (!variants.length) return null;
    var row = document.createElement('div');
    row.className = 'cg-opt__row';
    var lab = document.createElement('label');
    lab.className = 'cg-opt__label';
    lab.textContent = labelText;
    row.appendChild(lab);
    if (hint) {
      var h = document.createElement('div');
      h.className = 'cg-opt__hint';
      h.textContent = hint;
      row.appendChild(h);
    }
    var chips = document.createElement('div');
    chips.className = 'cg-upsell-chips cg-opt__chips';
    chips.id = id;
    cgWireQtyChips(handle, chips, variants, true, noneLabel);
    row.appendChild(chips);
    return row;
  }

  // Add to cart: base unit + every selected size (× qty) + any other ticked
  // add-on (UPS/brackets), as separate lines, in one /cart/add.js call.
  function cgBundleAdd(e) {
    // Pick the form that actually carries the variant id + Add-to-cart button.
    // (A Shop Pay installment form can also match form[action*="/cart/add"] and
    // sit first in the DOM, so never trust querySelector's first match.)
    var forms = document.querySelectorAll('#main-product form[action*="/cart/add"]');
    if (!forms.length) forms = document.querySelectorAll('form[action*="/cart/add"]');
    var form = null;
    for (var fi = 0; fi < forms.length; fi++) {
      if (forms[fi].querySelector('[name="id"]')) { form = forms[fi]; break; }
    }
    if (!form) form = forms[0];
    if (!form) return;
    var extras = [], seen = {};
    for (var h in CG_SEL) {
      var st = CG_SEL[h];
      for (var vid in st.qty) {
        var q = st.qty[vid];
        if (q > 0) { extras.push({ id: parseInt(vid, 10), quantity: q }); seen[vid] = true; }
      }
    }
    // Other add-ons the customer ticked in the Add-Ons list (not our HDD/Cat6).
    var boxes = document.querySelectorAll('#main-product .f8pr-upsell .upsell-items > li .check input[type="checkbox"]:checked');
    Array.prototype.forEach.call(boxes, function (cb) {
      var li = cb.closest('li');
      if (!li || li.classList.contains('cg-opt-source-hidden') || li.classList.contains('cg-opt-mqty')) return;
      var sel = li.querySelector('select[name="upsell-id"]');
      var vid = cb.dataset.id || (sel ? sel.value : '');
      if (vid && !seen[String(vid)]) { extras.push({ id: parseInt(vid, 10), quantity: 1 }); seen[String(vid)] = true; }
    });
    try { sessionStorage.setItem('cgLastAdd', 'ran; extras=' + extras.length + ' :: ' + JSON.stringify(extras)); } catch (_e) {}
    if (!extras.length) return;   // nothing extra → let the theme add the base normally
    if (e) { e.preventDefault(); e.stopPropagation(); if (e.stopImmediatePropagation) e.stopImmediatePropagation(); }
    var idInput = form.querySelector('[name="id"]');
    var qInput = form.querySelector('[name="quantity"]');
    if (!idInput) return;
    var baseQ = qInput ? (parseInt(qInput.value, 10) || 1) : 1;
    var items = [{ id: parseInt(idInput.value, 10), quantity: baseQ > 0 ? baseQ : 1 }].concat(extras);
    try { sessionStorage.setItem('cgLastAdd', JSON.stringify(items)); } catch (_e) {}
    if (window.__cgSetAddBusy) { try { window.__cgSetAddBusy(); } catch (_b) {} }  // restore the loading sweep the takeover suppresses
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        try { sessionStorage.setItem('cgLastAddResult', JSON.stringify(data).slice(0, 600)); } catch (_e) {}
        window.location.href = '/cart';
      })
      .catch(function () { try { form.submit(); } catch (_) {} });
  }

  function cgOptionDropdowns() {
    var hddItem = cgFindUpsell(CG_OPT_HDD);
    var cableItem = cgFindUpsell(CG_OPT_CABLE);
    if (!hddItem && !cableItem) return;

    // (Re)hide the source rows every pass — survives the theme re-rendering the
    // Add-Ons list (which drops the class from a freshly-rendered <li>).
    if (hddItem) hddItem.classList.add('cg-opt-source-hidden');
    if (cableItem) cableItem.classList.add('cg-opt-source-hidden');

    var host = document.getElementById('cg-opt-dropdowns');
    if (!host) {
      host = document.createElement('div');
      host.id = 'cg-opt-dropdowns';
      host.className = 'cg-opt';
      var anchor = document.querySelector('#main-product .f8pr-price') ||
                   document.querySelector('#main-product .f8pr-button');
      if (!anchor || !anchor.parentNode) return;
      anchor.parentNode.insertBefore(host, anchor);
    }
    if (hddItem && !document.getElementById('cg-opt-hdd')) {
      var r1 = cgBuildOptRow('cg-opt-hdd', 'Storage: WD Purple or Seagate SkyHawk HDD', CG_OPT_HDD, 'No HDD');
      if (r1) host.appendChild(r1);
    }
    if (cableItem && !document.getElementById('cg-opt-cable')) {
      var r2 = cgBuildOptRow('cg-opt-cable', 'PoE Cat6 cable', CG_OPT_CABLE, 'No cable');
      if (r2) host.appendChild(r2);
    }

  }

  // Bind the Add-to-cart takeover once. cgBundleAdd no-ops (letting the theme
  // add the base normally) whenever nothing accessory is selected, so this is
  // safe to bind on every product.
  function cgBindTakeover() {
    if (window.__cgBundleBound) return;
    if (!document.getElementById('main-product') && !document.querySelector('#main-product')) return;
    window.__cgBundleBound = true;
    // Bind at the document level (capture phase) so the takeover fires no matter
    // how many cart forms exist (Shop Pay installment forms, etc.) or which one
    // holds the real Add-to-cart button. Capture runs before the theme's own
    // handlers, so cgBundleAdd can pre-empt the theme's single-item add.
    document.addEventListener('submit', function (ev) {
      var f = ev.target;
      if (f && f.matches && f.matches('form[action*="/cart/add"]') && f.closest && f.closest('#main-product')) cgBundleAdd(ev);
    }, true);
    document.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest
        ? ev.target.closest('#main-product [name="add"], #main-product .overlay-buy_button')
        : null;
      if (btn) cgBundleAdd(ev);
    }, true);
  }

  function boot() {
    preloadUpsells();
    buildAllChips();
    cgOptionDropdowns();
    cgBindTakeover();
    scheduleTotalRender(0);
    scheduleTotalRender(200);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Rebuild chips if the upsell list re-renders (idempotent via the dataset guard).
  if (window.__cgCore) { window.__cgCore.register(buildAllChips); window.__cgCore.register(cgOptionDropdowns); window.__cgCore.register(cgBindTakeover); }

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('#main-product .f8pr-upsell a[data-upsell]');
    if (openBtn) { activeUpsellItem = openBtn.closest('li'); return; }
    var chooseBtn = e.target.closest('article[data-title="upsell-popup"] .link-btn a');
    if (chooseBtn) {
      setTimeout(syncActiveUpsell, 10);
      setTimeout(syncActiveUpsell, 80);
      setTimeout(syncActiveUpsell, 180);
    }
  }, true);

  document.addEventListener('change', function (e) {
    if (e.target.matches('#main-product .f8pr-upsell .check input[type="checkbox"]') ||
        e.target.matches('#main-product input[name="quantity"]')) {
      scheduleTotalRender(0);
      scheduleTotalRender(80);
    }
    if (e.target.matches('#main-product select[name="id"]') ||
        e.target.matches('#main-product select[name="variant-id"]') ||
        e.target.matches('#main-product select[name^="options"]') ||
        e.target.matches('#main-product input[type="radio"][name^="options"]')) {
      setTimeout(function () {
        resetBaseUnitPrices();
        scheduleTotalRender(60);
        scheduleTotalRender(180);
        scheduleTotalRender(320);
      }, 0);
    }
  }, true);

  document.addEventListener('input', function (e) {
    if (e.target.matches('#main-product input[name="quantity"]')) scheduleTotalRender(0);
  }, true);
})();


/* =========================================================
   [CGJS-35] SHOPIFY VARIANT SELECTS → CHIPS
   Turn each native variant option <select> (Colour, NVR Recorder, Lens, …) into
   a row of single-select chips matching the accessory chips. Clicking a chip
   sets the select's value and fires `change`, so the theme's own variant / price
   / availability logic runs unchanged. The native select (+ its styled widget)
   is hidden. Sold-out options are shown struck-through and are not clickable.
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgVariantChips) return;
  window.__cgVariantChips = true;
  var core = window.__cgCore;

  // Convert Shopify variant <select>s into chips — COLOUR option only (gate
  // below). The theme rebuilds the whole variant widget on every variant change
  // (~67 DOM mutations, in async stages ~1s) — that re-render, NOT our chips, is
  // what makes the native dropdown briefly unresponsive right after a change. We
  // re-convert the chips on delayed ticks so they survive that re-render, and
  // keep our added churn tiny (the price count-up now writes via nodeValue, no
  // observer storm). See [[cctvguru-variant-chips-rerender]].
  var CG_VARIANT_CHIPS_ENABLED = true;
  if (!CG_VARIANT_CHIPS_ENABLED) return;

  // Read a select's option label text (e.g. "Colour", "NVR", "Lens Type").
  function cgSelectLabel(sel) {
    var p = sel.closest('p') || sel.parentElement;
    var lab = p && p.querySelector('label');
    if (!lab) return '';
    var tn = lab.childNodes[0];
    return ((tn && tn.nodeType === 3) ? tn.nodeValue : lab.textContent).replace(/\s+/g, ' ').trim();
  }
  // Only the Colour option becomes chips (match the label or the select name).
  function cgIsColourSelect(sel) {
    return /colou?r/i.test(cgSelectLabel(sel)) || /colou?r/i.test(sel.name || '');
  }

  function paint(sel) {
    var chips = sel._cgChips;
    if (!chips) return;
    var val = sel.value;
    for (var i = 0; i < sel.options.length; i++) {
      var chip = chips.querySelector('button[data-idx="' + i + '"]');
      if (!chip) continue;
      chip.classList.toggle('is-active', sel.options[i].value === val);
      chip.classList.toggle('is-oos', !!sel.options[i].disabled);
    }
  }

  // Shorten a long variant option label so it fits a box: drop a trailing
  // "(tagline)", a "— MODEL" suffix, redundant "AI NVR"/"NVR", and turn
  // "8-Channel" into "8CH". Falls back to the original if it'd empty out.
  function cgShortenLabel(s) {
    var out = String(s || '')
      .replace(/\s*\([^)]*\)\s*$/, '')                 // "(smart search)", "(DH-PFM350-900)"
      .replace(/\s*[—–]\s.*$/, '')                     // em/en dash + everything after ("— NVR5208…", "– 24/7 Recording")
      .replace(/\bSurveillance\b/gi, '')               // "Surveillance HDD" boilerplate
      .replace(/\bAI\s+NVR\b/gi, '')                   // "AI NVR"
      .replace(/\bNVR\b/gi, '')                         // stray "NVR"
      .replace(/\bHDD\b/gi, '')                         // stray "HDD"
      .replace(/(\d+)\s*-\s*Channel/gi, '$1CH')         // "8-Channel" -> "8CH"
      .replace(/\s{2,}/g, ' ')
      .trim();
    return out || String(s || '').trim();
  }
  // Lens options ("2.8mm (108.8° Wide Angle)") get richer labels than the
  // generic shortener: "2.8mm 108.8° Wide", falling back to "2.8mm Wide" if the
  // row wouldn't fit on one line (checked after render). Returns {withDeg,noDeg}
  // or null when it isn't a lens option (every option must start with "<n>mm").
  function cgLensLabels(sel) {
    if (sel.options.length < 2) return null;
    var withDeg = [], noDeg = [];
    for (var i = 0; i < sel.options.length; i++) {
      var t = (sel.options[i].textContent || '').trim();
      var mm = t.match(/^\s*([\d.]+)\s*mm\b/i);
      if (!mm) return null;
      var deg = t.match(/([\d.]+)\s*°/);
      var d = /\bwide\b/i.test(t) ? 'Wide'
        : (/\bnarrow/i.test(t) ? 'Narrow'
        : (/\btele/i.test(t) ? 'Tele' : ''));
      var base = mm[1] + 'mm';
      noDeg.push((base + (d ? ' ' + d : '')).trim());
      withDeg.push((base + (deg ? ' ' + deg[1] + '°' : '') + (d ? ' ' + d : '')).trim());
    }
    return { withDeg: withDeg, noDeg: noDeg };
  }
  // Display labels for a select's options — shortened, UNLESS shortening would
  // make any two collide (then keep the full labels so the choices stay
  // distinguishable).
  function cgBoxLabels(sel) {
    var full = [], shortl = [], seen = {}, dup = false;
    for (var i = 0; i < sel.options.length; i++) {
      var f = (sel.options[i].textContent || '').trim();
      var sh = cgShortenLabel(f);
      full.push(f); shortl.push(sh);
      var key = sh.toLowerCase();
      if (seen[key]) dup = true;
      seen[key] = true;
    }
    return dup ? full : shortl;
  }

  function build() {
    // The theme uses a single master select "variant-id" (styled by a
    // .bv_mainselect widget). Some themes also use per-option selects.
    var selects = document.querySelectorAll('#main-product select[name="variant-id"], #main-product select[name^="options"]');
    // When per-option selects exist, the combined master "variant-id" holds
    // multi-option combos (e.g. "White / 2.8mm") — and its label is just the
    // FIRST option's name, so a Colour-first product makes it look like a Colour
    // select and it'd wrongly become clumsy combo chips. Skip the master in that
    // case; the per-option Colour select drives the chips. (On single-option
    // colour products there's no per-option select, so the master IS the colour
    // picker and is allowed through.)
    var hasPerOption = !!document.querySelector('#main-product select[name^="options"]');
    for (var i = 0; i < selects.length; i++) {
      var sel = selects[i];
      if (sel.options.length < 2) continue;
      // Self-heal: the theme rebuilds the whole variant widget on every variant
      // change, which wipes our chips. If this select was converted before but
      // its chips are no longer in the DOM, clear the flag so it re-converts.
      if (sel.dataset.cgVchip === '1') {
        if (sel._cgChips && document.body.contains(sel._cgChips)) continue;
        delete sel.dataset.cgVchip;
      }
      if (sel.name === 'variant-id' && hasPerOption) continue;   // never chip the combined master
      // Convert EVERY variant option to boxes (Colour, NVR, Lens, Channels…) so
      // there's no native dropdown widget left to glitch. Labels are auto-
      // shortened to fit; the row scrolls (with ‹ › arrows) if it overflows.
      // No option-count cap — [CG-110] hides ALL native variant <p>s once boxes
      // are active, so every one must actually be converted (else it'd vanish).
      var lens = cgLensLabels(sel);
      var labels = lens ? lens.withDeg : cgBoxLabels(sel);
      sel.dataset.cgVchip = '1';

      var chips = document.createElement('div');
      chips.className = 'cg-vchips';
      (function (sel, chips, labels) {
        for (var k = 0; k < sel.options.length; k++) {
          (function (o, idx) {
            var chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'cg-vchip';
            chip.setAttribute('data-idx', String(idx));
            chip.textContent = labels[idx];
            chip.addEventListener('click', function () {
              if (o.disabled) return;
              sel.value = o.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
              paint(sel);
            });
            chips.appendChild(chip);
          })(sel.options[k], k);
        }
      })(sel, chips, labels);

      // Put the chips inside the select's container and hide the styled widget
      // (.bv_mainselect). The native select is already hidden by the theme.
      // The native select, its .bv_mainselect widget AND the option label all
      // live inside one <p class="has-select">. Rather than fight the theme to
      // hide the widget (and its blue .bv_background chrome) while keeping the
      // label, rebuild the label ourselves, drop label + chips in AFTER the <p>,
      // then hide the ENTIRE <p>. One move, nothing left to leak.
      var p = sel.closest('p') || sel.parentElement;
      var origLabel = p.querySelector('label');
      var labelText = '';
      if (origLabel) {
        var tn = origLabel.childNodes[0];
        labelText = ((tn && tn.nodeType === 3) ? tn.nodeValue : origLabel.textContent).replace(/\s+/g, ' ').trim();
      }
      if (labelText) {
        var myLabel = document.createElement('div');
        myLabel.className = 'cg-vlabel';
        myLabel.textContent = labelText;
        p.parentNode.insertBefore(myLabel, p.nextSibling);
        p.parentNode.insertBefore(chips, myLabel.nextSibling);
      } else {
        p.parentNode.insertBefore(chips, p.nextSibling);
      }
      p.style.setProperty('display', 'none', 'important');
      var block = sel.closest('.f8pr-variant-selection') || p.parentNode;
      if (block) block.classList.add('cg-vchips-on');
      // Persistent body flag → CSS keeps the native variant dropdowns hidden
      // even in the brief moment the theme re-renders them (before we re-convert),
      // so the dropdown never flashes into view. Set only after a real conversion.
      document.body.classList.add('cg-vboxes');
      sel._cgChips = chips;
      sel.addEventListener('change', function () { paint(this); });
      paint(sel);
      // Lens fit-check: the degree-rich labels are in; if they wrap to a 2nd row,
      // fall back to the shorter "2.8mm Wide" form so it stays one line. (offsetTop
      // forces a synchronous reflow, so this swaps before the browser paints.)
      if (lens && chips.clientWidth > 20) {   // only when a real width is measurable
        var lbtns = chips.querySelectorAll('.cg-vchip');
        var top0 = lbtns.length ? lbtns[0].offsetTop : 0, wrapped = false;
        for (var z = 0; z < lbtns.length; z++) { if (lbtns[z].offsetTop > top0 + 2) { wrapped = true; break; } }
        if (wrapped) { for (var z2 = 0; z2 < lbtns.length; z2++) lbtns[z2].textContent = lens.noDeg[z2]; }
      }
    }
    // [CG-111] Record the variant block's current (boxes-present) height so CSS
    // can reserve it and it won't collapse to 0 when the theme wipes the boxes on
    // the next variant change (that collapse shoved the price ~150px = "flash").
    var cgVs = document.querySelector('#main-product .f8pr-variant-selection');
    if (cgVs && cgVs.querySelector('.cg-vchips') && cgVs.offsetHeight > 0) {
      // Only ever RAISE the reservation. build() also runs on delayed ticks while
      // the theme is mid-rebuild, where vs can measure short; the block's full
      // height is constant per product, so locking to the max seen can't shrink
      // it back down and re-open the collapse.
      var cgCur = parseInt(getComputedStyle(document.body).getPropertyValue('--cg-vs-h'), 10) || 0;
      if (cgVs.offsetHeight > cgCur) document.body.style.setProperty('--cg-vs-h', cgVs.offsetHeight + 'px');
    }
  }

  build();
  if (core) core.register(build);
  // The theme rebuilds the variant widget asynchronously after a variant change
  // (in stages, after its availability/price update), and the shared observer's
  // single debounced tick can miss the final render — leaving the colour picker
  // reverted to a native dropdown. Re-run build on a few delayed ticks after any
  // variant change so the chips are reliably restored once the theme settles.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('#main-product select[name="variant-id"], #main-product select[name^="options"]')) {
      // The theme re-renders in stages; its FINAL render can land ~1s later, so
      // include a late tick or the restored chips get wiped again (verified live).
      setTimeout(build, 80);
      setTimeout(build, 300);
      setTimeout(build, 700);
      setTimeout(build, 1400);
    }
  }, true);
})();


/* =========================================================
   [CGJS-36] DESKTOP SCROLL ARROWS for the horizontal chip rows
   (HDD / PoE / multi-qty Add-Ons). Touch users swipe; PC users get
   clickable ‹ › arrows that appear only when the row overflows in
   that direction. CSS limits them to desktop (min-width:1001px).
   Instant scroll (scrollLeft) is used deliberately — native smooth
   scroll silently no-ops in some embedded webviews.
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgChipArrows) return;
  window.__cgChipArrows = true;
  var core = window.__cgCore;

  function arrowSvg(dir) {
    var d = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 6l6 6-6 6';
    return '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
      '<path d="' + d + '" fill="none" stroke="currentColor" stroke-width="2.6" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function update(wrap, sc) {
    var max = sc.scrollWidth - sc.clientWidth;
    var x = sc.scrollLeft;
    wrap.classList.toggle('cg-can-left', x > 2);
    wrap.classList.toggle('cg-can-right', max > 2 && x < max - 2);
  }

  function wire(sc) {
    if (!sc || sc.dataset.cgArrows === '1' || !sc.parentNode) return;
    sc.dataset.cgArrows = '1';
    var wrap = document.createElement('div');
    wrap.className = 'cg-chip-scroller';
    sc.parentNode.insertBefore(wrap, sc);
    wrap.appendChild(sc);
    ['left', 'right'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cg-chip-arrow cg-chip-arrow--' + dir;
      b.tabIndex = -1;
      b.setAttribute('aria-label', dir === 'left' ? 'Scroll left' : 'Scroll right');
      b.innerHTML = arrowSvg(dir);
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var amt = Math.max(140, Math.round(sc.clientWidth * 0.75));
        sc.scrollLeft += (dir === 'left' ? -amt : amt);
        update(wrap, sc);
      });
      wrap.appendChild(b);
    });
    sc.addEventListener('scroll', function () { update(wrap, sc); }, { passive: true });
    window.addEventListener('resize', function () { update(wrap, sc); });
    update(wrap, sc);
    setTimeout(function () { update(wrap, sc); }, 250);
  }

  function run() {
    var rows = document.querySelectorAll(
      '#main-product .cg-opt__chips, ' +
      '#main-product .upsell-items > li.cg-opt-mqty .cg-upsell-chips, ' +
      '#main-product .f8pr-variant-selection .cg-vchips'
    );
    Array.prototype.forEach.call(rows, wire);
  }

  run();
  if (core) core.register(run);
})();


/* =========================================================
   [CGJS-37] COUNT-UP THE PRICE when the variant changes — same rolling
   number as the HDD/PoE selectable boxes. The theme sets the new price
   instantly and re-renders it in stages, so our rAF loop re-queries the
   price element every frame and overwrites the current value — winning
   the race against the theme's occasional writes (and element swaps).
   Covers the main price and the mobile sticky buy-bar price.
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgPricePulse) return;
  window.__cgPricePulse = true;

  // Price freeze + roll. [CG-111] now reserves the variant block's height so the
  // price no longer moves ~150px during the theme's re-render — which means this
  // cover overlay finally stays aligned. On a variant change we cover the price
  // with a steady clone while the theme re-renders/re-fetches (~1.5s on kits),
  // then reveal instantly if unchanged, or roll old→new if it changed. No flash,
  // no fake transient values, and NVR animates like the HDD/PoE selections.
  var CG_PRICE_FX = true;
  if (!CG_PRICE_FX) return;

  var PSEL = '#main-product .f8pr-price.s1pr.price';
  function priceEl() { return document.querySelector(PSEL); }
  function currentNum() {
    var el = priceEl(); if (!el) return NaN;
    var m = (el.textContent || '').replace(/incl[\s\S]*/i, '').match(/\$[\d,]+(?:\.\d{2})?/g);
    return m ? parseFloat(m[m.length - 1].replace(/[$,]/g, '')) : NaN;
  }
  function fmt(n) { return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

  // ---- Variant price cache -------------------------------------------------
  // /products/{handle}.js carries every variant's price, so on a variant change
  // we can show the new price INSTANTLY instead of waiting ~1.5s for the theme's
  // section re-fetch. The theme still re-fetches in the background (SKU, stock,
  // boxes); the price just no longer waits on it.
  var cgVariants = null;
  // The price shown on the page is a BUNDLE total (variant price + any selected
  // add-ons like UPS/HDD/PoE, layered on by the bundle code). So we animate by
  // the VARIANT price DELTA, not the absolute variant price — add-ons don't change
  // on a variant switch, so newShown = oldShown + (newVariant − oldVariant). This
  // makes same-price switches (White↔Black) a zero delta = no animation, and keeps
  // NVR switches correct even with add-ons active. cgLastVariantPrice tracks the
  // currently-selected variant's own price (dollars).
  var cgLastVariantPrice = NaN;
  (function loadVariants() {
    var host = document.querySelector('#main-product');
    var url = (host && host.getAttribute('data-product-url')) || window.location.pathname;
    if (!/^\/products\//.test(url)) return;
    try {
      fetch(url.split('?')[0] + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (p) {
          if (p && p.variants) { cgVariants = p.variants; var v0 = cgTargetVariant(); if (v0) cgLastVariantPrice = v0.price / 100; }
        })
        .catch(function () {});
    } catch (_) {}
  })();
  // The variant matching the CURRENT option selections (post-change), so we can
  // read its price from the cache. Matches per-option selects by value; falls
  // back to the single variant-id select/input for one-option products.
  function cgTargetVariant() {
    if (!cgVariants) return null;
    var perOpt = document.querySelectorAll('#main-product select[name^="options"]');
    if (perOpt.length) {
      var vals = [].map.call(perOpt, function (s) { return (s.value || '').trim(); });
      for (var i = 0; i < cgVariants.length; i++) {
        var v = cgVariants[i], ok = true, opts = v.options || [];
        for (var j = 0; j < vals.length; j++) {
          if (('' + (opts[j] == null ? '' : opts[j])).trim() !== vals[j]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }
    var master = document.querySelector('#main-product select[name="variant-id"], #main-product input[name="variant-id"]');
    if (master) { var id = parseInt(master.value, 10); for (var k = 0; k < cgVariants.length; k++) if (cgVariants[k].id === id) return cgVariants[k]; }
    return null;
  }

  // ---- Cover + instant roll ------------------------------------------------
  // A single controller persists across rapid changes so quick NVR→Colour clicks
  // re-target the same cover clone instead of fighting. On a change we cover the
  // live price with a steady clone, roll it to the CACHED new price immediately
  // (no waiting), then hold the cover until the theme's own re-fetch lands the
  // same value underneath — reconciling if the cache was ever off — and reveal.
  var ctl = null;
  function cgClearTimers(c) {
    if (!c) return;
    clearTimeout(c.settleTimer); clearTimeout(c.settleCap);
    if (c.poll) { clearInterval(c.poll); c.poll = 0; }
    if (c.mo) { try { c.mo.disconnect(); } catch (_) {} c.mo = null; }
    c.finished = true;
  }
  function cgCleanup(c) {
    cgClearTimers(c);
    if (c.raf) { cancelAnimationFrame(c.raf); c.raf = 0; }
    if (c.stRaf) { cancelAnimationFrame(c.stRaf); c.stRaf = 0; }
    // Reconcile the sticky bar to the final live price, then release its hold so
    // syncPrice takes over again.
    if (c.stEl) { var lv = currentNum(); if (!isNaN(lv)) cgSetSticky(c, lv); }
    if (c.held) { window.__cgStickyPriceHold = false; c.held = false; }
    if (c.clone && c.clone.parentNode) c.clone.parentNode.removeChild(c.clone);
    if (c.setRel && c.parent) { c.parent.style.position = c.prevPos || ''; c.setRel = false; }
    if (ctl === c) ctl = null;
  }
  function cgSetClone(c, v) {
    var s = fmt(v);
    if (c.cloneNode) c.cloneNode.nodeValue = s; else if (c.cloneAmt) c.cloneAmt.textContent = s;
    c.shownNum = v;
  }
  function cgRoll(c, to) {                                    // animate the clone shown→to
    if (c.raf) { cancelAnimationFrame(c.raf); c.raf = 0; }
    if (!c.cloneAmt || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) { cgSetClone(c, to); return; }
    var from = c.shownNum, start = null, dur = 280;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var ease = 1 - Math.pow(1 - p, 3);
      cgSetClone(c, from + (to - from) * ease);
      if (p < 1) c.raf = requestAnimationFrame(step); else { c.raf = 0; cgSetClone(c, to); }
    }
    c.raf = requestAnimationFrame(step);
    setTimeout(function () { if (c.raf) { cancelAnimationFrame(c.raf); c.raf = 0; cgSetClone(c, to); } }, dur + 220);
  }
  // Mobile-price format: the sticky buy-bar drops the ".00" on whole dollars
  // (matches its stripWholeDollarCents), so mid-roll frames don't show odd cents.
  function fmtSticky(n) {
    var whole = Math.abs(n - Math.round(n)) < 0.005;
    return '$' + n.toLocaleString('en-AU', whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function cgSetSticky(c, v) {
    if (!c.stEl) return;
    var s = fmtSticky(v);
    if (c.stNode && c.stEl.contains(c.stNode)) c.stNode.nodeValue = s; else c.stEl.textContent = s;
    c.stShown = v;
  }
  // Roll the mobile sticky-bar price (its own element — the main price is hidden
  // on mobile, so the desktop clone above isn't visible there). syncPrice is held
  // while this runs so the theme's slower main-price update can't snap it back.
  function cgRollSticky(c, to) {
    if (!c.stEl) return;
    if (c.stRaf) { cancelAnimationFrame(c.stRaf); c.stRaf = 0; }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { cgSetSticky(c, to); return; }
    var from = isNaN(c.stShown) ? to : c.stShown, start = null, dur = 280;
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var ease = 1 - Math.pow(1 - p, 3);
      cgSetSticky(c, from + (to - from) * ease);
      if (p < 1) c.stRaf = requestAnimationFrame(step); else { c.stRaf = 0; cgSetSticky(c, to); }
    }
    c.stRaf = requestAnimationFrame(step);
    setTimeout(function () { if (c.stRaf) { cancelAnimationFrame(c.stRaf); c.stRaf = 0; cgSetSticky(c, to); } }, dur + 220);
  }
  // Watch until the theme's LIVE price stops churning, then run cb — adapts to
  // fast (same-price ~120ms) and slow (kit re-fetch ~1.5s) updates. Used only to
  // decide when it's safe to reveal (or, on a cache miss, to roll).
  function cgWatchSettle(c, host, baseOld, cb) {
    c.finished = false; var newVal = NaN, stable = 0;
    function done() { if (c.finished) return; cgClearTimers(c); cb(); }
    function schedule() { clearTimeout(c.settleTimer); c.settleTimer = setTimeout(done, 300); }
    c.mo = new MutationObserver(schedule);
    try { c.mo.observe(host, { childList: true, subtree: true, characterData: true }); } catch (_) {}
    c.poll = setInterval(function () {
      var v = currentNum();
      if (!isNaN(v) && v !== baseOld) { if (v === newVal) stable++; else { stable = 0; newVal = v; } if (stable >= 4) done(); }
      else { stable = 0; newVal = NaN; }
    }, 30);
    schedule();
    c.settleCap = setTimeout(done, 2600);                    // absolute cap — never freeze longer
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches ||
        !t.matches('#main-product select[name="variant-id"], #main-product select[name^="options"]')) return;
    var el = priceEl(); if (!el) return;

    // Reuse the live controller (rapid clicks) or start a fresh cover.
    var c = ctl;
    if (!c || !c.clone || !document.body.contains(c.clone)) {
      var oldNum = currentNum(); if (isNaN(oldNum)) return;
      var parent = el.parentNode;
      var setRel = false, prevPos = parent.style.position;
      if (getComputedStyle(parent).position === 'static') { parent.style.position = 'relative'; setRel = true; }
      var clone = el.cloneNode(true);
      clone.classList.add('cg-price-frozen');
      clone.setAttribute('aria-hidden', 'true');
      clone.style.setProperty('position', 'absolute', 'important');
      clone.style.setProperty('left', el.offsetLeft + 'px', 'important');
      clone.style.setProperty('top', el.offsetTop + 'px', 'important');
      clone.style.setProperty('margin', '0', 'important');
      clone.style.setProperty('z-index', '10', 'important');
      clone.style.setProperty('background', '#fff', 'important');
      clone.style.setProperty('pointer-events', 'none', 'important');
      parent.insertBefore(clone, el.nextSibling);
      var cloneAmt = clone.querySelector('.cg-price-amount');
      var cloneNode = (cloneAmt && cloneAmt.firstChild && cloneAmt.firstChild.nodeType === 3) ? cloneAmt.firstChild : null;
      // Mobile sticky-bar price (its own visible element; the main price is
      // hidden < 1001px). Roll this too so mobile animates like desktop.
      var stEl = document.querySelector('#cg-sticky-buybar .cg-price-amount');
      var stNode = (stEl && stEl.firstChild && stEl.firstChild.nodeType === 3) ? stEl.firstChild : null;
      var stShown = stEl ? parseFloat((stEl.textContent || '').replace(/[^0-9.]/g, '')) : NaN;
      c = ctl = {
        clone: clone, cloneAmt: cloneAmt, cloneNode: cloneNode, shownNum: oldNum, baseOld: oldNum,
        parent: parent, prevPos: prevPos, setRel: setRel, raf: 0, settleTimer: 0, poll: 0, mo: null, settleCap: 0, finished: true,
        stEl: stEl, stNode: stNode, stShown: stShown, stRaf: 0, held: false
      };
    }
    cgClearTimers(c);

    var host = el.closest('.f8pr') || document.querySelector('#main-product') || c.parent;
    var tv = cgTargetVariant();
    var variantNew = tv ? tv.price / 100 : NaN;
    // Delta this variant switch adds to the shown price (0 for same-price switches
    // like White↔Black). NaN if we can't resolve it (cache not ready yet).
    var delta = (!isNaN(variantNew) && !isNaN(cgLastVariantPrice)) ? (variantNew - cgLastVariantPrice) : NaN;
    if (!isNaN(variantNew)) cgLastVariantPrice = variantNew;           // remember for the next switch

    if (!isNaN(delta)) {
      if (Math.abs(delta) >= 0.005) cgRoll(c, c.shownNum + delta);     // INSTANT — bundle-correct, no round-trip
      // Re-resolve the sticky amount in case syncPrice recreated it since capture.
      if (!c.stEl || !document.body.contains(c.stEl)) {
        c.stEl = document.querySelector('#cg-sticky-buybar .cg-price-amount');
        c.stNode = (c.stEl && c.stEl.firstChild && c.stEl.firstChild.nodeType === 3) ? c.stEl.firstChild : null;
        c.stShown = c.stEl ? parseFloat((c.stEl.textContent || '').replace(/[^0-9.]/g, '')) : NaN;
      }
      if (c.stEl && !isNaN(c.stShown) && Math.abs(delta) >= 0.005) {
        window.__cgStickyPriceHold = true; c.held = true;             // own the sticky price while rolling
        cgRollSticky(c, c.stShown + delta);
      }
      cgWatchSettle(c, host, c.baseOld, function () {                  // hold cover until theme catches up
        var live = currentNum();
        if (!isNaN(live) && Math.abs(live - c.shownNum) >= 0.005) cgSetClone(c, live);   // reconcile if delta was off
        cgCleanup(c);
      });
    } else {
      cgWatchSettle(c, host, c.baseOld, function () {                  // unknown delta → roll to whatever the theme lands
        var live = currentNum();
        if (isNaN(live) || live === c.baseOld) cgCleanup(c);
        else { cgRoll(c, live); setTimeout(function () { cgCleanup(c); }, 320); }
      });
    }
  }, true);
})();


/* =========================================================
   [CGJS-04] OKENDO MOBILE TABS
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgOkendoMobileTabsFinal) return;
  window.__cgOkendoMobileTabsFinal = true;

  var core = window.__cgCore;
  var mq = window.matchMedia('(max-width: 1000px)');
  var attrId = 'data-cg-okendo-move-id';
  var anchorPrefix = 'cg-okendo-anchor-';
  var sectionId = 'cg-oke-mobile-tab';

  function normalize(text) { return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase(); }
  function firstMatch(selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }
  function hasReviewCards(node) {
    return !!node.querySelector(
      '.oke-review, [data-oke-review], .oke-w-review, .oke-w-reviews-list, [class*="review-card"], [class*="review-list"], [class*="reviews-list"]'
    );
  }
  function isMovableReviewBlock(node) {
    if (!node) return false;
    var text = normalize(node.textContent);
    var hasOkendoMarkup = !!node.querySelector(
      '[data-oke-container], .okeReviews, .oke-w, [data-oke-reviews-widget], [data-oke-star-rating], .oke-sr'
    );
    var hasReviewLanguage =
      /happy customers|customer reviews|write a review|no reviews yet|verified reviews|okendo reviews/.test(text);
    var summaryOnly =
      !hasReviewCards(node) && hasOkendoMarkup &&
      /\b\d+\s+reviews?\b/.test(text) &&
      !/write a review|read more|verified buyer|happy customers|customer reviews/.test(text) &&
      text.length < 220;
    return (hasReviewCards(node) || hasReviewLanguage || hasOkendoMarkup) && !summaryOnly;
  }
  function getReviewBlocks() {
    var candidates = document.querySelectorAll(
      '.shopify-block.shopify-app-block, .okeReviews, .oke-w, [data-oke-container], [data-oke-reviews-widget], [data-oke-star-rating], [class*="oke"]'
    );
    var blocks = [];
    Array.prototype.forEach.call(candidates, function (candidate) {
      if (!candidate || candidate.id === sectionId || candidate.closest('#' + sectionId)) return;
      var block = candidate.closest('.shopify-block.shopify-app-block') ||
                  candidate.closest('[data-oke-container]') || candidate;
      if (!isMovableReviewBlock(block)) return;
      var skip = false;
      for (var i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i] === block || blocks[i].contains(block)) { skip = true; break; }
        if (block.contains(blocks[i])) blocks.splice(i, 1);
      }
      if (!skip) blocks.push(block);
    });
    return blocks;
  }
  function ensureAnchor(node, index) {
    var moveId = node.getAttribute(attrId);
    if (!moveId) { moveId = String(index + 1); node.setAttribute(attrId, moveId); }
    var anchorId = anchorPrefix + moveId;
    var anchor = document.getElementById(anchorId);
    if (!anchor) {
      anchor = document.createElement('div');
      anchor.id = anchorId;
      anchor.style.display = 'none';
      node.parentNode.insertBefore(anchor, node);
    }
    return anchor;
  }
  function getInsertTarget() {
    return firstMatch([
      'body.template-product .product-recommendations.has-anchor',
      'body.template-product .product-recommendations.product-recommendations-initialized',
      'body.template-product [data-template*="recommendations"]',
      'body.template-product #main-product .product-recommendations.has-anchor',
      'body.template-product #main-product [class*="product-recommendations"]'
    ]);
  }
  function ensureReviewSection() {
    var section = document.getElementById(sectionId);
    if (section) return section;
    section = document.createElement('div');
    section.id = sectionId;
    section.className = 'has-anchor active';
    section.setAttribute('data-index', '99');
    var header = document.createElement('a');
    header.href = '#';
    header.className = 'tabs-header';
    header.textContent = 'Happy Customers';
    var inner = document.createElement('div');
    inner.className = 'tabs-inner';
    var body = document.createElement('div');
    body.className = 'cg-oke-mobile-tab__body';
    var anchor = document.createElement('span');
    anchor.className = 'anchor';
    anchor.id = 'section-reviews-mobile';
    inner.appendChild(body);
    section.appendChild(header);
    section.appendChild(inner);
    section.appendChild(anchor);
    header.addEventListener('click', function (event) {
      event.preventDefault();
      section.classList.toggle('active');
    });
    return section;
  }
  function placeSection(section) {
    var target = getInsertTarget();
    if (target && target.parentNode) {
      if (target.previousElementSibling !== section) target.parentNode.insertBefore(section, target);
      return true;
    }
    return false;
  }
  function restoreBlocks() {
    Array.prototype.forEach.call(document.querySelectorAll('[' + attrId + ']'), function (block) {
      var moveId = block.getAttribute(attrId);
      if (!moveId) return;
      var anchor = document.getElementById(anchorPrefix + moveId);
      if (anchor && anchor.parentNode && anchor.nextSibling !== block) {
        anchor.parentNode.insertBefore(block, anchor.nextSibling);
      }
    });
    var section = document.getElementById(sectionId);
    if (section && section.parentNode) section.parentNode.removeChild(section);
  }
  function fixRecommendationLabel() {
    var rec = getInsertTarget();
    if (!rec) return;
    var header = rec.querySelector('.tabs-header');
    if (!header) return;
    var heading = header.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) heading.textContent = 'You may also like';
    else header.innerHTML = '<h2>You may also like</h2>';
  }
  function moveBlocks() {
    if (!mq.matches) { restoreBlocks(); return; }
    fixRecommendationLabel();
    var blocks = getReviewBlocks();
    if (!blocks.length) return;
    var section = ensureReviewSection();
    var body = section.querySelector('.cg-oke-mobile-tab__body');
    blocks.forEach(function (block, index) {
      ensureAnchor(block, index);
      if (block.parentNode !== body) body.appendChild(block);
    });
    placeSection(section);
    fixRecommendationLabel();
  }

  core.register(moveBlocks);
  if (mq.addEventListener) mq.addEventListener('change', moveBlocks);
  else if (mq.addListener) mq.addListener(moveBlocks);
})();


/* =========================================================
   [CGJS-05] STICKY BUY BAR  (+ chat link, folded in)
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgStickyBuyBarFinal) return;
  window.__cgStickyBuyBarFinal = true;

  var core = window.__cgCore;
  var toInt = core.toInt;
  var clamp = core.clamp;
  var mq = window.matchMedia('(max-width: 760px)');
  var barId = 'cg-sticky-buybar';
  var queued = false;

  function getPriceSource() { return document.querySelector('#main-product .f8pr-price.s1pr.price'); }
  function getQtyInput() { return document.querySelector('#main-product input[name="quantity"]'); }
  function getRealAddButton() {
    return (
      document.querySelector('#main-product .f8pr-button.wide > .overlay-buy_button') ||
      document.querySelector('#main-product button[name="add"]') ||
      document.querySelector('#main-product [type="submit"][name="add"]') ||
      document.querySelector('#main-product form[action*="/cart/add"] [type="submit"]') ||
      document.querySelector('#main-product .link-btn.submit a') ||
      document.querySelector('#main-product .link-btn.submit button')
    );
  }
  function isDisabledButton(btn) {
    if (!btn) return true;
    return !!(btn.disabled || btn.getAttribute('aria-disabled') === 'true' ||
      btn.classList.contains('disabled') ||
      /sold out|unavailable/i.test((btn.textContent || '').trim()));
  }
  function openFrontChat(event) {
    event.preventDefault();
    if (window.FrontChat) window.FrontChat('show');
  }
  function ensureBar() {
    var bar = document.getElementById(barId);
    if (bar) return bar;
    bar = document.createElement('div');
    bar.id = barId;
    bar.innerHTML =
      '<div class="cg-sticky-buybar__price"></div>' +
      '<div class="cg-sticky-buybar__row">' +
        '<div class="cg-sticky-buybar__qty">' +
          '<button type="button" class="cg-sticky-buybar__qty-btn cg-sticky-buybar__qty-btn--dec" aria-label="Decrease quantity">-</button>' +
          '<span class="cg-sticky-buybar__qty-value">1</span>' +
          '<button type="button" class="cg-sticky-buybar__qty-btn cg-sticky-buybar__qty-btn--inc" aria-label="Increase quantity">+</button>' +
        '</div>' +
        '<button type="button" class="cg-sticky-buybar__button">Add to cart</button>' +
      '</div>';
    document.body.appendChild(bar);

    bar.querySelector('.cg-sticky-buybar__qty-btn--dec').addEventListener('click', function () { changeQty(-1); });
    bar.querySelector('.cg-sticky-buybar__qty-btn--inc').addEventListener('click', function () { changeQty(1); });
    bar.querySelector('.cg-sticky-buybar__button').addEventListener('click', function () {
      var liveBtn = getRealAddButton();
      if (!liveBtn || isDisabledButton(liveBtn)) return;
      setStickyBusy(this); // instant loader feedback
      liveBtn.click();
    });

    // chat link → WhatsApp (wa.me opens the app on mobile / web on desktop)
    var link = document.createElement('a');
    link.href = 'https://wa.me/61468088369';
    link.target = '_blank';
    link.rel = 'noopener';
    link.className = 'cg-sticky-buybar__chat-link';
    link.textContent = 'Questions? Chat on WhatsApp';
    bar.appendChild(link);

    return bar;
  }
  function removeBar() {
    var bar = document.getElementById(barId);
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    document.body.classList.remove('cg-sticky-buybar-active');
    document.body.classList.remove('cg-sticky-buybar-visible');
  }
  function stripWholeDollarCents(container) {
    // Mobile sticky bar only: drop ".00" from whole-dollar prices (e.g.
    // $17,925.00 → $17,925) so big numbers + "Incl. GST" fit on one line.
    // Real cents (e.g. $14.50) are left intact, and "Incl. GST" is untouched
    // because the regex only matches digits followed by exactly ".00".
    if (!container) return;
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    var nodes = [], n;
    while ((n = walker.nextNode())) nodes.push(n);
    for (var i = 0; i < nodes.length; i++) {
      var t = nodes[i].nodeValue;
      if (t && /\d\.00\b/.test(t)) nodes[i].nodeValue = t.replace(/(\d)\.00\b/g, '$1');
    }
  }
  function syncPrice() {
    var source = getPriceSource();
    if (!source) return false;
    // While the price animation ([CGJS-37]) is rolling the sticky amount, don't
    // overwrite it — re-copying the (still-old, until the theme catches up) main
    // price would snap the roll back. The animator reconciles + releases when done.
    if (window.__cgStickyPriceHold) return true;
    var target = ensureBar().querySelector('.cg-sticky-buybar__price');
    target.innerHTML = source.innerHTML;
    stripWholeDollarCents(target);
    return true;
  }
  function syncQty() {
    var input = getQtyInput();
    if (!input) return false;
    var bar = ensureBar();
    var decBtn = bar.querySelector('.cg-sticky-buybar__qty-btn--dec');
    var incBtn = bar.querySelector('.cg-sticky-buybar__qty-btn--inc');
    var valueEl = bar.querySelector('.cg-sticky-buybar__qty-value');
    var min = toInt(input.getAttribute('min'), 1);
    var max = input.getAttribute('max') ? toInt(input.getAttribute('max'), 999) : 999;
    var value = clamp(toInt(input.value, min), min, max);
    input.value = String(value);
    valueEl.textContent = String(value);
    decBtn.disabled = value <= min;
    incBtn.disabled = value >= max;
    return true;
  }
  function isBusyButton(btn) {
    return !!(btn && (btn.dataset.cgBusy === 'true' || btn.classList.contains('cg-is-busy')));
  }
  function setStickyBusy(stickyBtn) {
    if (!stickyBtn.classList.contains('cg-is-busy')) {
      stickyBtn.classList.add('cg-is-busy');
      stickyBtn.innerHTML = '<span class="cg-btn-load" aria-hidden="true"></span><span class="cg-btn-load-text" role="status">Adding…</span>';
    }
    stickyBtn.disabled = true;
  }
  function syncButton() {
    var realBtn = getRealAddButton();
    if (!realBtn) return false;
    var stickyBtn = ensureBar().querySelector('.cg-sticky-buybar__button');
    // Mid-add: show the loader, never "Unavailable"
    if (isBusyButton(realBtn)) { setStickyBusy(stickyBtn); return true; }
    stickyBtn.classList.remove('cg-is-busy');
    var disabled = isDisabledButton(realBtn);
    stickyBtn.disabled = disabled;
    stickyBtn.textContent = disabled
      ? ((realBtn.textContent || 'Unavailable').replace(/\s+/g, ' ').trim() || 'Unavailable')
      : 'Add to cart';
    return true;
  }
  function changeQty(direction) {
    var input = getQtyInput();
    if (!input) return;
    var min = toInt(input.getAttribute('min'), 1);
    var max = input.getAttribute('max') ? toInt(input.getAttribute('max'), 999) : 999;
    var step = toInt(input.getAttribute('step'), 1);
    var current = clamp(toInt(input.value, min), min, max);
    var next = clamp(current + step * direction, min, max);
    if (next === current) return;
    input.value = String(next);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    scheduleUpdate();
  }
  function shouldShowBar() { return !document.body.classList.contains('cg-mobile-menu-open'); }
  function update() {
    if (!mq.matches) { removeBar(); return; }
    if (!syncPrice() || !syncQty() || !syncButton()) { removeBar(); return; }
    document.body.classList.add('cg-sticky-buybar-active');
    document.body.classList.toggle('cg-sticky-buybar-visible', shouldShowBar());
  }
  function scheduleUpdate() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; update(); });
  }

  core.register(update);
  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);

  // Variant / qty changes update the price text via [CGJS-03] (textContent,
  // which the childList observer won't catch) — mirror it on the same events,
  // with a trailing tick to catch CGJS-03's ~80ms render.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest('#main-product')) {
      scheduleUpdate();
      setTimeout(update, 130);
    }
  }, true);
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches('#main-product input[name="quantity"]')) scheduleUpdate();
  }, true);

  if (mq.addEventListener) mq.addEventListener('change', scheduleUpdate);
  else if (mq.addListener) mq.addListener(scheduleUpdate);
})();


/* =========================================================
   [CGJS-06] MOBILE MENU STATE
   ========================================================= */
(function () {
  if (window.__cgMobileMenuStateFinal) return;
  window.__cgMobileMenuStateFinal = true;

  var core = window.__cgCore;
  var scrollQueued = false;

  function getNav() { return document.querySelector('.shopify-section-header #nav'); }
  function isMenuOpen() {
    var nav = getNav();
    if (!nav || window.innerWidth > 1000) return false;
    var style = window.getComputedStyle(nav);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    var rect = nav.getBoundingClientRect();
    return rect.width > 120 && rect.left < window.innerWidth - 24 && rect.right > 24;
  }
  function syncMenuState() {
    var open = isMenuOpen();
    document.documentElement.classList.toggle('cg-mobile-menu-open', open);
    document.body.classList.toggle('cg-mobile-menu-open', open);
  }
  // The theme shows a submenu's child <ul> without adding any class/attr to
  // the <li> (verified in DOM). So we detect "open" by whether the child list
  // is actually visible, and tag the <li> with .cg-open for the CSS chevron +
  // slide animations to hook onto.
  function markOpenToggles() {
    var nav = getNav();
    if (!nav || window.innerWidth > 1000) return;
    var items = nav.querySelectorAll('li.toggle, li.mtoggle');
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      var sub = null, kids = li.children;
      for (var k = 0; k < kids.length; k++) {
        if (kids[k].tagName === 'UL') { sub = kids[k]; break; }
      }
      var open = !!sub && sub.getBoundingClientRect().height > 4;
      li.classList.toggle('cg-open', open);
    }
  }
  function observeNav() {
    var nav = getNav();
    if (!nav || nav.dataset.cgMenuObserved === '1') return;
    nav.dataset.cgMenuObserved = '1';
    // Narrow observer: only nav class/style toggles drive the open state.
    new MutationObserver(syncMenuState).observe(nav, {
      attributes: true, attributeFilter: ['class', 'style']
    });
  }
  function run() { observeNav(); syncMenuState(); markOpenToggles(); }

  core.register(run);

  // Slide the drawer in when the hamburger opens it. We set the off-screen
  // START position while #nav is still hidden (capture phase, before the
  // theme's own handler shows it), then slide to 0 on the next frame — so
  // there's no flash/FOUC (the earlier CSS-keyframe approach flashed because
  // the theme paints the drawer at rest for one frame before the animation).
  function animateDrawerOpen(e) {
    if (window.innerWidth > 1000) return;
    var toggle = e.target && e.target.closest
      ? e.target.closest('[aria-controls="nav"]') : null;
    if (!toggle) return;
    if (toggle.getAttribute('aria-expanded') === 'true') return; // closing → skip
    var nav = getNav();
    if (!nav) return;
    nav.style.setProperty('transition', 'none', 'important');
    nav.style.setProperty('transform', 'translateX(-100%)', 'important');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        nav.style.setProperty('transition', 'transform 0.42s cubic-bezier(0.32,0.72,0,1)', 'important');
        nav.style.setProperty('transform', 'translateX(0)', 'important');
      });
    });
  }
  document.addEventListener('click', animateDrawerOpen, true);

  // (Back-navigation animation removed: the top-level <li>s contain the
  // open panel, so animating them shifts the panel you're still viewing
  // during the theme's panel-close → a visible reset glitch. Not reliably
  // fixable without risking that flicker, so the drawer-open slide above is
  // the animation we keep.)

  document.addEventListener('click', function () {
    // next-frame check so nested tiers animate as instantly as the first tier
    requestAnimationFrame(markOpenToggles);
    requestAnimationFrame(function () { requestAnimationFrame(markOpenToggles); });
    setTimeout(function () { syncMenuState(); markOpenToggles(); }, 30);
    setTimeout(function () { syncMenuState(); markOpenToggles(); }, 180);
    // re-check after the theme's own expand/collapse settles
    setTimeout(markOpenToggles, 430);
  }, true);
  document.addEventListener('touchstart', function () {
    requestAnimationFrame(markOpenToggles);
    setTimeout(function () { syncMenuState(); markOpenToggles(); }, 30);
    setTimeout(function () { syncMenuState(); markOpenToggles(); }, 180);
  }, { passive: true });
  window.addEventListener('resize', syncMenuState);
  // rAF-throttle scroll so we don't force layout on every scroll event.
  window.addEventListener('scroll', function () {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(function () { scrollQueued = false; syncMenuState(); });
  }, { passive: true });
})();


/* =========================================================
   [CGJS-07] DESKTOP REPEATED CTA + SAFE CART REDIRECT
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgDesktopBundleFinal) return;
  window.__cgDesktopBundleFinal = true;

  var core = window.__cgCore;
  var sectionId = 'cg-desktop-repeat-cta';
  var redirectDelay = 8;

  var state = { pending: false, busyButtons: [], busyTimer: 0, fallbackTimer: 0 };

  function getUpsellSection() { return document.querySelector('#main-product .f8pr-upsell'); }
  // Real add-ons present? Some kits carry only HDD + Cat6 accessories, which are
  // surfaced as the top selectors and their upsell rows hidden (cg-opt-source-
  // hidden → display:none), leaving the "Add Ons & Accessories" list empty. Test
  // each item's OWN display (not height) so a hidden .f8pr-upsell parent can't
  // make real add-ons read as absent (avoids a hide/show feedback loop).
  function cgHasVisibleAddOns() {
    var upsell = getUpsellSection();
    if (!upsell) return false;
    var items = upsell.querySelectorAll('.upsell-items > li');
    for (var i = 0; i < items.length; i++) {
      if (getComputedStyle(items[i]).display !== 'none') return true;
    }
    return false;
  }
  // Hide the whole "Add Ons & Accessories" block (heading + empty list) when
  // there are no real add-ons to show.
  function syncEmptyUpsell() {
    var upsell = getUpsellSection();
    if (upsell) upsell.classList.toggle('cg-upsell-empty', !cgHasVisibleAddOns());
  }
  function getPriceSource() { return document.querySelector('#main-product .f8pr-price.s1pr.price'); }
  function getQtyInput() { return document.querySelector('#main-product input[name="quantity"]'); }
  function getRealAddButton() {
    return (
      document.querySelector('#main-product .f8pr-button.wide > .overlay-buy_button') ||
      document.querySelector('#main-product button[name="add"]') ||
      document.querySelector('#main-product [type="submit"][name="add"]') ||
      document.querySelector('#main-product form[action*="/cart/add"] [type="submit"]') ||
      document.querySelector('#main-product .link-btn.submit a') ||
      document.querySelector('#main-product .link-btn.submit button')
    );
  }
  function isDisabledButton(btn) {
    if (!btn) return true;
    return !!(btn.disabled || btn.getAttribute('aria-disabled') === 'true' ||
      btn.classList.contains('disabled') ||
      /sold out|unavailable/i.test((btn.textContent || '').trim()));
  }
  function triggerRealAddButton(liveBtn) {
    if (!liveBtn) return;
    if (typeof liveBtn.click === 'function') { liveBtn.click(); return; }
    try {
      liveBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      return;
    } catch (e) {}
    if (liveBtn.form && typeof liveBtn.form.requestSubmit === 'function') liveBtn.form.requestSubmit();
  }
  function getSelectedAddOnsCount() {
    return document.querySelectorAll('#main-product .f8pr-upsell .check input[type="checkbox"]:checked').length;
  }
  function getQtyValue() {
    var input = getQtyInput();
    var qty = input ? parseInt(input.value, 10) : 1;
    return qty > 0 ? qty : 1;
  }
  function rememberOriginal(btn) {
    if (btn && !btn.dataset.cgOriginalHtml) btn.dataset.cgOriginalHtml = btn.innerHTML;
  }
  function setBusy(btn) {
    if (!btn || btn.dataset.cgBusy === 'true') return;
    rememberOriginal(btn);
    btn.dataset.cgBusy = 'true';
    btn.classList.add('cg-is-busy');
    btn.setAttribute('aria-disabled', 'true');
    btn.innerHTML = '<span class="cg-btn-load" aria-hidden="true"></span><span class="cg-btn-load-text" role="status">Adding…</span>';
    if (btn.closest('#' + sectionId)) btn.disabled = true;
  }
  function clearBusy(btn) {
    if (!btn) return;
    btn.dataset.cgBusy = 'false';
    btn.classList.remove('cg-is-busy');
    btn.removeAttribute('aria-disabled');
    if (btn.dataset.cgOriginalHtml) btn.innerHTML = btn.dataset.cgOriginalHtml;
    if (btn.closest('#' + sectionId)) btn.disabled = false;
  }
  function clearPendingTimers() { clearTimeout(state.busyTimer); clearTimeout(state.fallbackTimer); }
  function startPending(buttons, busyDelay) {
    if (state.pending) return;
    state.pending = true;
    state.busyButtons = buttons.filter(Boolean);
    clearPendingTimers();
    state.busyTimer = setTimeout(function () {
      state.busyButtons.forEach(function (btn) { setBusy(btn); });
    }, busyDelay || 0);
    state.fallbackTimer = setTimeout(function () { finishFailure(); }, 3200);
  }
  function finishSuccess() {
    if (!state.pending) return;
    clearPendingTimers();
    // Mobile: redirect to /cart (the drawer is cramped on small screens).
    // Desktop: stay on the page so the (now-styled) cart drawer slides in,
    // letting the customer keep shopping (e.g. add an NVR after a camera).
    if (window.matchMedia && window.matchMedia('(max-width: 1000px)').matches) {
      state.pending = false;
      state.busyButtons = [];
      window.location.assign('/cart');
      return;
    }
    state.busyButtons.forEach(function (btn) { clearBusy(btn); });
    state.pending = false;
    state.busyButtons = [];
  }
  function finishFailure() {
    if (!state.pending) return;
    clearPendingTimers();
    state.busyButtons.forEach(function (btn) { clearBusy(btn); });
    state.pending = false;
    state.busyButtons = [];
  }
  // On mobile, adding to cart redirects to /cart with the button left mid-sweep
  // ("Adding…"). Swiping / pressing Back restores this page from the browser's
  // back-forward cache with the button still busy, and init never re-runs — so
  // the loader stays stuck until a manual reload. pageshow fires on both a fresh
  // load and a bfcache restore, and never mid-navigation, so reset every busy
  // button (main Add to cart, Add bundle, and the mirrored sticky buy-bar) here.
  function cgResetProductBusy() {
    clearPendingTimers();
    state.pending = false;
    state.busyButtons = [];
    var btns = document.querySelectorAll('[data-cg-busy="true"], .cg-is-busy');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (b.dataset.cgBusy === 'true' || b.dataset.cgOriginalHtml) {
        clearBusy(b);                        // restores its saved original label
      } else {
        b.classList.remove('cg-is-busy');    // sticky button: no saved label
        b.disabled = false;
        if (b.classList.contains('cg-sticky-buybar__button')) b.textContent = 'Add to cart';
      }
    }
  }
  window.addEventListener('pageshow', cgResetProductBusy);
  function urlMatchesCartAdd(url) { return /\/cart\/add(?:\.js)?(?:\?|$)/.test(String(url || '')); }
  function wrapFetch() {
    if (window.__cgBundleFetchWrapped) return;
    window.__cgBundleFetchWrapped = true;
    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;
    window.fetch = function () {
      var url = arguments[0];
      var requestUrl = typeof url === 'string' ? url : (url && url.url) || '';
      return originalFetch.apply(this, arguments).then(function (response) {
        if (state.pending && urlMatchesCartAdd(requestUrl)) {
          if (response && response.ok) setTimeout(finishSuccess, redirectDelay);
          else finishFailure();
        }
        return response;
      }).catch(function (error) {
        if (state.pending && urlMatchesCartAdd(requestUrl)) finishFailure();
        throw error;
      });
    };
  }
  function wrapXHR() {
    if (window.__cgBundleXhrWrapped) return;
    window.__cgBundleXhrWrapped = true;
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__cgRequestUrl = url;
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      xhr.addEventListener('load', function () {
        if (state.pending && urlMatchesCartAdd(xhr.__cgRequestUrl)) {
          if (xhr.status >= 200 && xhr.status < 300) setTimeout(finishSuccess, redirectDelay);
          else finishFailure();
        }
      });
      xhr.addEventListener('error', function () {
        if (state.pending && urlMatchesCartAdd(xhr.__cgRequestUrl)) finishFailure();
      });
      return originalSend.apply(this, arguments);
    };
  }
  function removeSection() {
    var existing = document.getElementById(sectionId);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }
  function ensureSection() {
    if (window.innerWidth <= 1000) { removeSection(); return null; }
    var upsell = getUpsellSection();
    if (!upsell) { removeSection(); return null; }
    // No real add-ons (e.g. only HDD/Cat6 top selectors, or the sole add-on is
    // out of stock) → no point showing the "add everything" bundle CTA.
    if (!cgHasVisibleAddOns()) { removeSection(); return null; }
    var existing = document.getElementById(sectionId);
    if (existing) return existing;
    var section = document.createElement('section');
    section.id = sectionId;
    section.innerHTML =
      '<p class="cg-desktop-repeat-cta__eyebrow">Ready to Add Your Kit?</p>' +
      '<h3 class="cg-desktop-repeat-cta__title">Add everything in one go.</h3>' +
      '<div class="cg-desktop-repeat-cta__meta">' +
        '<span class="cg-desktop-repeat-cta__chip cg-desktop-repeat-cta__chip--qty">Qty 1</span>' +
        '<span class="cg-desktop-repeat-cta__chip cg-desktop-repeat-cta__chip--addons">No add-ons selected yet</span>' +
      '</div>' +
      '<div class="cg-desktop-repeat-cta__price"></div>' +
      '<div class="cg-desktop-repeat-cta__actions">' +
        '<button type="button" class="cg-desktop-repeat-cta__button">Add bundle to cart</button>' +
      '</div>';
    upsell.insertAdjacentElement('afterend', section);
    return section;
  }
  function bindMainButton(btn) {
    if (!btn || btn.dataset.cgBundleBound === 'true') return;
    btn.dataset.cgBundleBound = 'true';
    btn.addEventListener('click', function () {
      if (state.pending || isDisabledButton(btn)) return;
      startPending([btn], 80);
    }, true);
  }
  function bindRepeatButton() {
    if (document.documentElement.dataset.cgBundleRepeatBound === 'true') return;
    document.documentElement.dataset.cgBundleRepeatBound = 'true';
    document.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest
        ? event.target.closest('#' + sectionId + ' .cg-desktop-repeat-cta__button') : null;
      if (!btn) return;
      var liveBtn = getRealAddButton();
      if (state.pending || !liveBtn || isDisabledButton(liveBtn)) return;
      event.preventDefault();
      startPending([btn], 0);
      requestAnimationFrame(function () { triggerRealAddButton(liveBtn); });
    });
  }
  function syncSection() {
    var section = ensureSection();
    if (!section) return;
    var priceSource = getPriceSource();
    var priceTarget = section.querySelector('.cg-desktop-repeat-cta__price');
    var qtyChip = section.querySelector('.cg-desktop-repeat-cta__chip--qty');
    var addonsChip = section.querySelector('.cg-desktop-repeat-cta__chip--addons');
    var button = section.querySelector('.cg-desktop-repeat-cta__button');
    var realBtn = getRealAddButton();
    var qty = getQtyValue();
    var addOnsCount = getSelectedAddOnsCount();
    if (priceTarget && priceSource) priceTarget.innerHTML = priceSource.innerHTML;
    if (qtyChip) qtyChip.textContent = 'Qty ' + qty;
    if (addonsChip) {
      addonsChip.textContent = addOnsCount
        ? addOnsCount + ' add-on' + (addOnsCount === 1 ? '' : 's') + ' selected'
        : 'No add-ons selected yet';
    }
    if (button && button.dataset.cgBusy !== 'true') {
      button.disabled = isDisabledButton(realBtn);
      button.textContent = button.disabled ? 'Unavailable' : 'Add bundle to cart';
    }
  }
  function run() {
    wrapFetch();
    wrapXHR();
    syncEmptyUpsell();   // hide the "Add Ons & Accessories" block when it has no real items
    bindMainButton(getRealAddButton());
    bindRepeatButton();
    syncSection();
  }

  core.register(run);
  // The bundle takeover (cgBundleAdd) pre-empts the theme with a document-level
  // capture handler + stopImmediatePropagation, which also blocks this module's
  // bindMainButton click handler — so the loading sweep never starts. Expose a
  // hook so the takeover can trigger the same busy visual on the real Add
  // button (the sticky buy-bar button then mirrors it via syncButton).
  window.__cgSetAddBusy = function () {
    var b = getRealAddButton();
    if (b && !isDisabledButton(b)) { setBusy(b); return true; }
    return false;
  };
  window.addEventListener('resize', core.schedule);
  // Variant/qty/add-on changes update the mirrored price + chips.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest('#main-product')) { syncSection(); setTimeout(syncSection, 130); }
  }, true);
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches('#main-product input[name="quantity"]')) syncSection();
  }, true);
})();


/* =========================================================
   [CGJS-09] KEEP BRANDS ACTIVE ON BRAND COLLECTIONS
   [CGJS-10] KEEP TOP NAV ACTIVE FOR MEGAMENU CHILD PAGES
   (run once — no observers needed)
   ========================================================= */
(function () {
  var core = window.__cgCore;

  function normalizePath(path) { return (path || '/').replace(/\/+$/, '') || '/'; }

  function brandActive() {
    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/collections/') !== 0) return;
    var brandHandles = ['dahua', 'hikvision', 'ubiquiti', 'unv', 'hilook', 'axis'];
    var isBrand = brandHandles.some(function (h) {
      return path === '/collections/' + h || path.indexOf('/collections/' + h + '-') === 0;
    });
    if (!isBrand) return;
    var items = document.querySelectorAll('.shopify-section-header #nav > ul[data-type="main-nav"] > li');
    Array.prototype.forEach.call(items, function (item) {
      var link = item.querySelector(':scope > a:not(.toggle-back)');
      if (link && (link.textContent || '').trim().toLowerCase().indexOf('brands') !== -1) {
        item.classList.add('cg-nav-active');
      }
    });
  }

  function megamenuActive() {
    var currentPath = normalizePath(window.location.pathname);
    var items = document.querySelectorAll('.shopify-section-header #nav > ul[data-type="main-nav"] > li');
    Array.prototype.forEach.call(items, function (item) {
      if (item.classList.contains('cg-nav-active')) return; // brand pass may have set it
      var childLinks = item.querySelectorAll('ul a[href], div a[href]');
      if (!childLinks.length) return;
      var hasMatch = Array.prototype.some.call(childLinks, function (link) {
        var href = link.getAttribute('href');
        if (!href || href.indexOf('/') !== 0) return false;
        return normalizePath(href) === currentPath;
      });
      if (hasMatch) item.classList.add('cg-nav-active');
    });
  }

  core.ready(function () { brandActive(); megamenuActive(); });
})();


/* =========================================================
   [CGJS-11] MOBILE: MOVE SKU UNDER BRAND
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgSkuMoveFinal) return;
  window.__cgSkuMoveFinal = true;

  var core = window.__cgCore;
  var mq = window.matchMedia('(max-width: 1000px)');

  function getSkuBlock() {
    var el = document.querySelector('#main-product .f8pr-codes') ||
             document.querySelector('.f8pr-codes');
    if (el) return el;
    // Fallback: the <p> whose label is "SKU:"
    var ps = document.querySelectorAll('#main-product p, .f8pr p');
    for (var i = 0; i < ps.length; i++) {
      var s = ps[i].querySelector('span.strong');
      if (s && /^SKU:/i.test((s.textContent || '').trim())) return ps[i];
    }
    return null;
  }

  function getBrandBlock() {
    // Find the Brand line by its bold "Brand" label across p / li / codes blocks
    // (the theme markup changed, so don't assume ul.l4dr > li).
    var els = document.querySelectorAll('#main-product p, #main-product li, #main-product .f8pr-codes, .f8pr p, .f8pr li, .f8pr-codes');
    for (var i = 0; i < els.length; i++) {
      var st = els[i].querySelector('span.strong');
      if (st && /^brand/i.test((st.textContent || '').trim())) return els[i];
    }
    return null;
  }

  function ensureAnchor(sku) {
    if (document.getElementById('cg-sku-anchor') || !sku.parentNode) return;
    var a = document.createElement('span');
    a.id = 'cg-sku-anchor';
    a.style.display = 'none';
    sku.parentNode.insertBefore(a, sku);
  }

  function moveForMobile() {
    var sku = getSkuBlock();
    if (!sku) return;
    ensureAnchor(sku);

    var brand = getBrandBlock();
    if (!brand || !brand.parentNode) return;

    // If Brand is a <li> in a row list, drop the SKU onto its own line below the
    // whole list; otherwise put it right after the Brand block.
    var anchor = (brand.tagName === 'LI' && brand.parentNode) ? brand.parentNode : brand;
    if (!anchor.parentNode) return;

    var host = document.getElementById('cg-sku-li');
    if (!host) {
      host = document.createElement('div');
      host.id = 'cg-sku-li';
    }
    if (sku.parentNode !== host) host.appendChild(sku);
    if (anchor.nextElementSibling !== host) {
      anchor.parentNode.insertBefore(host, anchor.nextElementSibling);
    }
  }

  function restoreDesktop() {
    var host = document.getElementById('cg-sku-li');
    var anchor = document.getElementById('cg-sku-anchor');
    var sku = getSkuBlock();

    if (sku && anchor && anchor.parentNode && sku.parentNode !== anchor.parentNode) {
      anchor.parentNode.insertBefore(sku, anchor.nextSibling);
    }
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }

  function run() {
    if (mq.matches) moveForMobile();
    else restoreDesktop();
  }

  core.register(run);
  if (mq.addEventListener) mq.addEventListener('change', run);
  else if (mq.addListener) mq.addListener(run);
})();


/* =========================================================
   [CGJS-12] HIDE THEME SHARE WIDGET (product page)
   Keeps Brand + SKU clean; native browser share covers sharing.
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgShareSheetFinal) return;
  window.__cgShareSheetFinal = true;

  var core = window.__cgCore;

  function run() {
    // Theme's inline social/share widget stays hidden on all viewports —
    // every modern browser has its own native share/copy.
    var li = document.querySelector('li.has-social');
    if (li && li.dataset.cgHidden !== '1') {
      li.dataset.cgHidden = '1';
      li.style.setProperty('display', 'none', 'important');
    }
    // Remove any leftover share element from earlier iterations
    var stray = document.getElementById('cg-share-end') || document.getElementById('cg-share-host');
    if (stray && stray.parentNode) stray.parentNode.removeChild(stray);
  }

  core.register(run);
})();


/* =========================================================
   [CGJS-13] SKU SUBSTRING SEARCH BOOSTER
   Native Shopify search only matches whole tokens / prefixes,
   so "3667" never finds SKU "DH-IPC-HDW3667...". This module
   loads a slim CDN-hosted SKU index (handle/title/img/price/skus),
   then:
     (a) injects substring SKU matches into the theme's predictive
         dropdown by patching the /search/suggest response, and
     (b) renders a "matching SKUs" strip on the /search results page
         (which is server-rendered and returns nothing for a substring).
   Fail-safe: any unexpected response shape is passed through untouched,
   so native search can never break.
   ========================================================= */
(function () {
  if (window.CG_AI_SEARCH && window.CG_AI_SEARCH.enabled && window.CG_AI_SEARCH.owner === 'exclusive') return;
  if (window.__cgSkuSearch) return;
  window.__cgSkuSearch = true;

  var INDEX_URL = 'https://cdn.shopify.com/s/files/1/0637/1554/1221/files/cg-sku-index.json';
  var MIN_LEN = 3;            // minimum normalized query length to act on
  var MAX_DROPDOWN = 10;      // products injected into predictive dropdown
  var MAX_PAGE = 24;          // products shown on /search results strip

  var DEBUG = /[?&]cgskudebug=1/.test(location.search);
  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[cg-sku]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function norm(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  // SKU-ish = a digit OR a hyphen present (real product names rarely have
  // either), separating SKU lookups ("2387", "gr-ds", "DH-IPC") from normal
  // word searches so we only splice SKU matches when it actually looks like one.
  function isSkuish(q) { var n = norm(q); return n.length >= MIN_LEN && (/[0-9]/.test(n) || q.indexOf('-') !== -1); }

  function getParam(url, name) {
    try {
      var m = String(url).match(new RegExp('[?&]' + name + '=([^&]*)'));
      return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
    } catch (e) { return ''; }
  }
  function isSuggestUrl(url) { return /\/search\/suggest/.test(String(url || '')); }
  function cacheBust() {
    // NOTE: must be the `?v=` param — Shopify's CDN treats `?v` as a cache key
    // (any value fetches the latest origin file) but IGNORES other query params,
    // so a custom `?cgv=` would be pinned to the stale year-cached copy.
    var d = new Date();
    return '' + d.getFullYear() + (d.getMonth() + 1) + d.getDate() + d.getHours(); // hour granularity — index updates propagate within ~1h
  }

  /* ---- index load (in-memory + sessionStorage, fetched once) ---------- */
  var INDEX = null, loadPromise = null;
  function loadIndex() {
    if (INDEX) return Promise.resolve(INDEX);
    if (loadPromise) return loadPromise;
    try {
      var cached = sessionStorage.getItem('cgSkuIndex');
      if (cached) { INDEX = JSON.parse(cached); log('index from session', INDEX.items.length); return Promise.resolve(INDEX); }
    } catch (e) {}
    loadPromise = fetch(INDEX_URL + '?v=' + cacheBust(), { credentials: 'omit' })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (j) {
        INDEX = (j && j.items) ? j : { items: [] };
        try { sessionStorage.setItem('cgSkuIndex', JSON.stringify(INDEX)); } catch (e) {}
        log('index loaded', INDEX.items.length);
        return INDEX;
      })
      .catch(function (e) { log('index load failed', e); INDEX = { items: [] }; return INDEX; });
    return loadPromise;
  }

  /* ---- substring match against the index ------------------------------ */
  function matchSku(query, limit) {
    var nq = norm(query);
    if (!isSkuish(query) || !INDEX || !INDEX.items) return [];
    var items = INDEX.items, scored = [], i, k;
    for (i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it.hay || it.hay.indexOf(nq) === -1) continue;
      var best = 3;                                  // 0 exact, 1 prefix, 2 contains
      for (k = 0; k < it.sk.length; k++) {
        var ns = norm(it.sk[k]);
        if (ns === nq) { best = 0; break; }
        else if (ns.indexOf(nq) === 0) { if (best > 1) best = 1; }
        else if (ns.indexOf(nq) !== -1) { if (best > 2) best = 2; }
      }
      scored.push([best, it.av ? 0 : 1, it]);        // in-stock first within a tier
    }
    scored.sort(function (a, b) { return (a[0] - b[0]) || (a[1] - b[1]); });
    var out = [];
    for (i = 0; i < scored.length && out.length < (limit || MAX_DROPDOWN); i++) out.push(scored[i][2]);
    return out;
  }

  /* ---- shape an index item like a /search/suggest product ------------- */
  function toSuggestProduct(it) {
    var url = '/products/' + it.h;
    var img = it.i || '';
    return {
      id: it.h, title: it.t, handle: it.h, url: url, image: img,
      featured_image: img ? { url: img, alt: it.t, aspect_ratio: 1, width: 200, height: 200 } : null,
      price: it.pn, price_min: it.pn, price_max: it.px,
      compare_at_price: null, compare_at_price_min: null, compare_at_price_max: null,
      available: !!it.av, type: it.ty || '', vendor: it.vn || '', tags: [],
      sku: (it.sk && it.sk[0]) || '',
      variants: (it.sk || []).map(function (s) { return { sku: s, available: !!it.av, price: it.pn, title: s }; }),
      __cgSku: true
    };
  }

  function injectIntoSuggest(json, query) {
    try {
      var root = json && json.resources && json.resources.results;
      if (!root || Object.prototype.toString.call(root.products) !== '[object Array]') return json;
      if (!isSkuish(query)) return json;
      var matches = matchSku(query, MAX_DROPDOWN);
      if (!matches.length) return json;
      var existing = {};
      root.products.forEach(function (p) { if (p && p.handle) existing[p.handle] = 1; });
      var inject = [];
      matches.forEach(function (it) { if (!existing[it.h]) inject.push(toSuggestProduct(it)); });
      if (!inject.length) return json;
      root.products = inject.concat(root.products);    // SKU matches first
      log('injected', inject.length, 'for', query);
      return json;
    } catch (e) { log('inject error', e); return json; }
  }

  /* ---- inject into the rendered HTML dropdown (section_id=livesearch) -- */
  // The Xtra theme's predictive dropdown fetches a rendered Liquid section
  // (HTML), not JSON — so we splice matching <li> cards into its product
  // <ul class="l4ca compact">, matching the theme's own card markup.
  function esc(s) {
    return (s || '').replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  // handle -> index item, for annotating native cards by their product handle
  var HANDLE_MAP = null;
  function handleMap() {
    if (HANDLE_MAP) return HANDLE_MAP;
    var map = {};
    if (INDEX && INDEX.items) {
      for (var i = 0; i < INDEX.items.length; i++) map[INDEX.items[i].h] = INDEX.items[i];
      HANDLE_MAP = map;                                      // only cache once the index is populated
    }
    return map;
  }
  // Pick the image for the variant whose SKU best matches the query, so e.g.
  // searching the "…-BLK" SKU shows the black photo instead of the default.
  function pickImage(it, nq) {
    if (!it.im || !nq) return it.i;
    var best = -1, bestRank = 99;
    for (var k = 0; k < it.sk.length; k++) {
      var ns = norm(it.sk[k]);
      var rank = ns === nq ? 0 : (ns.indexOf(nq) === 0 ? 1 : (ns.indexOf(nq) !== -1 ? 2 : 99));
      if (rank < bestRank) { bestRank = rank; best = k; }
    }
    return (best >= 0 && it.im[best]) ? it.im[best] : it.i;
  }
  function buildLi(it, nq) {
    var url = '/products/' + it.h;
    var src = pickImage(it, nq);
    var img = src
      ? '<figure><picture><img src="' + src + '" alt="' + esc(it.t) + '" loading="lazy"></picture></figure>'
      : '';
    var price = it.pn === it.px ? money(it.pn) : money(it.pn) + ' – ' + money(it.px);
    // SKU is added post-render (see annotateSkus) — the theme's card
    // decorator strips unexpected children from the response HTML.
    return '<li data-cg-sku="1">' + img +
      '<section><div class="cols align-middle"><div><h2><a href="' + url + '">' + esc(it.t) + '</a></h2></div>' +
      '<p class="price s1pr">' + price + '</p></div></section></li>';
  }
  // Splice substring matches (SKU-ish queries only) AND annotate every native
  // product card with its SKU (any query, when the index is loaded).
  function processSection(html, query) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      // 1) substring injection — SKU-ish queries only
      if (isSkuish(query)) {
        var matches = matchSku(query, MAX_DROPDOWN);
        if (matches.length) {
          var existing = {};
          doc.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
            var m = (a.getAttribute('href') || '').match(/\/products\/([^?#]+)/);
            if (m) existing[m[1]] = 1;
          });
          var fresh = matches.filter(function (it) { return !existing[it.h]; });
          if (fresh.length) {
            var nq = norm(query);
            var lis = fresh.map(function (it) { return buildLi(it, nq); }).join('');
            var ul = doc.querySelector('ul.l4ca');
            if (ul) {
              ul.insertAdjacentHTML('afterbegin', lis);       // native results present
            } else {
              // "no results" state — build the product list ourselves
              var ulHtml = '<ul class="l4ca compact">' + lis + '</ul>';
              var nodes = [].slice.call(doc.querySelectorAll('a, p, div, button'));
              var viewAll = nodes.filter(function (e) { return /view all results/i.test(e.textContent || '') && e.children.length < 2; })[0];
              if (viewAll && viewAll.parentNode) viewAll.insertAdjacentHTML('beforebegin', ulHtml);
              else (doc.querySelector('.shopify-section > div') || doc.querySelector('.shopify-section') || doc.body).insertAdjacentHTML('beforeend', ulHtml);
            }
            log('section injected', fresh.length, 'for', query);
          }
        }
      }
      return doc.body.innerHTML;
    } catch (e) { log('section process error', e); return html; }
  }

  /* ---- enhance the predictive dropdown (post-render) ------------------- */
  // The theme strips extra nodes from the section HTML and re-renders the
  // dropdown on every keystroke, so SKUs + grouped results are applied to the
  // live #livesearch DOM after render and re-applied via the shared observer.
  function injectExtrasStyle() {
    if (document.getElementById('cg-ls-style')) return;
    var st = document.createElement('style');
    st.id = 'cg-ls-style';
    st.textContent =
      '#livesearch .cg-has-sku{flex-wrap:wrap}' +
      '#livesearch .cg-has-sku .price{flex-basis:100%;width:100%;order:8;margin:1px 0 0;padding-left:0;text-align:left;justify-content:flex-start}' +
      '#livesearch .cg-ls-sku{flex-basis:100%;width:100%;display:block;order:9;font-size:13px;font-weight:500;color:#333;letter-spacing:.01em;margin-top:1px;line-height:1.2;-webkit-user-select:text;user-select:text;cursor:text}' +
      '#livesearch .cg-ls-extra{margin-bottom:16px}' +   // space before the native Pages & Blog Posts section
      '#livesearch .cg-ls-group{margin:4px 0 0}' +
      '#livesearch .cg-ls-h{font-size:14px;font-weight:800;text-transform:none;letter-spacing:normal;color:#0b1613;margin:10px 0 2px}' +
      '#livesearch .cg-ls-group ul{list-style:none;margin:0;padding:0}' +
      '#livesearch .cg-ls-group li{margin:0;padding:0}' +
      // line-clamp (not nowrap) keeps text wrappable so the dropdown can't widen
      '#livesearch .cg-ls-group li a{display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;padding:2px 0;line-height:1.35;font-size:14px;font-weight:500;color:#0b1613;text-decoration:none}' +
      '#livesearch .cg-ls-group li a:hover,#livesearch .cg-ls-group li a:focus{color:#0b1613;text-decoration:underline}' +
      '#livesearch .cg-ls-group li a:visited{color:#0b1613}' +
      // truncate long native Pages & Blog Posts titles to a single line too (same method)
      '#livesearch a[href*="/pages/"],#livesearch a[href*="/blogs/"]{display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}' +
      // Flatten the native "View all" pill (a.strong.inline = blue gradient bg +
      // 999px radius, white text) into a plain right-aligned link, hover underline.
      // repeated classes boost specificity above the theme's gradient rule
      '#livesearch a.strong.strong.strong.inline.inline.inline{background:none!important;background-image:none!important;background-color:transparent!important;color:#0b1613!important;padding:0!important;margin:0!important;border:0!important;border-radius:0!important;box-shadow:none!important;min-height:0!important;min-width:0!important;font-size:13px!important;font-weight:600;text-decoration:none}' +
      '#livesearch a.strong.strong.inline.inline span{color:#0b1613!important;background:none!important;text-decoration:none}' +
      '#livesearch a.strong.strong.inline.inline:hover,#livesearch a.strong.strong.inline.inline:hover span{text-decoration:underline}' +
      '#livesearch .cg-viewall{text-align:right;margin:6px 0 2px}';
    (document.head || document.documentElement).appendChild(st);
  }

  // Show SKU under each product, as real selectable text (so it can be copied).
  function annotateSkus(box, nq) {
    if (!INDEX) return;
    var map = handleMap();
    var anchors = box.querySelectorAll('h2 a[href*="/products/"]');
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      var liEl = a.closest ? a.closest('li') : null;
      // Insert into the card's `.cols` row (sibling of the price element, which
      // the theme's decorator preserves) as REAL selectable text so the SKU can
      // be copy-pasted — a CSS ::after pseudo-element can't be selected.
      var cols = a.closest ? a.closest('.cols') : null;
      if (!cols) continue;
      var m = (a.getAttribute('href') || '').match(/\/products\/([^?#]+)/);
      if (!m) continue;
      var it = map[m[1]];
      if (!it) continue;
      // swap to the searched variant's image (e.g. "...-BLK" -> black photo)
      if (liEl && it.im && nq && nq.length >= MIN_LEN) {
        var vimg = pickImage(it, nq);
        var imgEl = liEl.querySelector('img');
        if (vimg && vimg !== it.i && imgEl && imgEl.getAttribute('src') !== vimg) {
          imgEl.setAttribute('src', vimg); imgEl.removeAttribute('srcset');
          if (imgEl.hasAttribute('data-src')) imgEl.setAttribute('data-src', vimg);
          if (imgEl.hasAttribute('data-srcset')) imgEl.removeAttribute('data-srcset');
        }
      }
      if (!it.sk || !it.sk[0] || cols.querySelector('.cg-ls-sku')) continue;
      cols.insertAdjacentHTML('beforeend', '<span class="cg-ls-sku">' + esc(it.sk[0]) + '</span>');
      if (cols.classList) cols.classList.add('cg-has-sku');   // make the flex row wrap so SKU drops below
      // strip the theme's leading non-breaking space so the price aligns flush-left
      var priceEl = cols.querySelector('.price');
      if (priceEl && priceEl.firstChild && priceEl.firstChild.nodeType === 3) {
        priceEl.firstChild.textContent = priceEl.firstChild.textContent.replace(/^[\s ]+/, '');
      }
    }
  }

  // Grouped non-product results (Collections / Pages / Blog / Suggestions)
  var extrasCache = {}, extrasInflight = {};
  function getExtras(q, cb) {
    if (extrasCache[q]) { cb(extrasCache[q]); return; }
    if (extrasInflight[q]) return;
    extrasInflight[q] = true;
    // Only `collection` — the theme already renders native Suggestions and a
    // "Pages & Blog Posts" section, so we'd only be duplicating those.
    fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=collection&resources[limit]=5', { credentials: 'omit' })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (j) {
        var res = (j && j.resources && j.resources.results) || {};
        extrasCache[q] = { collections: res.collections || [] };
        extrasInflight[q] = false;
        cb(extrasCache[q]);
      })
      .catch(function () { extrasInflight[q] = false; });
  }
  function currentLsQuery(box) {
    var p = box.querySelector('p');
    var m = (p && p.textContent || '').match(/for\s+["“'”](.+?)["“'”]/);
    return m ? m[1].trim() : '';
  }
  function lsGroup(title, items, mapFn) {
    if (!items || !items.length) return '';
    var lis = items.slice(0, 5).map(function (it) {
      var o = mapFn(it);
      if (!o.url) return '';
      return '<li><a href="' + o.url + '">' + (o.raw ? o.label : esc(o.label)) + '</a></li>';
    }).join('');
    return lis ? '<div class="cg-ls-group"><p class="cg-ls-h">' + title + '</p><ul>' + lis + '</ul></div>' : '';
  }
  function insertExtras(box, q, d) {
    var ex = box.querySelector('.cg-ls-extra');
    if (ex && ex.getAttribute('data-cg-q') === q) return;       // already current
    if (ex && ex.parentNode) ex.parentNode.removeChild(ex);
    var html = lsGroup('Collections', d.collections, function (c) { return { url: c.url, label: c.title }; });
    if (!html) return;
    var wrap = '<div class="cg-ls-extra" data-cg-q="' + esc(q) + '">' + html + '</div>';
    // Always place groups BELOW the product list.
    var ul = box.querySelector('ul.l4ca');
    if (ul) { ul.insertAdjacentHTML('afterend', wrap); return; }
    var nodes = [].slice.call(box.querySelectorAll('p, div'));
    var viewAll = nodes.filter(function (e) { return /view all results/i.test(e.textContent || '') && e.children.length < 3; })[0];
    if (viewAll && viewAll.parentNode) viewAll.insertAdjacentHTML('beforebegin', wrap);
    else box.insertAdjacentHTML('beforeend', wrap);
  }
  // Flatten the native "View all results" pill into a compact right-aligned
  // link and move it directly under the product list (its natural spot).
  function placeViewAll(box) {
    var anchors = box.getElementsByTagName('a'), va = null;
    for (var i = 0; i < anchors.length; i++) {
      if (/view all/i.test(anchors[i].textContent || '')) { va = anchors[i]; break; }   // loose: survives our relabel
    }
    if (!va) return;
    // relabel ("view all" only links to product results) + arrow in the text
    var span = va.querySelector('span') || va;
    if (span.textContent.indexOf('View all Products') === -1) span.textContent = 'View all Products →';
    // Flatten the blue pill via INLINE styles — the theme's gradient rule is
    // more specific than any stylesheet override, but inline !important wins.
    // Re-applied on each render via the burst, so it sticks once settled.
    var flat = { background: 'none', 'background-image': 'none', 'background-color': 'transparent',
      border: 'none', 'border-radius': '0', padding: '0', margin: '0', 'box-shadow': 'none',
      'min-height': '0', 'min-width': '0', color: '#0e5fd8', 'font-size': '13px', 'font-weight': '600' };
    for (var k in flat) va.style.setProperty(k, flat[k], 'important');
    if (span !== va) span.style.setProperty('color', '#0e5fd8', 'important');
    var wrap = (va.closest && va.closest('p')) || va.parentElement || va;
    if (wrap.classList) wrap.classList.add('cg-viewall');
    var ul = box.querySelector('ul.l4ca');
    if (ul && ul.nextElementSibling !== wrap) ul.insertAdjacentElement('afterend', wrap);
  }

  // Re-apply SKUs + groups from cache (sync) — used by the settle burst.
  function reapplyLivesearch(box) {
    var q = currentLsQuery(box);
    annotateSkus(box, norm(q));
    placeViewAll(box);
    if (q && extrasCache[q]) insertExtras(box, q, extrasCache[q]);
  }
  // The theme re-renders the dropdown several times (and runs a late per-card
  // decorate pass) as results stream in, wiping our additions. Rather than
  // guess the final render time, re-apply a few times after the query changes.
  var lsBurst = [], lsLastQ = '';
  function scheduleReapplyBurst() {
    for (var i = 0; i < lsBurst.length; i++) clearTimeout(lsBurst[i]);
    lsBurst = [150, 400, 800, 1500].map(function (ms) {
      return setTimeout(function () {
        var b = document.getElementById('livesearch');
        if (b) reapplyLivesearch(b);
      }, ms);
    });
  }
  function enhanceLivesearch() {
    var box = document.getElementById('livesearch');
    if (!box) return;
    var q = currentLsQuery(box);
    annotateSkus(box, norm(q));
    placeViewAll(box);
    if (!q || q.replace(/\s/g, '').length < 2) return;
    var ex = box.querySelector('.cg-ls-extra');
    if (!(ex && ex.getAttribute('data-cg-q') === q)) {
      getExtras(q, function (d) {
        var b = document.getElementById('livesearch');
        if (b && currentLsQuery(b) === q) insertExtras(b, q, d);
      });
    }
    if (q !== lsLastQ) { lsLastQ = q; scheduleReapplyBurst(); }
  }

  /* ---- patch fetch (primary transport) -------------------------------- */
  function rewriteHtmlResponse(resp, q) {
    return resp.clone().text().then(function (htmlText) {
      return new Response(processSection(htmlText, q),
        { status: resp.status, statusText: resp.statusText, headers: resp.headers });
    }).catch(function () { return resp; });
  }
  function wrapFetch() {
    if (window.__cgSkuFetch || typeof window.fetch !== 'function') return;
    window.__cgSkuFetch = true;
    var orig = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (!isSuggestUrl(url)) return p;
      var q = getParam(url, 'q');
      return p.then(function (resp) {
        if (!resp || !resp.ok) return resp;
        var ct = (resp.headers && resp.headers.get && resp.headers.get('content-type')) || '';
        var isJson = ct.indexOf('json') !== -1;
        var isHtml = ct.indexOf('html') !== -1;
        if (!isJson && !isHtml) return resp;
        var skuish = isSkuish(q);
        if (isHtml) {
          // SKU-ish: wait for the index (inject + annotate). Otherwise annotate
          // only — and never block a normal search waiting on the index.
          if (skuish) return loadIndex().then(function () { return rewriteHtmlResponse(resp, q); }).catch(function () { return resp; });
          if (!INDEX) { loadIndex(); return resp; }
          return rewriteHtmlResponse(resp, q);
        }
        // JSON fallback path — SKU-ish only
        if (!skuish) return resp;
        return loadIndex().then(function () {
          return resp.clone().json().then(function (json) {
            return new Response(JSON.stringify(injectIntoSuggest(json, q)),
              { status: resp.status, statusText: resp.statusText, headers: resp.headers });
          });
        }).catch(function () { return resp; });
      });
    };
  }

  /* ---- patch XHR (covers jQuery $.ajax) -------------------------------- */
  function wrapXHR() {
    if (window.__cgSkuXhr) return;
    window.__cgSkuXhr = true;
    var proto = XMLHttpRequest.prototype;
    var rawText = Object.getOwnPropertyDescriptor(proto, 'responseText');
    if (!rawText || !rawText.get) return;
    var origOpen = proto.open, origSend = proto.send;
    proto.open = function (method, url) { this.__cgUrl = url; this.__cgSuggest = isSuggestUrl(url); return origOpen.apply(this, arguments); };
    proto.send = function () {
      var xhr = this;
      if (xhr.__cgSuggest) {
        var q = getParam(xhr.__cgUrl, 'q');
        if (isSkuish(q)) {
          if (!INDEX) loadIndex();
          var memo = null, computed = false;
          var compute = function () {
            if (computed) return memo;
            computed = true;
            var raw = rawText.get.call(xhr);
            try {
              var ct = (xhr.getResponseHeader && xhr.getResponseHeader('content-type')) || '';
              if (!INDEX || ct.indexOf('json') === -1) { memo = raw; return memo; }
              memo = JSON.stringify(injectIntoSuggest(JSON.parse(raw), q));
            } catch (e) { memo = raw; }
            return memo;
          };
          try {
            Object.defineProperty(xhr, 'responseText', {
              configurable: true,
              get: function () { return xhr.readyState === 4 ? compute() : rawText.get.call(xhr); }
            });
            Object.defineProperty(xhr, 'response', {
              configurable: true,
              get: function () { return xhr.readyState === 4 ? compute() : rawText.get.call(xhr); }
            });
          } catch (e) { log('xhr defineProperty failed', e); }
        }
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ---- /search results page: render a "matching SKUs" strip ----------- */
  function money(n) {
    var v = Number(n || 0);
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }
  // (Removed: the /search results-page "SKU matches" strip — native search
  // already shows full-SKU matches, and the predictive dropdown covers the
  // as-you-type case, so the strip was unwanted in all scenarios.)

  /* ---- prewarm the index as soon as the user touches search ----------- */
  function prewarm() {
    var fired = false;
    function go() { if (fired) return; fired = true; loadIndex(); }
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input[type="search"], input[name="q"], [id*="earch"] input')) go();
    }, true);
    document.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('input[type="search"], input[name="q"]')) go();
    }, true);
  }

  function boot() {
    injectExtrasStyle();
    wrapFetch();
    wrapXHR();
    prewarm();
    if (window.__cgCore && window.__cgCore.register) window.__cgCore.register(enhanceLivesearch);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();


/* =========================================================
   [CGJS-USP] USP / benefit-list icons (ul.l4us)
   The theme rebuilds the USP list ~1s after load (adds
   .l4us-initialized), which strips any sibling-selector CSS
   icon. So we match each line BY ITS TEXT and set a per-line
   CSS custom property (--cg-usp-icon); cg-product.css reads it:
     ul.l4us li::before{ background-image:var(--cg-usp-icon, <check>) }
   Text-matching is immune to DOM rebuilds / reordering, and we
   re-run on every DOM change so the icons survive the rebuild.
   ========================================================= */
(function () {
  if (window.__cgUspIcons) return;
  window.__cgUspIcons = true;

  var PRE = "%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' " +
            "fill='none' stroke='%232563eb' stroke-width='2' " +
            "stroke-linecap='round' stroke-linejoin='round'%3E";
  function ico(body) {
    return 'url("data:image/svg+xml,' + PRE + body + "%3C/svg%3E\")";
  }

  var SVG = {
    clock:   ico("%3Ccircle cx='12' cy='12' r='10'/%3E%3Cpolyline points='12 6 12 12 16 14'/%3E"),
    shield:  ico("%3Cpath d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'/%3E%3Cpath d='m9 12 2 2 4-4'/%3E"),
    refund:  ico("%3Cpolyline points='1 4 1 10 7 10'/%3E%3Cpath d='M3.51 15a9 9 0 1 0 2.13-9.36L1 10'/%3E"),
    truck:   ico("%3Crect x='1' y='3' width='15' height='13'/%3E%3Cpolygon points='16 8 20 8 23 11 23 16 16 16 16 8'/%3E%3Ccircle cx='5.5' cy='18.5' r='2.5'/%3E%3Ccircle cx='18.5' cy='18.5' r='2.5'/%3E"),
    headset: ico("%3Cpath d='M3 18v-6a9 9 0 0 1 18 0v6'/%3E%3Cpath d='M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z'/%3E%3Cpath d='M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z'/%3E"),
    tag:     ico("%3Cpath d='M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z'/%3E%3Cline x1='7' y1='7' x2='7.01' y2='7'/%3E"),
    box:     ico("%3Cpath d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'/%3E%3Cpolyline points='3.27 6.96 12 12.01 20.73 6.96'/%3E%3Cline x1='12' y1='22.08' x2='12' y2='12'/%3E")
  };

  // First match wins. 'support' / 'back-to-base' are tested before
  // 'warranty' so the Tech-Support line doesn't grab the shield.
  var RULES = [
    [/dispatch|order within/i,                'clock'],
    [/back-to-base|tech support|support/i,    'headset'],
    [/warranty/i,                             'shield'],
    [/money back|guarantee|refund/i,          'refund'],
    [/free shipping|shipping/i,               'box'],
    [/australian|express delivery|stock/i,    'truck'],
    [/bulk|discount|quote/i,                  'tag']
  ];

  function keyFor(text) {
    for (var i = 0; i < RULES.length; i++) {
      if (RULES[i][0].test(text)) return RULES[i][1];
    }
    return '';
  }

  function apply() {
    var lis = document.querySelectorAll('ul.l4us li');
    for (var i = 0; i < lis.length; i++) {
      var li = lis[i];
      var key = keyFor(li.textContent || '');
      if (li.getAttribute('data-cg-usp') === key) continue; // idempotent
      li.setAttribute('data-cg-usp', key);
      if (key && SVG[key]) li.style.setProperty('--cg-usp-icon', SVG[key]);
      else li.style.removeProperty('--cg-usp-icon');        // fall back to check
    }
  }

  var core = window.__cgCore;
  if (core && core.register) {
    core.register(apply);   // runs debounced on every DOM change + lifecycle
    core.ready(apply);
  } else {
    // Portable fallback if dropped into a cg-custom.js without the shared core
    var raf = false;
    function sched() {
      if (raf) return;
      raf = true;
      requestAnimationFrame(function () { raf = false; apply(); });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', apply);
    } else {
      apply();
    }
    window.addEventListener('load', sched);
    window.addEventListener('pageshow', sched);
    if (window.MutationObserver && document.body) {
      new MutationObserver(sched).observe(document.body, { childList: true, subtree: true });
    }
    setTimeout(apply, 500);
    setTimeout(apply, 1800);
  }
})();


/* =========================================================
   [CGJS-14] CART DRAWER BUTTONS — pill shape + blue checkout (global)
   Runs site-wide (the drawer is a global element, not template-cart).
   Inline styles beat the theme's red, same as the cart-page approach.
   ========================================================= */
(function () {
  if (window.__cgDrawerBtnsFinal) return;
  window.__cgDrawerBtnsFinal = true;

  var core = window.__cgCore;
  if (!core) return;

  function style() {
    // Checkout (drawer + cart): force blue + pill. Broad selector to catch
    // <button>, <input>, or <a href=".../checkout">.
    var checkout = document.querySelectorAll('[name="checkout"], a[href$="/checkout"]');
    for (var i = 0; i < checkout.length; i++) {
      var s = checkout[i].style;
      s.setProperty('--secondary_bg', '#0e5fd8', 'important');
      s.setProperty('--secondary-bg', '#0e5fd8', 'important');
      s.setProperty('background', '#0e5fd8', 'important');
      s.setProperty('background-color', '#0e5fd8', 'important');
      s.setProperty('background-image', 'none', 'important');
      s.setProperty('color', '#fff', 'important');
      s.setProperty('border-radius', '999px', 'important');
      s.setProperty('overflow', 'hidden', 'important');
    }

    // "Activate" — the button beside the discount input (found via placeholder).
    var di = document.querySelector('input[placeholder="Add your code here"]');
    if (di) {
      var scope = di.closest('form, fieldset, div') || di.parentElement;
      var act = scope && scope.querySelector('button, [type="submit"], [type="button"]');
      if (act) act.style.setProperty('border-radius', '999px', 'important');
    }

    // "View cart" link only → pill. Do NOT pill all drawer buttons — that was
    // barrelling the express payment buttons (and the upsell cart-icon button).
    // Checkout/Activate are pilled by their own CSS ([CG-26]); the header cart
    // icon (a[href="/cart"] in the nav) is excluded by the form scope.
    var pills = document.querySelectorAll('form[action="/cart"] a[href$="/cart"]');
    for (var j = 0; j < pills.length; j++) {
      pills[j].style.setProperty('border-radius', '999px', 'important');
    }

    // Tag "Continue shopping" links (cart page + drawer) for the hover underline.
    // Each link is marked once (data-cg-cont), so later passes only check new ones.
    var links = document.querySelectorAll('a:not([data-cg-cont])');
    for (var k = 0; k < links.length; k++) {
      var t = (links[k].textContent || '').replace(/\s+/g, ' ').trim()
        .replace(/^[‹<‹←\s]+/, '').toLowerCase();
      links[k].setAttribute('data-cg-cont', '1');
      if (t === 'continue shopping') links[k].classList.add('cg-continue-shopping');
    }
  }

  core.register(style);

  // Mobile: tapping the header cart icon should go to the cart PAGE, not open
  // the drawer. Desktop keeps the drawer. Capture-phase +
  // stopImmediatePropagation kills the theme's drawer-open handler; the
  // link's href="/cart" then navigates normally.
  document.addEventListener('click', function (e) {
    if (!window.matchMedia || !window.matchMedia('(max-width: 1000px)').matches) return;
    var t = e.target && e.target.closest
      ? e.target.closest('a[href$="/cart"], a[href="/cart"]')
      : null;
    if (!t) return;
    // Header cart icon only — not the in-cart "View cart" or other links.
    if (!t.closest('.shopify-section-header, #header, #header-inner, #nav-user')) return;
    e.stopImmediatePropagation();
    var href = t.getAttribute('href');
    if (!href) { e.preventDefault(); window.location.assign('/cart'); }
  }, true);
})();


/* =========================================================
   [CGJS-15] CART PAGE — show SKU under each line item
   Pulls SKUs from /cart.js and matches rows by variant id (from the title link).
   ========================================================= */
(function () {
  // Run on the cart page OR wherever the cart drawer (aside#cart) exists.
  if (!document.body.classList.contains('template-cart') && !document.getElementById('cart')) return;
  if (window.__cgCartSkuFinal) return;
  window.__cgCartSkuFinal = true;

  var core = window.__cgCore;
  var skuMap = null;
  var loading = false;

  function insertSkus() {
    if (!skuMap) return;
    var rows = document.querySelectorAll('body.template-cart ul.l4ca > li, body.template-cart .l4ct li, #cart ul.l4ca > li, #cart .l4ct li');
    for (var i = 0; i < rows.length; i++) {
      var li = rows[i];
      if (li.querySelector('.cg-cart-sku')) continue;
      var titleEl = li.querySelector('h2') || li.querySelector('h3') || li.querySelector('h4');
      var link = (titleEl && titleEl.querySelector('a[href*="variant="]')) ||
                 li.querySelector('a[href*="variant="]');
      if (!link) continue;
      var m = (link.getAttribute('href') || '').match(/variant=(\d+)/);
      var sku = m && skuMap[m[1]];
      if (!sku) continue;
      // Insert INSIDE the title heading (after the link) so it stacks under the
      // title — the drawer wraps title/price in a flex .cols, so a sibling div
      // would become its own column instead.
      var host = titleEl || link.closest('h1, h2, h3, h4') || link.parentNode;
      var el = document.createElement('span');
      el.className = 'cg-cart-sku';
      el.textContent = 'SKU: ' + sku;
      host.appendChild(el);
    }
  }

  function loadSkus() {
    if (skuMap) { insertSkus(); return; }
    if (loading) return;
    loading = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        skuMap = {};
        (cart.items || []).forEach(function (it) {
          if (it.id) skuMap[String(it.id)] = it.sku || '';
        });
        loading = false;
        insertSkus();
      })
      .catch(function () { loading = false; });
  }

  core.register(loadSkus);
})();


/* =========================================================
   [CGJS-16] PRODUCT BREADCRUMB — insert a category crumb
   Theme renders only "Home > Product". We add the product's
   TYPE as a middle crumb linking to its collection:
   Home > {type} > Product.
   The collection handle is the slugified type; add an override
   in TYPE_MAP below if a type's collection handle differs.
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.__cgBreadcrumbFinal) return;
  window.__cgBreadcrumbFinal = true;

  var core = window.__cgCore;
  if (!core) return;

  // Category detection from the product TYPE (first match wins → order
  // specific before general). `generic` is the all-brands collection.
  var CATS = [
    { re: /intercom kit|video intercom/i,                key: 'intercomkit', generic: 'video-intercom-kits',             label: 'Video Intercom Kits' },
    { re: /intercom|door ?station|door ?bell|doorbell/i, key: 'intercom',    generic: 'video-intercoms-door-bells',      label: 'Intercoms & Doorbells' },
    { re: /\bnvr\b|network video recorder/i,             key: 'nvr',         generic: 'nvrs',                            label: 'NVR Recorders' },
    { re: /\bdvr\b|\bxvr\b/i,                             key: 'dvr',         generic: 'hd-recorders',                    label: 'DVR / XVR Recorders' },
    { re: /\bkit\b|bundle/i,                              key: 'kit',         generic: 'cctv-kits',                       label: 'CCTV Kits' },
    { re: /\bptz\b/i,                                     key: 'ptz',         generic: 'ptz-security-cameras',            label: 'PTZ Cameras' },
    { re: /thermal/i,                                     key: 'thermal',     generic: 'thermal-series-security-cameras', label: 'Thermal Cameras' },
    { re: /panoramic|fisheye/i,                           key: 'panoramic',   generic: 'panoramic-security-cameras',      label: 'Panoramic Cameras' },
    { re: /camera/i,                                      key: 'camera',      generic: 'security-cameras',                label: 'Security Cameras' },
    { re: /\bswitch(es)?\b|poe switch/i,                  key: 'switch',      generic: 'switches',                        label: 'Switches' },
    { re: /monitor/i,                                     key: 'monitor',     generic: 'monitors',                        label: 'Monitors' },
    { re: /hard ?disk|hard ?drive|\bsd card|memory card|storage/i, key: 'storage', generic: 'hard-drives-storage',       label: 'Hard Drives & Storage' },
    { re: /\bups\b/i,                                     key: 'ups',         generic: 'ups',                             label: 'UPS' },
    { re: /extender/i,                                    key: 'extender',    generic: 'extender',                        label: 'Extenders' },
    { re: /hdmi splitter|hdmi switch/i,                   key: 'hdmisplit',   generic: 'hdmi-switcher-splitter',          label: 'HDMI Splitters' },
    { re: /hdmi cable/i,                                  key: 'hdmicable',   generic: 'hdmi-cable',                      label: 'HDMI Cables' },
    { re: /network cable|ethernet cable/i,               key: 'netcable',    generic: 'ethernet-cable',                  label: 'Ethernet Cable' },
    { re: /bracket|mount/i,                               key: 'bracket',     generic: 'cctv-brackets-mounts',            label: 'Brackets & Mounts' },
    { re: /accessor/i,                                    key: 'accessory',   generic: 'cctv-accessories',                label: 'CCTV Accessories' }
  ];

  // Brand-specific collections by vendor + category key. When present, these
  // win over the generic collection. Values: [handle, label]. All verified.
  var BRANDS = [
    { re: /dahua/i, map: {
        camera:      ['dahua-security-cameras', 'Dahua Cameras'],
        nvr:         ['dahua-nvr-recorders',    'Dahua NVR Recorders'],
        kit:         ['dahua-cctv-kits',        'Dahua CCTV Kits'],
        ptz:         ['dahua-ptz-cameras',      'Dahua PTZ Cameras'],
        intercomkit: ['dahua-intercom-kits',    'Dahua Intercom Kits'],
        intercom:    ['dahua-video-intercoms',  'Dahua Video Intercoms'],
        accessory:   ['dahua-cctv-accessories', 'Dahua Accessories']
      } },
    { re: /hikvision/i, map: {
        camera:      ['hikvision-security-cameras',      'Hikvision Cameras'],
        nvr:         ['hikvision-nvr-recorders',         'Hikvision NVR Recorders'],
        kit:         ['hikvision-cctv-kits',             'Hikvision CCTV Kits'],
        ptz:         ['hikvision-ptz-cameras',           'Hikvision PTZ Cameras'],
        intercomkit: ['hikvision-intercom-kits',         'Hikvision Intercom Kits'],
        intercom:    ['hikvision-video-intercom-system', 'Hikvision Intercoms'],
        accessory:   ['hikvision-cctv-accessories',      'Hikvision Accessories']
      } },
    { re: /hilook/i, map: {
        camera: ['hilook-security-cameras', 'HiLook Cameras']
      } },
    { re: /ubiquiti|unifi/i, map: {
        camera: ['unifi-protect-cameras', 'UniFi Protect Cameras']
      } },
    { re: /axis/i, map: {
        camera:    ['axis-security-cameras',  'Axis Cameras'],
        nvr:       ['axis-video-recorders',   'Axis Video Recorders'],
        ptz:       ['axis-ptz-cameras',       'Axis PTZ Cameras'],
        panoramic: ['axis-panoramic-cameras', 'Axis Panoramic Cameras'],
        intercom:  ['axis-network-intercoms', 'Axis Network Intercoms'],
        accessory: ['axis-cctv-accessories',  'Axis Accessories']
      } },
    { re: /\bunv\b|uniview/i, map: {
        camera: ['unv-security-cameras', 'UNV Cameras'],
        nvr:    ['unv-nvr',              'UNV NVR Recorders'],
        kit:    ['unv-cctv-kit',         'UNV CCTV Kits']
      } },
    { re: /uniarch/i, map: {
        camera: ['uniarch-network-cameras',          'Uniarch Cameras'],
        nvr:    ['uniarch-network-video-recorders',  'Uniarch NVR Recorders'],
        kit:    ['uniarch-cctv-kits',                'Uniarch CCTV Kits']
      } },
    { re: /vivotek/i, map: {
        camera:    ['vivotek-security-cameras', 'VIVOTEK Cameras'],
        nvr:       ['vivotek-nvr',              'VIVOTEK NVR'],
        accessory: ['vivotek-accessories',     'VIVOTEK Accessories']
      } },
    { re: /idis/i, map: {
        camera:   ['idis-security-cameras', 'IDIS Cameras'],
        nvr:      ['idis-nvrs',             'IDIS NVRs'],
        kit:      ['idis-cctv-kits',        'IDIS CCTV Kits'],
        intercom: ['idis-intercoms',        'IDIS Intercoms']
      } },
    { re: /akuvox/i, map: {
        intercomkit: ['akuvox-intercom-kit', 'Akuvox Intercom Kits']
      } }
  ];

  function categoryFor(type, vendor) {
    var cat = null;
    for (var i = 0; i < CATS.length; i++) { if (CATS[i].re.test(type)) { cat = CATS[i]; break; } }
    if (!cat) return null;
    // Brand-specific collection wins when one exists for this category.
    for (var b = 0; b < BRANDS.length; b++) {
      if (BRANDS[b].re.test(vendor || '') && BRANDS[b].map[cat.key]) {
        var e = BRANDS[b].map[cat.key];
        return { handle: e[0], label: e[1] };
      }
    }
    return { handle: cat.generic, label: cat.label };
  }

  function meta() {
    try {
      if (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        return ShopifyAnalytics.meta.product;
      }
    } catch (e) {}
    return null;
  }

  function getType() {
    var p = meta();
    if (p && p.type) return p.type;
    var m = document.querySelector('meta[property="product:category"], meta[name="product:category"]');
    return m ? (m.getAttribute('content') || '') : '';
  }

  function getVendor() {
    var p = meta();
    return (p && p.vendor) ? p.vendor : '';
  }

  function slug(s) {
    return String(s || '')
      .toLowerCase().trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  var attempted = false;       // only try the (async) crumb insert once
  var verifyCache = {};        // handle -> true|false once checked

  function buildCrumb(ol, label, handle) {
    if (ol.querySelector('.cg-bc-cat')) return;
    var items = ol.children;
    if (items.length < 2) return;
    var productLi = items[items.length - 1];
    var li = document.createElement('li');
    li.className = 'cg-bc-cat';
    var a = document.createElement('a');
    a.href = '/collections/' + handle;
    a.textContent = label;
    a.setAttribute('data-cg-cont', '1');
    li.appendChild(a);
    ol.insertBefore(li, productLi);
  }

  // Try candidate crumbs in priority order (category first, then brand) and
  // use the FIRST whose collection actually exists — so no 404 links, and a
  // brand crumb still shows when the product type has no collection.
  function tryCandidates(ol, list, i) {
    if (i >= list.length) return;            // none existed → leave Home › Product
    var c = list[i];
    // Category rule handles are known-good (real collections) — insert directly.
    if (c.trusted) { buildCrumb(ol, c.label, c.handle); return; }
    if (verifyCache[c.handle] === true) { buildCrumb(ol, c.label, c.handle); return; }
    if (verifyCache[c.handle] === false) { tryCandidates(ol, list, i + 1); return; }
    // Brand fallback handle is uncertain — verify with a light HEAD (200/404).
    fetch('/collections/' + c.handle, { method: 'HEAD', credentials: 'same-origin' })
      .then(function (res) {
        var ok = !!(res && res.ok);
        verifyCache[c.handle] = ok;
        if (ok) buildCrumb(ol, c.label, c.handle);
        else tryCandidates(ol, list, i + 1);
      })
      .catch(function () { verifyCache[c.handle] = false; tryCandidates(ol, list, i + 1); });
  }

  function tryInsert(ol) {
    if (attempted || ol.querySelector('.cg-bc-cat')) return;
    if (ol.children.length < 2) return; // wait for Home + Product
    var type = getType();
    var vendor = getVendor();
    var list = [];
    // 1) Category collection from the type (preferred), via keyword rules.
    var cat = type ? categoryFor(type, vendor) : null;
    if (cat) list.push({ label: cat.label, handle: cat.handle, trusted: true });
    // 2) Brand collection fallback (verified, since vendor handles vary).
    if (vendor) list.push({ label: vendor, handle: slug(vendor) });
    if (!list.length) { attempted = true; return; }
    attempted = true;
    tryCandidates(ol, list, 0);
  }

  function addSeparators(ol) {
    // Real "›" separator elements before every crumb except the first.
    // (Bulletproof — the theme's ::before separator is absolutely positioned
    // and fights CSS overrides, so we use elements instead.)
    var lis = ol.children;
    for (var i = 1; i < lis.length; i++) {
      var node = lis[i];
      if (node.querySelector && node.querySelector('.cg-bc-sep')) continue;
      var sep = document.createElement('span');
      sep.className = 'cg-bc-sep';
      sep.setAttribute('aria-hidden', 'true');
      sep.textContent = '›';
      node.insertBefore(sep, node.firstChild);
    }
  }

  function run() {
    var ol = document.querySelector('nav.n6br ol, nav[aria-label="breadcrumbs"] ol');
    if (!ol) return;
    tryInsert(ol);
    addSeparators(ol);
  }

  core.register(run);
})();


/* [CGJS-17] removed — the price text-node wrap (.cg-price-amount + the
   .cg-price-suffix for "Incl. GST") is now done inside CGJS-03's
   ensureAmountSpan(), which runs in the correct order so the price total +
   compare-at also work. */




/* =========================================================
   [CGJS-18] COLLECTION / FEATURED CARD SKU
   The Sandbox (Xtra Live 8) product cards show a stock badge (a check icon
   + an info-tooltip "i" icon, in p.stock.s1tt-cont) where the live theme
   showed "SKU: xxx". The SKU isn't in the card markup, so we read it from
   /products/{handle}.js (lazily via IntersectionObserver, cached per handle)
   and render "SKU: xxx" with a bold "SKU:". CSS hides the stock icon line.
   ========================================================= */
(function () {
  if (window.__cgCardSku) return;
  window.__cgCardSku = true;
  var core = window.__cgCore;
  if (!core) return;

  function esc(s) {
    return (s || '').replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  }
  function copyText(txt) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(txt);
    }
    return new Promise(function (res, rej) {
      try {
        var ta = document.createElement('textarea');
        ta.value = txt;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        document.execCommand('copy');
        document.body.removeChild(ta);
        res();
      } catch (e) { rej(e); }
    });
  }
  function bindCopy(btn, sku) {
    if (!btn) return;
    function doCopy(ev) {
      ev.preventDefault();
      ev.stopPropagation();
      copyText(sku).then(function () {
        btn.classList.add('is-copied');
        clearTimeout(btn._cgT);
        btn._cgT = setTimeout(function () { btn.classList.remove('is-copied'); }, 1300);
      }).catch(function () {});
    }
    btn.addEventListener('click', doCopy);
    btn.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ' || ev.keyCode === 13 || ev.keyCode === 32) doCopy(ev);
    });
  }
  // Main product-page SKU is left as the theme renders it (reverted — the
  // relocation/spacing interfered with the star-rating layout). Only the
  // collection / featured card SKUs are decorated below.

  var cache = {}; // handle -> product object (or null)
  function fetchProduct(handle, cb) {
    if (cache[handle] === '__pending') return;
    if (handle in cache) { cb(cache[handle]); return; }
    cache[handle] = '__pending';
    fetch(root() + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { cache[handle] = p || null; cb(cache[handle]); })
      .catch(function () { cache[handle] = null; cb(null); });
  }

  function handleFromCard(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return '';
    var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }
  function variantIdFromCard(card) {
    var inp = card.querySelector('input[name="id"]');
    return inp ? String(inp.value) : '';
  }
  function skuFor(prod, vid) {
    if (!prod || !prod.variants || !prod.variants.length) return '';
    var i;
    if (vid) {
      for (i = 0; i < prod.variants.length; i++) {
        if (String(prod.variants[i].id) === vid) return prod.variants[i].sku || '';
      }
    }
    for (i = 0; i < prod.variants.length; i++) {
      if (prod.variants[i].sku) return prod.variants[i].sku;
    }
    return '';
  }

  function decorate(card) {
    if (card.dataset.cgSku) return;
    card.dataset.cgSku = '1';
    var handle = handleFromCard(card);
    if (!handle) return;
    var vid = variantIdFromCard(card);
    fetchProduct(handle, function (prod) {
      var sku = skuFor(prod, vid);
      if (!sku) return;
      if (card.querySelector('.cg-card-sku')) return;
      var el = document.createElement('p');
      el.className = 'cg-card-sku';
      el.innerHTML = '<b>SKU:</b> <span class="cg-card-sku__val" tabindex="0" ' +
        'data-sku="' + esc(sku) + '" title="Tap to copy" aria-label="Copy SKU ' + esc(sku) + '">' +
        esc(sku) + '</span>';
      bindCopy(el.querySelector('.cg-card-sku__val'), sku);
      var stock = card.querySelector('p.stock, .s1tt-cont');
      if (stock && stock.parentNode) {
        stock.parentNode.insertBefore(el, stock);
      } else {
        var h = card.querySelector('h3');
        if (h && h.parentNode) h.parentNode.insertBefore(el, h.nextSibling);
        else card.appendChild(el);
      }
      card.classList.add('cg-sku-on');
    });
  }

  var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (ents) {
    for (var i = 0; i < ents.length; i++) {
      if (ents[i].isIntersecting) { decorate(ents[i].target); io.unobserve(ents[i].target); }
    }
  }, { rootMargin: '300px' }) : null;

  // Main product-page SKU: theme renders <div class="f8pr-codes"><p>
  // <span class="strong">SKU:</span> VALUE</p></div>. Make just the VALUE
  // tap-to-copy, in place (no relocation — that fought the star-rating layout).
  function decorateMainSku() {
    var scope = document.querySelector('#main-product');
    if (!scope) return;
    var ps = scope.querySelectorAll('.f8pr-codes p');
    for (var j = 0; j < ps.length; j++) {
      var p = ps[j];
      if (p.querySelector('.cg-card-sku__val')) continue;   // already done
      var strong = p.querySelector('.strong, strong, b');
      if (!strong || !/sku/i.test(strong.textContent || '')) continue;
      var value = '', valueNode = null, nodes = p.childNodes, k;
      for (k = 0; k < nodes.length; k++) {
        if (nodes[k].nodeType === 3 && (nodes[k].nodeValue || '').trim()) {
          value = (nodes[k].nodeValue || '').trim(); valueNode = nodes[k]; break;
        }
      }
      if (!valueNode || !value) continue;
      var span = document.createElement('span');
      span.className = 'cg-card-sku__val';
      span.setAttribute('tabindex', '0');
      span.setAttribute('data-sku', value);
      span.setAttribute('title', 'Tap to copy');
      span.setAttribute('aria-label', 'Copy SKU ' + value);
      span.textContent = value;
      valueNode.parentNode.replaceChild(span, valueNode);
      span.parentNode.insertBefore(document.createTextNode(' '), span); // keep the space after "SKU:"
      bindCopy(span, value);
    }
  }

  // Wrap each collection-filter group (an <h4> heading + its following options)
  // into a .cg-fsec div so each section can get its own tinted background.
  function wrapFilterSections() {
    // Desktop only. The mobile drawer is the theme's native lazy accordion
    // (options injected on tap) — wrapping/styling it fights the theme and
    // looks broken, so we leave mobile entirely native.
    if (window.innerWidth < 990) return;
    var fieldsets = document.querySelectorAll('.f8fl fieldset');
    for (var fi = 0; fi < fieldsets.length; fi++) {
      var fs = fieldsets[fi];
      if (fs.dataset.cgFsec) continue;
      var h4s = [].slice.call(fs.children).filter(function (n) { return n.tagName === 'H4'; });
      if (!h4s.length) continue;
      fs.dataset.cgFsec = '1';
      h4s.forEach(function (h4, idx) {
        var sec = document.createElement('div');
        sec.className = 'cg-fsec ' + (idx % 2 === 0 ? 'cg-fsec-a' : 'cg-fsec-b');
        fs.insertBefore(sec, h4);
        var node = h4;
        while (node) {
          var nxt = node.nextSibling;
          sec.appendChild(node);
          if (nxt && nxt.nodeType === 1 && nxt.tagName === 'H4') break; // stop at next section
          node = nxt;
        }
      });
    }
  }

  // Force the collection layout inline (beats the theme rules): narrow filter
  // aside + the products column fills the rest. Desktop only; cleared on mobile.
  var FILTER_W = 290;
  function sizeFilterAside() {
    var form = document.querySelector('.f8fl');
    if (!form) return;
    // The real flex layout lives on .collection-wrapper (or the nearest flex
    // parent). The aside can be nested one or more wrappers deep inside the
    // actual flex child, so we must size that CHILD — not the aside — or the
    // wrapper keeps its wide default and leaves an empty gap column.
    var container = form.closest('.collection-wrapper');
    var aside = form.closest('aside') || form.parentNode;
    if (!container) container = aside ? aside.parentNode : null;
    if (!container) return;
    // find the direct child of the container that contains the form
    var filterCol = form;
    while (filterCol.parentNode && filterCol.parentNode !== container) {
      filterCol = filterCol.parentNode;
    }
    var desktop = window.innerWidth >= 990;
    var i, c, n;
    if (desktop) {
      // Force a clean left-packed flex row. The theme uses row-reverse, so
      // when the products column stopped growing both columns packed to the
      // RIGHT and left an empty band. We pin direction, justify + explicit
      // order so the filter is always left and the grid fills the remainder.
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('flex-direction', 'row', 'important');
      container.style.setProperty('justify-content', 'flex-start', 'important');
      container.style.setProperty('align-items', 'flex-start', 'important');
      container.style.setProperty('flex-wrap', 'nowrap', 'important');
      container.style.setProperty('gap', '24px', 'important');
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('max-width', '100%', 'important');
      container.style.setProperty('margin-left', '0', 'important');
      container.style.setProperty('margin-right', '0', 'important');
      // .section-width-boxed adds a lopsided 251px LEFT padding — that was the
      // empty band pushing both columns right. Zero it so the row is flush-left.
      container.style.setProperty('padding-left', '0', 'important');
      container.style.setProperty('padding-right', '0', 'important');
      for (i = 0; i < container.children.length; i++) {
        c = container.children[i];
        if (c.nodeType !== 1) continue;
        if (c === filterCol) {
          c.style.setProperty('order', '0', 'important');          // filter always first (left)
          c.style.setProperty('flex', '0 0 ' + FILTER_W + 'px', 'important');
          c.style.setProperty('width', FILTER_W + 'px', 'important');
          c.style.setProperty('min-width', FILTER_W + 'px', 'important');
          c.style.setProperty('max-width', FILTER_W + 'px', 'important');
          c.style.setProperty('margin', '0', 'important');
          c.style.setProperty('box-sizing', 'border-box', 'important');
        } else {
          c.style.setProperty('order', '1', 'important');          // products column after filter
          c.style.setProperty('flex', '1 1 0%', 'important');       // basis 0 → fills ALL remaining space
          c.style.setProperty('min-width', '0', 'important');
          c.style.setProperty('max-width', 'none', 'important');
          c.style.setProperty('width', 'auto', 'important');
          c.style.setProperty('margin', '0', 'important');
          c.style.setProperty('padding-left', '0', 'important');
        }
      }
    } else {
      container.style.removeProperty('display');
      container.style.removeProperty('flex-direction');
      container.style.removeProperty('justify-content');
      container.style.removeProperty('align-items');
      container.style.removeProperty('flex-wrap');
      container.style.removeProperty('gap');
      container.style.removeProperty('width');
      container.style.removeProperty('max-width');
      container.style.removeProperty('margin-left');
      container.style.removeProperty('margin-right');
      container.style.removeProperty('padding-left');
      container.style.removeProperty('padding-right');
      for (i = 0; i < container.children.length; i++) {
        c = container.children[i];
        if (c.nodeType !== 1) continue;
        c.style.removeProperty('order');
        c.style.removeProperty('flex');
        c.style.removeProperty('width');
        c.style.removeProperty('max-width');
        c.style.removeProperty('min-width');
        c.style.removeProperty('margin');
        c.style.removeProperty('padding-left');
        c.style.removeProperty('box-sizing');
      }
    }
  }
  if (!window.__cgFilterResize) {
    window.__cgFilterResize = true;
    window.addEventListener('resize', function () {
      wrapFilterSections();   // re-wrap if the theme rebuilt the filter for the new breakpoint
      sizeFilterAside();
    });
  }

  function scan() {
    decorateMainSku();
    wrapFilterSections();
    sizeFilterAside();
    var cards = document.querySelectorAll('.product-card');
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.dataset.cgSkuObs) continue;
      c.dataset.cgSkuObs = '1';
      if (io) io.observe(c); else decorate(c);
    }
  }

  core.register(scan);
})();


/* =========================================================
   [CGJS-19] FOOTER "MESSAGE US FROM iPHONE" LINK
   The live theme has an Apple Messages-for-Business link in the Customer Care
   footer, right after the WhatsApp item; the Sandbox (Xtra Live 8) theme is
   missing it. Inject the same <li> after the WhatsApp link. Idempotent.
   ========================================================= */
(function () {
  if (window.__cgIMessage) return;
  window.__cgIMessage = true;
  var core = window.__cgCore;
  if (!core) return;

  var APPLE_URL = 'https://bcrw.apple.com/urn:biz:660e29b2-7b40-48b6-8269-cf7be0921829';
  var ICON = 'https://cdn.shopify.com/s/files/1/0637/1554/1221/files/Messages_for_Business_-_Icon_45545db9-2e8c-4d94-b4df-ba530998eda5.svg';

  function add() {
    // Add after EVERY WhatsApp footer item (the site has more than one footer:
    // mobile + desktop). Idempotent per list, so it never duplicates.
    var links = document.querySelectorAll('a[href*="wa.me"], a[href*="api.whatsapp"], a[href*="whatsapp"]');
    for (var i = 0; i < links.length; i++) {
      var li = links[i].closest('li');
      if (!li || !li.parentNode) continue;            // must be a list item
      var ul = li.parentNode;
      // skip if this list already has the iMessage link (ours or the theme's)
      if (ul.querySelector('a[href*="bcrw.apple.com"]')) continue;
      var newLi = document.createElement('li');
      newLi.className = 'cg-imessage-li';
      newLi.innerHTML = '<a href="' + APPLE_URL + '">' +
        '<i aria-hidden="true" class="icon-imessage">' +
        '<img src="' + ICON + '" alt="CCTV Guru Apple Messages for Business" width="18" height="18"></i>' +
        'Message Us from iPhone</a>';
      li.insertAdjacentElement('afterend', newLi);
    }
  }

  core.register(add);
})();


/* =========================================================
   [CGJS-20] FOOTER SOCIAL ICONS (under Customer Care)
   The live theme has a social row (ul.l4sc) the Sandbox is missing. Inject it
   under the Customer Care contact list (ul.l4cn), ordered most-popular-first,
   using the theme's own icon classes. Idempotent + safe on the live theme
   (skips any column that already has a .l4sc social row).
   ========================================================= */
(function () {
  if (window.__cgFooterSocial) return;
  window.__cgFooterSocial = true;
  var core = window.__cgCore;
  if (!core) return;

  // Most-popular-first.
  var SOCIALS = [
    { label: 'Facebook',  href: 'https://facebook.com/cctvguru.com.au',        icon: 'icon-facebook' },
    { label: 'Instagram', href: 'https://www.instagram.com/cctvguru.com.au/',  icon: 'icon-instagram' },
    { label: 'YouTube',   href: 'https://www.youtube.com/@cctv-guru',          icon: 'icon-youtube' },
    { label: 'TikTok',    href: 'https://www.tiktok.com/@cctvguru.com.au',     icon: 'icon-tiktok' },
    { label: 'X',         href: 'https://x.com/cctvguru_au',                   icon: 'icon-twitter' },
    { label: 'LinkedIn',  href: 'https://www.linkedin.com/company/cctv-guru/', icon: 'icon-linkedin' }
  ];

  function buildHTML() {
    var h = '';
    for (var i = 0; i < SOCIALS.length; i++) {
      var s = SOCIALS[i];
      h += '<a aria-label="' + s.label + '" href="' + s.href + '" rel="external noopener" target="external">' +
        '<i aria-hidden="true" class="' + s.icon + '"></i></a>';
    }
    return h;
  }

  function add() {
    var lists = document.querySelectorAll('ul.l4cn');
    for (var i = 0; i < lists.length; i++) {
      var ul = lists[i];
      var col = ul.parentNode;
      if (!col) continue;
      if (col.querySelector('.l4sc')) continue;   // already has a social row
      var social = document.createElement('ul');
      social.className = 'l4sc cg-social-row';
      social.innerHTML = buildHTML();
      ul.insertAdjacentElement('afterend', social);
    }
  }

  core.register(add);
})();


/* =========================================================
   [CGJS-21] FOOTER PAYMENT ICONS — add Afterpay + Zip, reorder
   The Sandbox footer payment list (ul.l4pm) is missing Afterpay & Zip that the
   live site shows. Inject them (Shopify's standard SVGs) if absent, then sort
   all icons most-popular-first. Idempotent: once present + sorted it does
   nothing (so no mutation loop).
   ========================================================= */
(function () {
  if (window.__cgPayIcons) return;
  window.__cgPayIcons = true;
  var core = window.__cgCore;
  if (!core) return;

  var AFTERPAY_SVG = '<svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" aria-labelledby="pi-afterpay"><title id="pi-afterpay">Afterpay</title><path fill="#b2fce4" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path d="m27.4 6.8-2.7-1.6-2.7-1.6c-1.8-1-4.1.3-4.1 2.4v.4c0 .2.1.4.3.5l1.3.7c.4.2.8-.1.8-.5v-.8c0-.4.4-.7.8-.5l2.5 1.4 2.4 1.5c.4.2.4.7 0 .9l-2.5 1.4-2.5 1.6c-.4.2-.8-.1-.8-.5v-.4c0-2.1-2.3-3.4-4.1-2.4l-2.7 1.6-2.7 1.6c-1.8 1-1.8 3.7 0 4.7l2.7 1.6 2.7 1.6c1.8 1 4.1-.3 4.1-2.4v-.4c0-.2-.1-.4-.3-.5l-1.3-.7c-.4-.2-.8.1-.8.5v.8c0 .4-.4.7-.8.5l-2.5-1.4-2.5-1.5c-.4-.2-.4-.7 0-.9l2.5-1.4 2.5-1.4c.4-.2.8.1.8.5v.4c0 2.1 2.3 3.4 4.1 2.4l2.7-1.6 2.7-1.6c1.9-1.2 1.9-3.8.1-4.9z"/></svg>';
  var ZIP_SVG = '<svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" aria-labelledby="pi-zip"><title id="pi-zip">Zip</title><path d="M34.9 23H3.1c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h31.8c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2z" fill="#fff"/><path fill="#1a0826" d="M6.5 14.7l.2 1.9h8l-.2-2.2h-3.8v-.2l3.4-2.4-.2-1.9h-8l.3 2.1h3.7v.3z"/><path fill="#aa8fff" d="M14.8 9.9l.9 6.7h8l-.8-6.7z"/><path d="M32.1 12.3c-.2-1.5-1.4-2.4-3-2.4h-5.3l.8 6.7H27l-.2-1.3h2.5c2.1-.1 3-1.3 2.8-3zm-3 1.1h-2.5l-.2-1.6h2.5c.6 0 .9.3.9.8.1.2 0 .8-.7.8z" fill="#1a0826"/><ellipse transform="rotate(-46.771 16.352 8.156)" cx="16.4" cy="8.2" rx="1.1" ry="1.3" fill="#1a0826"/><path d="M34.9 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3.1c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h31.8m0-1H3.1c-1.7 0-3 1.3-3 3v18c0 1.7 1.3 3 3 3h31.8c1.7 0 3-1.3 3-3V3c0-1.6-1.3-3-3-3z" opacity=".07"/></svg>';

  // most-popular-first (AU). Unknown ids fall to the end, original order kept.
  var ORDER = ['visa','master','american_express','paypal','apple_pay','google_pay','shopify_pay','afterpay','zip','unionpay','klarna'];

  function piId(li) {
    var svg = li.querySelector('svg[aria-labelledby^="pi-"]');
    return svg ? (svg.getAttribute('aria-labelledby') || '').replace(/^pi-/, '') : '';
  }
  function rank(li) {
    var i = ORDER.indexOf(piId(li));
    return i < 0 ? 999 : i;
  }
  function ensure(ul, id, svg) {
    if (ul.querySelector('svg[aria-labelledby="pi-' + id + '"]')) return;
    var li = document.createElement('li');
    li.innerHTML = svg;
    ul.appendChild(li);
  }
  function isSorted(items) {
    var prev = -1;
    for (var i = 0; i < items.length; i++) {
      var r = rank(items[i]);
      if (r < prev) return false;
      prev = r;
    }
    return true;
  }
  function arrange() {
    var uls = document.querySelectorAll('ul.l4pm');
    for (var u = 0; u < uls.length; u++) {
      var ul = uls[u];
      ensure(ul, 'afterpay', AFTERPAY_SVG);
      ensure(ul, 'zip', ZIP_SVG);
      var items = Array.prototype.slice.call(ul.children).filter(function (n) { return n.tagName === 'LI'; });
      if (isSorted(items)) continue;                 // already ordered → no DOM churn
      items.sort(function (a, b) { return rank(a) - rank(b); });
      for (var i = 0; i < items.length; i++) ul.appendChild(items[i]);
    }
  }

  core.register(arrange);
})();


/* =========================================================
   [CGJS-22] FOOTER ACN — replace the Shopify promo link with the ACN
   The theme's copyright line links to shopify.com ("Powered by Shopify" by
   default). Force it to bold "ACN 658 678 525" regardless of the link's text,
   so a future theme update that resets it back to Shopify is corrected too.
   Idempotent + self-healing (re-runs if the footer re-renders).
   ========================================================= */
(function () {
  if (window.__cgAcn) return;
  window.__cgAcn = true;
  var core = window.__cgCore;
  if (!core) return;

  var ACN_NUMBER = '658 678 525';        // normal weight
  var ACN_LABEL = 'ACN';                 // bold

  function boldCopyrightSign(p) {
    if (!p || p.querySelector('.cg-copy')) return;   // already done
    var nodes = p.childNodes;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.nodeType !== 3) continue;
      var v = n.nodeValue || '';
      var idx = v.indexOf('©');                  // ©
      if (idx === -1) continue;
      var frag = document.createDocumentFragment();
      if (v.slice(0, idx)) frag.appendChild(document.createTextNode(v.slice(0, idx)));
      var b = document.createElement('b');
      b.className = 'cg-copy';
      b.textContent = '©';
      frag.appendChild(b);
      if (v.slice(idx + 1)) frag.appendChild(document.createTextNode(v.slice(idx + 1)));
      n.parentNode.replaceChild(frag, n);
      return;
    }
  }

  function fix() {
    // ONLY the "Powered by Shopify" promo link (www.shopify.com?utm…) in the
    // site footer — NOT cdn.shopify.com (PDF files) and NOT generic <footer>s.
    var links = document.querySelectorAll(
      '.shopify-section-footer a[href*="//www.shopify.com"], .shopify-section-footer a[href*="shopify.com?"]'
    );
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var p = a.parentNode;
      // strip a leading "Powered by " text node if a theme update added one
      var prev = a.previousSibling;
      if (prev && prev.nodeType === 3 && /powered by\s*$/i.test(prev.nodeValue || '')) {
        prev.nodeValue = prev.nodeValue.replace(/powered by\s*$/i, '');
      }
      // replace the Shopify link with: <b>ACN</b> 658 678 525  (only "ACN" bold)
      var span = document.createElement('span');
      span.className = 'cg-acn';
      var lab = document.createElement('b');
      lab.textContent = ACN_LABEL;
      span.appendChild(lab);
      span.appendChild(document.createTextNode(' ' + ACN_NUMBER));
      if (p) {
        p.replaceChild(span, a);
        boldCopyrightSign(p);          // bold the © in the same line
      }
    }
  }

  core.register(fix);
})();


/* =========================================================
   [CGJS-23] FLOATING WHATSAPP BUTTON (bottom-left)
   wa.me opens the WhatsApp app on mobile and WhatsApp Web on desktop, so one
   link covers both. Placed bottom-left to stay clear of the Front chat bubble.
   ========================================================= */
(function () {
  if (window.__cgWhatsAppFab) return;
  window.__cgWhatsAppFab = true;

  var WA = 'https://wa.me/61468088369';
  var ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">' +
    '<path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>';

  function inject() {
    if (!document.body) return;
    if (document.getElementById('cg-whatsapp-fab')) return;
    var a = document.createElement('a');
    a.id = 'cg-whatsapp-fab';
    a.href = WA;
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('aria-label', 'Chat with us on WhatsApp');
    a.innerHTML = ICON;
    document.body.appendChild(a);
  }

  var core = window.__cgCore;
  if (core) core.register(inject);
  if (document.body) inject();
  else document.addEventListener('DOMContentLoaded', inject);
})();


/* =========================================================
   [CGJS-24] ACCOUNT DROPDOWN + "Hi {name}"
   Adds an Orders/Profile dropdown to the account button (links to the new
   customer-account portal). If window.cgCustomerName is set (via a small theme
   Liquid snippet), the button label becomes "Hi {name}". The menu is appended
   to <body> and positioned on open, so it can't disturb the header layout.
   ========================================================= */
(function () {
  if (window.__cgAcctMenu) return;
  window.__cgAcctMenu = true;
  var core = window.__cgCore;

  var ACCOUNT = 'https://account.cctvguru.com.au';
  var ORDERS = 'https://account.cctvguru.com.au/orders';
  var PROFILE = 'https://account.cctvguru.com.au/profile';
  var REGISTER = 'https://cctvguru.com.au/pages/tradies-rego';
  var REVIEWS = ''; // set to the Reviews URL to add that item

  function loggedIn() { return !!window.cgCustomerName; }

  var menu = null;
  function buildMenu() {
    if (menu) return menu;
    menu = document.createElement('div');
    menu.id = 'cg-acct-menu';
    menu.className = 'cg-acct-menu';
    menu.setAttribute('role', 'menu');
    var html = '';
    if (loggedIn()) {
      html += '<a href="' + ORDERS + '" role="menuitem">Orders</a>' +
        '<a href="' + PROFILE + '" role="menuitem">Profile</a>';
      if (REVIEWS) html += '<a href="' + REVIEWS + '" role="menuitem">Reviews</a>';
      // Logout — GET link to the theme-resolved logout route (window.cgLogoutUrl
      // = routes.account_logout_url); only shown when available so it can't 404.
      if (window.cgLogoutUrl) {
        html += '<a href="' + window.cgLogoutUrl + '" role="menuitem" class="cg-acct-logout">Logout</a>';
      }
    } else {
      // logged out: Login + B2B registration
      html += '<a href="' + ACCOUNT + '" role="menuitem">Log in</a>' +
        '<a href="' + REGISTER + '" role="menuitem" class="cg-acct-register">Apply for Trade Pricing</a>';
    }
    menu.innerHTML = html;
    document.body.appendChild(menu);
    return menu;
  }
  function positionMenu(anchor) {
    var r = anchor.getBoundingClientRect();
    menu.style.position = 'fixed';
    menu.style.top = (r.bottom + 8) + 'px';
    menu.style.right = Math.max(8, window.innerWidth - r.right) + 'px';
    menu.style.left = 'auto';
  }
  // Mobile (<=990): inline accordion right under the button. Desktop: floating
  // dropdown positioned under the button.
  function openFor(anchor) {
    buildMenu();
    if (window.innerWidth <= 990) {
      menu.classList.add('cg-acct-menu--inline');
      menu.classList.remove('cg-acct-menu--float', 'cg-open');
      menu.style.position = ''; menu.style.top = ''; menu.style.right = ''; menu.style.left = '';
      // Insert AFTER the account <li> (not inside it) so the theme's button
      // styling on .link-btn/.header-button can't cascade onto our links.
      var host = anchor.closest('li') || anchor;
      if (host.nextSibling !== menu) host.parentNode.insertBefore(menu, host.nextSibling);
      // Match the card width to the (centred) button so the borders line up
      // with the button's left/right edges.
      var aRect = anchor.getBoundingClientRect();
      if (aRect.width) {
        menu.style.setProperty('max-width', aRect.width + 'px', 'important');
        menu.style.setProperty('margin-left', 'auto', 'important');
        menu.style.setProperty('margin-right', 'auto', 'important');
        menu.style.setProperty('margin-top', '0', 'important');
        // pull the card up so its side borders touch the button's bottom edge
        var gap = menu.getBoundingClientRect().top - aRect.bottom;
        if (gap > 0) menu.style.setProperty('margin-top', (-gap) + 'px', 'important');
      }
      // square the button's bottom corners (inline !important beats the theme's
      // higher-specificity radius rule so the corners fill blue into the card)
      anchor.classList.add('cg-acct-trigger-open');
      anchor.style.setProperty('transition', 'border-radius 0.2s ease', 'important');
      anchor.style.setProperty('border-bottom-left-radius', '0', 'important');
      anchor.style.setProperty('border-bottom-right-radius', '0', 'important');
      anchor.style.setProperty('border-top-left-radius', '22px', 'important');   // match card's bottom radius
      anchor.style.setProperty('border-top-right-radius', '22px', 'important');
      void menu.offsetHeight;                                  // reflow so the slide-down animates
      requestAnimationFrame(function () { menu.classList.add('cg-open'); });
      return;
    }
    menu.classList.add('cg-acct-menu--float');
    menu.classList.remove('cg-acct-menu--inline');
    if (menu.parentNode !== document.body) document.body.appendChild(menu);
    positionMenu(anchor);
    menu.classList.add('cg-open');
  }
  function closeMenu() {
    if (menu) menu.classList.remove('cg-open');
    var triggers = document.querySelectorAll('.cg-acct-trigger-open');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].classList.remove('cg-acct-trigger-open');
      triggers[i].style.removeProperty('border-bottom-left-radius');
      triggers[i].style.removeProperty('border-bottom-right-radius');
      triggers[i].style.removeProperty('border-top-left-radius');
      triggers[i].style.removeProperty('border-top-right-radius');
      triggers[i].style.removeProperty('transition');
    }
  }

  function applyName(link) {
    if (link.dataset.cgNamed) return;
    // logged in → "Hi {name}"; logged out → "Account & B2B"
    var label = loggedIn() ? ('Hi ' + window.cgCustomerName) : 'Account';
    var walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (/orders|profile|account|b2b|sign\s*in|log\s*in|hi\s/i.test(node.nodeValue || '')) {
        node.nodeValue = label;
        link.dataset.cgNamed = '1';
        return;
      }
    }
  }

  function wire(link) {
    applyName(link);
    // Button opens the dropdown in BOTH states (logged in: Orders/Profile/Logout;
    // logged out: Login / Register for B2B pricing).
    if (link.dataset.cgAcct) return;
    link.dataset.cgAcct = '1';
    link.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      buildMenu();
      if (menu.classList.contains('cg-open')) { closeMenu(); return; }
      openFor(link);
    });
  }

  function scan() {
    // flag logged-in state so [CG-62] can show the tick on the "Hi Min" button
    if (loggedIn()) document.documentElement.classList.add('cg-loggedin');
    var links = document.querySelectorAll('a[href*="account.cctvguru.com.au"]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].closest('#cg-acct-menu')) continue; // skip our own dropdown items
      wire(links[i]);
    }
  }

  document.addEventListener('click', function (e) {
    if (menu && menu.classList.contains('cg-open') && !menu.contains(e.target)) closeMenu();
  });
  window.addEventListener('resize', closeMenu);

  if (core) core.register(scan);
  else { document.addEventListener('DOMContentLoaded', scan); scan(); }
})();


/* =========================================================
   [CGJS-25] COLLECTION SORT — rename + reorder options (DESKTOP bv-select)
   The theme's sort <select> feeds a bv-select flyout. We rename the labels and
   reorder BOTH the flyout AND the underlying <select> into the same order, so
   the sort still applies correctly however bv-select maps a click. Values are
   never touched (only .text), so functionality is preserved. Idempotent:
   original labels are stashed in data-cg-orig and everything is keyed off that.
   Desired order:
     1 Best Sellers (best selling)  2 Most Relevant  3 Featured
     4 Highest (price high→low)     5 Lowest (price low→high)
     6 Newest (date new→old)        7 Oldest (date old→new)
     8-9 Alphabetically A-Z / Z-A   (rest keep original label, pushed to end)
   ========================================================= */
(function () {
  if (window.__cgSortOrder) return;
  window.__cgSortOrder = true;
  var core = window.__cgCore;
  if (!core) return;

  // [normalized ORIGINAL label] -> new label, in desired display order
  var MAP = [
    ['best selling', 'Best Sellers'],
    ['most relevant', 'Most Relevant'],
    ['featured', 'Featured'],
    ['price, high to low', 'Highest'],
    ['price, low to high', 'Lowest'],
    ['date, new to old', 'Newest'],
    ['date, old to new', 'Oldest'],
    ['alphabetically, a-z', 'A-Z'],
    ['alphabetically, z-a', 'Z-A']
  ];
  function norm(s) { return (s || '').replace(/\s+/g, ' ').trim().toLowerCase(); }
  function lookup(orig) {
    var t = norm(orig);
    for (var i = 0; i < MAP.length; i++) if (MAP[i][0] === t) return { rank: i, label: MAP[i][1] };
    return { rank: 900, label: null };   // unknown → keep label, push to end
  }
  // set visible text without wiping element children (e.g. an inner .text span)
  function setText(el, label) {
    var target = el;
    if (el.children.length) {
      var t = el.querySelector('.text, .sl_inner');
      if (t) target = t; else return;
    }
    if (norm(target.textContent) !== norm(label)) target.textContent = label;
  }
  // stash original once, return it
  function orig(el, current) {
    if (!el.getAttribute('data-cg-orig')) el.setAttribute('data-cg-orig', norm(current));
    return el.getAttribute('data-cg-orig');
  }

  function apply() {
    // (1) native <select> — rename option text + reorder (values untouched)
    var sels = document.querySelectorAll('form.f8sr select, select[name="sort_by"]');
    for (var s = 0; s < sels.length; s++) {
      var sel = sels[s];
      var opts = [].slice.call(sel.options);
      for (var o = 0; o < opts.length; o++) {
        var op = opts[o];
        var r = lookup(orig(op, op.text));
        if (r.label) op.text = r.label;
        op.__cgRank = r.rank;
      }
      var sortedOpts = opts.slice().sort(function (a, b) { return a.__cgRank - b.__cgRank; });
      var oChanged = false;
      for (var so = 0; so < sortedOpts.length; so++) {
        if (sel.options[so] !== sortedOpts[so]) { oChanged = true; break; }
      }
      if (oChanged) sortedOpts.forEach(function (op) { sel.appendChild(op); });
    }

    // (2) flyout anchors — rename + reorder within their list container
    var anchors = document.querySelectorAll('.bv_ul_inner a, span.li > a, .bv_mainselect a:not(.bv_atual)');
    var parents = [];
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      if (a.classList.contains('bv_atual')) continue;
      var rr = lookup(orig(a, a.textContent));
      if (rr.label) setText(a, rr.label);
      var wrap = a.closest('span.li') || a;
      wrap.__cgRank = rr.rank;
      var parent = wrap.parentNode;
      if (parent && parents.indexOf(parent) < 0) parents.push(parent);
    }
    for (var p = 0; p < parents.length; p++) {
      var pn = parents[p];
      // let the flyout grow to show every option + round its box (inner+outer)
      var boxes = [pn, pn.parentNode];
      for (var b = 0; b < boxes.length; b++) {
        var bx = boxes[b];
        if (bx && bx.style) {
          bx.style.setProperty('max-height', 'none', 'important');
          bx.style.setProperty('border-radius', '12px', 'important');
          bx.style.setProperty('overflow', 'hidden', 'important');
        }
      }
      // reorder option wrappers by rank — re-run whenever the order is wrong
      // (bv-select re-renders the flyout, so a one-shot flag isn't enough)
      var kids = [].slice.call(pn.children).filter(function (k) { return typeof k.__cgRank === 'number'; });
      if (kids.length > 1) {
        var sortedKids = kids.slice().sort(function (x, y) { return x.__cgRank - y.__cgRank; });
        var fChanged = false;
        for (var fk = 0; fk < sortedKids.length; fk++) {
          if (kids[fk] !== sortedKids[fk]) { fChanged = true; break; }
        }
        if (fChanged) sortedKids.forEach(function (k) { pn.appendChild(k); });
      }
    }

    // (3) closed display (current selection) — reverse-map original → new label.
    // Reads current text each pass; new labels aren't original keys, so it's a
    // no-op once renamed (and re-applies after the user picks a new sort).
    var disp = document.querySelectorAll('.bv_atual .sl_inner, .bv_atual .text');
    for (var d = 0; d < disp.length; d++) {
      var dr = lookup(disp[d].textContent);
      if (dr.label) setText(disp[d], dr.label);
    }
  }

  core.register(apply);
})();


/* =========================================================
   [CGJS-26] PRICE-BEAT CTA → WhatsApp. The product-page "Found it cheaper?"
   link fires the removed Front chat (onclick=FrontChat, href=javascript:void).
   Redirect it (and any other dead FrontChat links) to WhatsApp, refresh the
   wording, and restyle the price-beat one as a clean callout badge (its inline
   styles + JS hover handlers are stripped so [CG-60] can take over).
   ========================================================= */
(function () {
  if (window.__cgPriceBeat) return;
  window.__cgPriceBeat = true;
  var core = window.__cgCore;
  var WA = 'https://wa.me/61468088369';
  var TXT = "Found it cheaper? We'll beat it by 5% — message us";   // desktop
  var TXT_SM = "Found cheaper? We'll beat it by 5% — chat now";     // mobile

  // set the badge label responsively — short copy on mobile, full on desktop
  function setText(a) {
    var span = a.querySelector('span');
    if (!span) return;
    span.removeAttribute('style');
    var product = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    var isUbiquiti = product && /^ubiquiti$/i.test(String(product.vendor || '').trim());
    var t = isUbiquiti
      ? ((window.innerWidth <= 700) ? "Found cheaper? We'll match the price — chat now" : "Found it cheaper? We'll match the price — message us")
      : ((window.innerWidth <= 700) ? TXT_SM : TXT);
    if (span.textContent.replace(/\s+/g, ' ').trim() !== t) span.textContent = t;
  }

  function fix() {
    // dead Front-chat links → WhatsApp price-beat badge
    var links = document.querySelectorAll('a[onclick*="FrontChat"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var isPB = /cheaper|beat it|5%/i.test(a.textContent);
      a.removeAttribute('onclick');
      a.removeAttribute('onmouseover');
      a.removeAttribute('onmouseout');
      a.setAttribute('href', WA);
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
      if (isPB) {
        a.classList.add('cg-pricebeat');
        a.removeAttribute('style');            // let [CG-60] style it
        var svg = a.querySelector('svg');
        if (svg) svg.removeAttribute('style');
      }
    }
    // responsive label on EVERY price-beat badge (converted + metafield blocks)
    var badges = document.querySelectorAll('a.cg-pricebeat');
    for (var j = 0; j < badges.length; j++) setText(badges[j]);
  }

  if (!window.__cgPbResize) {
    window.__cgPbResize = true;
    window.addEventListener('resize', function () {
      var badges = document.querySelectorAll('a.cg-pricebeat');
      for (var k = 0; k < badges.length; k++) setText(badges[k]);
    });
  }

  if (core) core.register(fix);
  else { document.addEventListener('DOMContentLoaded', fix); fix(); }
})();


/* =========================================================
   [CGJS-27] Cart countdown wording: "Shipped today?" → "Dispatch today?".
   The label lives in .countdown-container .strong; the timer beside it
   re-renders every second, so we re-apply via the shared observer (idempotent).
   ========================================================= */
(function () {
  if (window.__cgCountdownText) return;
  window.__cgCountdownText = true;
  var core = window.__cgCore;
  function fix() {
    var strongs = document.querySelectorAll('.countdown-container .strong');
    for (var i = 0; i < strongs.length; i++) {
      if (/shipped today/i.test(strongs[i].textContent)) strongs[i].textContent = 'Dispatch today?';
    }
  }
  if (core) core.register(fix);
  else { document.addEventListener('DOMContentLoaded', fix); fix(); }
})();


/* =========================================================
   [CGJS-28] Cart price-change pulse. When the qty changes and the total
   recalculates, pulse the total (green = increased, red = decreased). Cart
   page only; runs on the shared observer which fires when the total updates.
   ========================================================= */
(function () {
  if (window.__cgPriceAnim) return;
  window.__cgPriceAnim = true;
  var core = window.__cgCore;
  var KEY = 'cgCartLastTotal';

  // Source of truth = article.form-cart[data-totalprice] (theme-maintained),
  // so we don't depend on our own emphasizeTotal having run yet.
  function readTotal() {
    var art = document.querySelector('.form-cart[data-totalprice]');
    if (art) {
      var a = parseFloat((art.getAttribute('data-totalprice') || '').replace(/[^\d.]/g, ''));
      if (!isNaN(a)) return a;
    }
    var els = document.querySelectorAll('.cg-cart-total-value, .form-cart .mobile-only .strong');
    for (var i = 0; i < els.length; i++) {
      if (els[i].offsetParent === null) continue;
      var v = parseFloat((els[i].textContent || '').replace(/[^\d.]/g, ''));
      if (!isNaN(v)) return v;
    }
    return null;
  }

  function pulse(up) {
    // wait two frames so the theme's re-render settles, then animate the fresh
    // total element (otherwise the class lands on a node that's about to be
    // replaced, and nothing shows).
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      var t = document.querySelectorAll('.cg-cart-total-value, .form-cart .mobile-only .strong');
      for (var i = 0; i < t.length; i++) {
        var el = t[i];
        if (el.offsetParent === null) continue;
        el.classList.remove('cg-price-up', 'cg-price-down');
        void el.offsetWidth;                                   // reflow → restart animation
        el.classList.add(up ? 'cg-price-up' : 'cg-price-down');
      }
    }); });
  }

  // Compare against a value persisted in sessionStorage — this way it fires
  // whether the qty change re-rendered via AJAX OR reloaded the whole page
  // (the 409 fallback), since the stored value survives a reload.
  function check() {
    if (!document.body.classList.contains('template-cart')) return;
    var v = readTotal();
    if (v === null) return;
    var stored = sessionStorage.getItem(KEY);
    var prev = stored !== null ? parseFloat(stored) : null;
    if (prev !== null && Math.abs(v - prev) > 0.001) pulse(v > prev);
    if (prev === null || Math.abs(v - prev) > 0.001) {
      try { sessionStorage.setItem(KEY, String(v)); } catch (e) {}
    }
  }

  window.__cgPulseTotal = pulse;
  if (core) core.register(check);
  else { document.addEventListener('DOMContentLoaded', check); check(); }
})();


/* =========================================================
   [CGJS-29] Empty-cart page: point the big "Continue shopping" button at the
   homepage ("/") instead of the theme default (a collection page). Scoped to
   the EMPTY cart only (no form-cart, or data-totalqty="0"), so the in-cart
   back-link keeps its own behaviour. Idempotent.
   ========================================================= */
(function () {
  if (window.__cgContinueHome) return;
  window.__cgContinueHome = true;
  var core = window.__cgCore;
  function fix() {
    if (!document.body.classList.contains('template-cart')) return;
    var fc = document.querySelector('article.form-cart');
    var empty = !fc || fc.getAttribute('data-totalqty') === '0';
    if (!empty) return;
    var scope = document.getElementById('content') || document;
    var as = scope.querySelectorAll('a[href]');
    for (var i = 0; i < as.length; i++) {
      var a = as[i];
      var t = (a.textContent || '').replace(/[‹<>]/g, '').trim().toLowerCase();
      if (t === 'continue shopping' && a.getAttribute('href') !== '/') {
        a.setAttribute('href', '/');
      }
    }
  }
  if (core) core.register(fix);
  else { document.addEventListener('DOMContentLoaded', fix); fix(); }
})();


/* =========================================================
   [CGJS-30] Cart free-shipping banner wording. Rewrites the qualified message
   "Your order will be shipped for free!" -> "Your cart qualifies for free
   shipping." (keeps "free shipping" bold via the theme's .strong span). Matches
   only the "shipped for free" text so a not-yet-qualified "add $X" state is left
   alone; idempotent via a data flag.
   ========================================================= */
(function () {
  if (window.__cgFreeShipText) return;
  window.__cgFreeShipText = true;
  var core = window.__cgCore;
  function fix() {
    if (!document.body.classList.contains('template-cart')) return;
    var els = document.querySelectorAll('.l4al .overlay-lime, .l4al li.overlay-lime');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.getAttribute('data-cg-fs') === '1') continue;
      if (/shipped for free/i.test(el.textContent || '')) {
        el.setAttribute('data-cg-fs', '1');
        el.innerHTML = 'Your cart qualifies for <span class="strong">free shipping</span>.';
      }
    }
  }
  if (core) core.register(fix);
  else { document.addEventListener('DOMContentLoaded', fix); fix(); }
})();


/* =========================================================
   [CGJS-31] FAST cart qty updates (cart page). The theme's qty change hits
   /cart/change, 409s, and falls back to a ~2s full page reload. Instead we:
     1. optimistically update the shown total + GST instantly (cached unit price),
     2. debounce a clean POST /cart/change.js with Section Rendering,
     3. swap just the cart section's HTML from the response (no reload),
     4. on ANY error, fall back to location.reload() so the cart can't desync.
   Wired via window.__cgFastCart, called from the qty pill's update().
   ========================================================= */
(function () {
  if (!document.body.classList.contains('template-cart')) return;
  if (window.__cgFastCartInit) return;
  window.__cgFastCartInit = true;

  var cart = null;          // cached /cart.js
  var lastServerTotal = null;   // last SERVER-confirmed total (cents) — pulse direction
  var sectionEl = null, sectionId = null;
  var timers = {};

  function loadCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; if (lastServerTotal === null) lastServerTotal = c.total_price; return c; })
      .catch(function () {});
  }
  loadCart();

  function findSection() {
    var fc = document.querySelector('article.form-cart');
    var sec = fc && fc.closest('.shopify-section');
    if (sec && sec.id) { sectionEl = sec; sectionId = sec.id.replace(/^shopify-section-/, ''); }
    return sectionEl;
  }

  function money(cents) {
    var v = cents / 100;
    try { return '$' + v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch (e) { return '$' + v.toFixed(2); }
  }

  function setTotals(cents) {
    var t = document.querySelectorAll('.cg-cart-total-value, .form-cart .mobile-only .strong');
    for (var i = 0; i < t.length; i++) t[i].textContent = money(cents);
    var g = document.querySelectorAll('.cg-gst-note');
    var gst = Math.round(cents / 11);
    for (var j = 0; j < g.length; j++) g[j].textContent = 'Includes ' + money(gst) + ' GST';
    var fc = document.querySelector('article.form-cart');
    if (fc) fc.setAttribute('data-totalprice', money(cents));
    try { sessionStorage.setItem('cgCartLastTotal', String(cents / 100)); } catch (e) {}
  }

  function variantOf(input) {
    var li = input.closest('li'); if (!li) return null;
    var link = li.querySelector('a[href*="variant="]');
    var m = link && (link.getAttribute('href') || '').match(/variant=(\d+)/);
    return m ? m[1] : null;
  }

  // Product-page-style animation: COUNT the grand total AND the row's line price
  // from old -> new (easeOutCubic), with a subtle fade on the total. rAF
  // timestamp for timing (no Date/performance dep).
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  var tweenRaf = 0;

  // The row's CURRENT line-price text node, matched by its present value so the
  // struck-through compare-at (a different number) is skipped automatically.
  // The row's current line-price text node = a $ text node inside the price
  // element that is NOT the struck-through compare-at (.old-price / <s> / <del>).
  // Find the price text node that is ACTUALLY PAINTED. A Range around the node
  // reports a real rect only if rendered (0x0 for this theme's hidden clone) —
  // the only reliable way to grab the on-screen price, not the duplicate.
  function findLinePriceNode(row) {
    if (!row) return null;
    var w = document.createTreeWalker(row, NodeFilter.SHOW_TEXT, null, false);
    var n;
    while ((n = w.nextNode())) {
      if (!/\$\d/.test(n.nodeValue || '')) continue;
      var skip = false, p = n.parentElement;
      for (; p && p !== row.parentElement; p = p.parentElement) {
        var cls = (p.className || '') + '';
        if (/old-price|compare|was|strike/i.test(cls) || p.tagName === 'S' || p.tagName === 'DEL') { skip = true; break; }
      }
      if (skip) continue;
      var r = document.createRange();
      r.selectNodeContents(n);
      var rect = r.getBoundingClientRect();
      if (rect.width > 1 && rect.height > 1 && rect.bottom > 0 && rect.top < window.innerHeight) return n;
    }
    return null;
  }
  function parseCents(str) {
    var m = (str || '').match(/[\d,]+(?:\.\d{2})?/);
    return m ? Math.round(parseFloat(m[0].replace(/,/g, '')) * 100) : null;
  }

  function animateChange(fromTotal, toTotal, lineNode, fromLine, toLine) {
    if (tweenRaf) cancelAnimationFrame(tweenRaf);
    var els = document.querySelectorAll('.cg-cart-total-value, .form-cart .mobile-only .strong');
    var gEls = document.querySelectorAll('.cg-gst-note');
    var cls = toTotal >= fromTotal ? 'cg-price-up' : 'cg-price-down';
    for (var k = 0; k < els.length; k++) {
      els[k].classList.remove('cg-price-up', 'cg-price-down');
      void els[k].offsetWidth;
      els[k].classList.add(cls);
    }
    function setLine(cents) {
      if (lineNode) lineNode.nodeValue = (lineNode.nodeValue || '').replace(/\$[\d,]+(?:\.\d{2})?/, money(cents));
    }
    function paint(vTotal, vLine) {
      for (var i = 0; i < els.length; i++) els[i].textContent = money(vTotal);
      var gst = money(Math.round(vTotal / 11));
      for (var j = 0; j < gEls.length; j++) gEls[j].textContent = 'Includes ' + gst + ' GST';
      setLine(vLine);
    }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      paint(toTotal, toLine); setTotals(toTotal); return;
    }
    // Lock the price cell width to its widest value during the count so the
    // flexible product title beside it doesn't reflow/jitter as digits change.
    var priceEl = null, savedMinW = '';
    if (lineNode) {
      priceEl = (lineNode.parentElement && lineNode.parentElement.closest)
        ? (lineNode.parentElement.closest('.price') || lineNode.parentElement) : lineNode.parentElement;
      if (priceEl) {
        var origVal = lineNode.nodeValue;
        setLine(Math.max(fromLine, toLine));
        savedMinW = priceEl.style.minWidth;
        priceEl.style.setProperty('min-width', Math.ceil(priceEl.getBoundingClientRect().width) + 'px', 'important');
        lineNode.nodeValue = origVal;
      }
    }
    function unlockPrice() { if (priceEl) priceEl.style.minWidth = savedMinW; }
    var startTs = null, dur = 340;
    function frame(now) {
      if (startTs === null) startTs = now;
      var t = Math.min(1, (now - startTs) / dur);
      var e = easeOutCubic(t);
      paint(Math.round(fromTotal + (toTotal - fromTotal) * e), Math.round(fromLine + (toLine - fromLine) * e));
      if (t < 1) { tweenRaf = requestAnimationFrame(frame); }
      else { tweenRaf = 0; setTotals(toTotal); setLine(toLine); holdLine(); }
    }
    // Keep re-writing the final line value every frame (out-racing the theme's
    // reset) until the section swap detaches the node or ~1.4s passes.
    function holdLine() {
      if (!lineNode) { unlockPrice(); return; }
      var hstart = null;
      function hold(now) {
        if (hstart === null) hstart = now;
        if (!lineNode.isConnected || now - hstart > 1400) { unlockPrice(); return; }
        setLine(toLine);
        requestAnimationFrame(hold);
      }
      requestAnimationFrame(hold);
    }
    tweenRaf = requestAnimationFrame(frame);
  }

  function optimistic(input, vid, qty) {
    if (!cart || !cart.items) return;
    var item = null;
    cart.items.forEach(function (i) { if (String(i.id) === String(vid)) item = i; });
    if (!item) return;
    var unit = item.final_price;                 // per-unit cents
    var oldTotal = cart.total_price, oldLine = item.final_line_price;
    var newLine = unit * qty, newTotal = oldTotal + (newLine - oldLine);
    // This theme re-renders/resets the line price node every few frames, so we
    // (a) target the ACTUALLY-PAINTED node (Range-measured) and (b) keep writing
    // our value each frame during + after the tween (holdLine) to out-race the
    // reset, until the section swap shows the final value.
    var lineNode = findLinePriceNode(input.closest('li, tr'));
    var fromLine = lineNode ? parseCents(lineNode.nodeValue) : null;
    if (fromLine === null) fromLine = oldLine;
    item.quantity = qty; item.final_line_price = newLine; cart.total_price = newTotal;
    animateChange(oldTotal, newTotal, lineNode, fromLine, newLine);
  }

  // The dispatch countdown is driven by the theme's per-second timer bound to the
  // ORIGINAL element; any section re-render swaps in a fresh, timer-less clone, so
  // it goes blank. We keep the ORIGINAL element (with its live timer) and re-insert
  // it over the clone. Deterministic (grab the ticking element BEFORE nuking HTML).
  var liveCountdown = null;
  function grabLive() {
    var c = document.querySelector('.form-cart .countdown-container');
    if (c && /\d{1,2}:\d{2}:\d{2}/.test(c.textContent || '')) liveCountdown = c;
  }
  function restoreCountdown() {
    if (!liveCountdown) { grabLive(); return; }
    var cur = document.querySelector('.form-cart .countdown-container');
    if (!cur || cur === liveCountdown) return;                       // ours already in place
    if (cur.parentNode) cur.parentNode.replaceChild(liveCountdown, cur);  // clone replaced ours -> put live one back
  }

  function swapSection(html) {
    if (!sectionEl) return false;
    grabLive();                                    // capture the ticking element before we blow away the HTML
    var keep = liveCountdown;
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var fresh = tmp.querySelector('#' + sectionEl.id) || tmp.firstElementChild;
    if (!fresh) return false;
    sectionEl.innerHTML = fresh.innerHTML;
    if (keep) {
      var clone = sectionEl.querySelector('.countdown-container');
      if (clone && clone.parentNode) clone.parentNode.replaceChild(keep, clone);
    }
    return true;
  }

  function serverUpdate(vid, qty) {
    findSection();
    var body = { id: vid, quantity: qty };
    if (sectionId) { body.sections = sectionId; body.sections_url = location.pathname; }
    fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return r.json(); })
      .then(function (c) {
        cart = c;
        var swapped = (sectionId && c.sections && c.sections[sectionId]) ? swapSection(c.sections[sectionId]) : false;
        if (!swapped) setTotals(c.total_price);      // at least keep the total correct
        var cc = document.querySelector('[data-cart-count], .cart-count, #cart-count');
        if (cc) cc.textContent = c.item_count;
        lastServerTotal = c.total_price;
        try { sessionStorage.setItem('cgCartLastTotal', String(c.total_price / 100)); } catch (e) {}
      })
      .catch(function () { location.reload(); });     // never leave the cart desynced
  }

  window.__cgFastCart = function (input, qty) {
    var vid = variantOf(input);
    if (!vid) { input.dispatchEvent(new Event('change', { bubbles: true })); return; } // safe fallback
    optimistic(input, vid, qty);
    clearTimeout(timers[vid]);
    timers[vid] = setTimeout(function () { serverUpdate(vid, qty); }, 300);
  };

  if (window.__cgCore) window.__cgCore.register(restoreCountdown);  // also fires on the theme's own re-renders (delete)
  grabLive();                                                      // cache the live one now
})();
